import {
  useQuery,
  useMutation,
  QueryClient,
  QueryCache,
  MutationCache,
  useQueryClient,
  Query,
  Mutation,
  UseQueryOptions,
  useQueries,
} from '@tanstack/react-query-v4';
import { useMemo, useRef } from 'react';
import moment from 'moment';
import {
  CommunicationService,
  IGetByReviewLevel,
  PipelineNodeFilters,
  CustomTaskFilters,
} from './communicationService';
import {
  AuditStatus,
  AuditType,
  AuditOutcomeEnum,
} from '@scale/shared/trustAndSafety/tnsAudits/types';
import { WorkerSkillResponse } from '@scale/genai-data-service-client';
import {
  CorpProjectGroupNode,
  CorpProjectGroupProjectGroupNode,
  ProjectNameProps,
} from '@scale/shared/types/corpProjectGroup';
import {
  PipelineV3HumanNodesSummaryResponse,
  PipelineV3PendingNodesResponse,
  QMShowProjectResponse,
  SpecializedReviewExampleSubtaskResponse,
  SpecializedReviewTaskerQuery,
  SpecializedReviewTaskersResponse,
  ProjectRoleAddRequest,
} from '@scale/shared/qm/qmRequestTypes';
import {
  QualitySignalVerificationByQualitySignalId,
  QualitySignalVerification,
} from 'react-scale/corp/views/CorpProjectGroupManager/types';
import {
  ConfigType,
  SaveProjectPriorityRequestBody,
  SquadsConfig,
  TaskBatchStatus,
  ThroughputType,
  BatchAssignments,
} from '@scale/shared/defaults';
import {
  TaskAttemptInfo,
  LineChartDataType,
} from 'react-scale/corp/views/qm/QMLLMWorkerPerformancePage/hooks';
import { toast } from 'react-toastify';
import {
  invalidateProjectDataProviderQuery,
  optimisticUpdateProjectDataProviderQuery,
  setProjectDataProviderQueryData,
} from 'react-scale/corp/hooks/expertData/useExpertDataProviderQuery';
import produce from 'immer';
import { IProjectJson } from '@scale/shared/types/project';
import {
  ProjectCostsResponse,
  ProjectCostsDailyResponse,
  ProjectCostsCumulativeResponse,
  ProjectCostsPerTaskResponse,
  FinanceAlertConfigResponse,
  FinanceGuardrailConfigResponse,
  FinanceGlobalConfigResponse,
  FinanceApprovalRequestResponse,
  FinanceIdNameMapResponse,
  IFinanceApprovalRequest,
  FinanceAlertConfig,
  FinanceGuardrailConfig,
  SaveFinanceApprovalRequestInput,
  PodLeadUser,
  PausedHistoryResponse,
  ProjectCostPerAttemptLevelResponse,
  AdminFinanceDefaultsResponse,
  BulkUploadGuardrailRequest,
} from '@scale/shared/types/finance';
import { IConfigMetadata } from '@scale/shared/config/IConfigMetadata';
import { IConfigMeta } from 'corp/components/JsonSchemaConfigs/configTransforms';
import {
  DeliveryBuilderConfigCreate,
  IChatTaskFeatureConfig,
  DeliveryBuilderConfigDTO,
} from '@scale/shared/types/config';
import { ConfigAttributes } from '@scale/shared/types/configAttribute';
import {
  CheatingFlaggedTasksResponse,
  GenAIProjectSetupRequest,
  GenAIProjectUpdateRequest,
  LinterRatesStatsResponse,
  MultiTurnInteractionConfiguration,
  L8EscalatedTaskRate,
  WorkersDeactivatedForCheatingResponse,
  TaskersWithStrikeTagsResponse,
  TasksTouchedByConfirmedCheatersResponse,
} from '@scale/shared/types/chat';
import { BatchResults } from '@scale/shared/types/geographic';
import { ProjectWithStatus } from '@scale/shared/types/api/corp-api/find';
import { FilterObject } from 'react-scale/dashboard.scale.com/components/SelfServe/ModelEval/ModelEvalRevamp/utils/types';
import { ProjectGroupDiff, UpdateProjectGroupRequest } from '@scale/shared/dailyPlan/types/api';
import { parseWorkspaceContainers } from 'react-scale/corp/lib/api/taskTemplate';
import { ProvisionWorkspaceRequest } from '@scale/shared/sphereEngine/types';
import { Role } from '@scale/shared/corp/projectRoles';
import {
  CreateOrUpdateCourseV2Request,
  PostCourseV2VersionRevertParams,
  CourseSectionV2,
} from '@scale/shared/types/params/courseV2';
import { SandboxUsersCreateConfig } from 'react-scale/corp/views/CorpProjectGroupManager/views/ProjectSetup/ProjectSetupStore';
import { getLogger } from '@scale/frontend-lib/datadog';
import { ReportIssueReqBody, ReportIssueResBody } from '@scale/shared/reportIssue/api';
import { DevErrorStatus } from '@scale/shared/devErrorsTesting';
import { errorToast } from './Toast/errorToast';
import {
  IAuditErrorCategory,
  IAuditErrorCategoryInput,
} from '@scale/shared/types/auditErrorCategories';
import uuid from 'uuid';
import { IQueryCorpJobs } from '@scale/shared/types/corp';
import _, { noop } from 'lodash';
import {
  IProjectBillingSetup,
  IBillingApprovalRequest,
  BillingApprovalRequestResponse,
} from '@scale/shared/types/projectParameterGroup';
import {
  IQualityValidationJson,
  IQualityValidationComponentJson,
  CampaignName,
  AccessPermission,
  TaskType,
  ReviewLevel,
  PauseAction,
} from '@scale/shared-types';
import { ModelRegistryEndpoint } from '@scale/shared/types/modelRegistry/modelRegistryEndpoint';
import {
  DeliveryJSONSchema,
  DeliveryJSONSchemaResponse,
} from '@scale/shared/types/schemaRegistry/deliveryJSONSchema';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { IUseRedashQueryProps } from '@scale/shared/types/redash';
import { fetchFraudProfile } from 'react-scale/corp/views/fraud/helpers';
import { CorpCommand } from '@scale/shared/corp/helpers';
import {
  IFetchSkillEntriesParams,
  IFetchWorkerSkillTaskDataParams,
  CorpProfileProps,
} from '@scale/shared/types/user';
import {
  CreateQuestionBankRequest,
  CreateQuestionRequest,
  UpdateQuestionRequest,
  QuestionBankDTO,
  QuestionBankProjectInfo,
  QuestionDTO,
  UpdateQuestionBankRequest,
} from '@scale/shared/questionBank';
import { CreateCompletionRequest, Message } from '@scale/shared/types/chat-completion';
import { useConfigAttributes } from 'react-scale/corp/components/JsonSchemaConfigs/useConfigAttributes';
import { useUser } from 'react-scale/components/ui/UserContext';
import { QuickGrabTasksResponse } from '@scale/shared/quickGrab/types';
import { configJsonSchema } from '@scale/shared/config/ConfigSchema';
import { CreateTagParams } from '@scale/shared/types/tag';
import { FreeTextGradingType, IGraderConfig } from '@scale/shared/types/params/freeTextGrading';
import {
  ProjectPriorityLayer,
  DirectAssignContributorsRequestDTO,
  AssignOrMigrateAndAssignRequestDTO,
  AssignOrMigrateAndAssignResponseDTO,
} from '@scale/shared/projectPriorities';
import {
  ReRankProjectRequestDTO,
  BulkUpdateProjectPrioritiesRequestDTO,
  CreateOrUpdateProjectPriorityRequestDTO,
  UserEligibilityDTO,
} from '@scale/shared/outlier/dto';
import {
  ArchieQCRequirementSpecDocInput,
  ArchieAssistantActions,
  ClarificationQA,
  ArchieQCDisagreementData,
  CustomData,
  ArchieQCEnrichedEvalOutput,
} from '@scale/shared/archieQC/types';
import {
  CreateProjectSquadParams,
  UpdateProjectSquadParams,
  CreateProjectSquadResult,
  UpdateProjectSquadResult,
  ArchiveProjectSquadResult,
  SquadCSVUploadDataRow,
  SquadCSVUpdateDataRow,
  ValidateBulkCreateSquadInputResult,
  ValidateBulkUpdateSquadInputResult,
  ParsedSquadCSVUploadDataRow,
  ParsedSquadCSVUpdateDataRow,
  BulkCreateSquadsResult,
  BulkUpdateSquadsResult,
  AssignSquadsToProjectResult,
  RerunQueueAndGetHealthRequest,
  UserLevelHealthStatus,
} from '@scale/shared/types/expertManagement';
import {
  LintRule,
  LinterCostsRequest,
  LinterCostsResponse,
  LinterPerformance,
  ScriptLinterCondition,
  AttemptId,
} from '@scale/shared/types/linter';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { InsightReportDTO } from '@scale/customer-app-shared';
import Bluebird from 'bluebird';
import { TnSRequirementSpecDocInput } from '@scale/shared/trustAndSafety/taskCheatingSpec/types';
import { FilterGroup } from '@scale/compass-shared/filter';
import { HTTPError } from '@scale/compass-ui/api-fetch';
import EnrichedError from 'react-scale/lib/enrichedError';
import {
  CopilotCheck,
  ExpectedCopilotResult,
  CopilotStatsTimeFilter,
  CopilotLastKRunsFilter,
  CopilotMetricsMode,
  CopilotResult,
  ArchieCopilotCacheMode,
} from '@scale/shared/types/quality';
import { OverallProjectLLMCostData } from '@scale/shared/quality/llmCosts/types';

function isLocalEnvironment() {
  return typeof window !== 'undefined' && window.location.hostname === 'localhost';
}

const logger = getLogger();

/**
 * This function is used to handle errors thrown from queries. It does the following:
 * - Logs the error with a unique logId.
 * - Displays an error toast with with a user-facing message and debug info.
 *
 * @param error
 * @param query The query that threw the error. Assumes this function is only used for queries.
 */
function handleGlobalQueryError(error: any, query?: Query) {
  let userMessage =
    'An error occurred. If the problem persists, use go/routing to find the on-call team and post a message in #genai-eng-help.';
  let serverMessage = error.response?.message;
  let clientMessage = error.toString();

  if (error instanceof HTTPError) {
    userMessage = error.userErrorMessage || userMessage;
    serverMessage = error.message || serverMessage;
    clientMessage = '';
  } else if (error instanceof EnrichedError) {
    userMessage = error.userMessage || userMessage;
    serverMessage = error.serverMessage || serverMessage;
    clientMessage = error.clientMessage || clientMessage;
  }

  const queryKey = query?.queryHash;

  const logId = uuid.v4();
  logger.error('GenAI Ops Hub query error', {
    logId,
    statusCode: error.status ?? 'Unknown status code.',
    userMessage,
    serverMessage,
    clientMessage,
    error,
    queryKey,
  });

  // Show this errors only in local environment
  if (isLocalEnvironment()) {
    errorToast(
      logId,
      'query',
      userMessage,
      serverMessage,
      clientMessage,
      error.status,
      error.url,
      queryKey,
    );
  }
}

/**
 * This function is used to handle errors thrown from mutations. It does the following:
 * - Logs the error with a unique logId.
 * - Displays an error toast with with a user-facing message and debug info.
 *
 * @param error Should be EnrichedError thrown from communicationService. (If not, this function is backwards compatible to handle errors that are not enriched.)
 * @param variables Arguments used in the mutation.
 * @param context Context of the mutation, which is returned in the optional onMutate function. [See here](https://tanstack.com/query/v4/docs/framework/react/guides/mutations) for more info on setting context.
 */
function handleGlobalMutationError(
  error: any,
  variables: any,
  context: any,
  mutation: Mutation<unknown, unknown, unknown>,
) {
  let userMessage =
    'An error occurred. If the problem persists, use go/routing to find the on-call team and post a message in #genai-eng-help.';
  let serverMessage = error.response?.message;
  let clientMessage = error.toString();

  if (error instanceof HTTPError) {
    userMessage = error.userErrorMessage ?? userMessage;
    serverMessage = error.message;
    clientMessage = '';
  } else if (error instanceof EnrichedError) {
    userMessage = error.userMessage || userMessage;
    serverMessage = error.serverMessage || serverMessage;
    clientMessage = error.clientMessage || clientMessage;
  }

  const logId = uuid.v4();
  if (!mutation.meta?.ignoreGlobalErrorLogging) {
    logger.error('GenAI Ops Hub mutation error', {
      logId,
      statusCode: error.status ?? 'Unknown status code.',
      userMessage,
      serverMessage,
      clientMessage,
      error,
      variables,
      context,
    });
  }
  if (!mutation.meta?.overrideGlobalErrorToast) {
    errorToast(
      logId,
      'mutation',
      userMessage,
      serverMessage,
      clientMessage,
      error.status,
      error.url,
      variables,
      context,
    );
  }
}

const queryCache = new QueryCache({
  onError: handleGlobalQueryError,
});

const mutationCache = new MutationCache({
  onError: handleGlobalMutationError,
});

export const queryClient = isLocalEnvironment()
  ? new QueryClient({
      queryCache,
      mutationCache,
      defaultOptions: {
        queries: {
          retry: 1,
          refetchOnWindowFocus: false,
        },
      },
    })
  : new QueryClient({
      queryCache,
      mutationCache,
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    });

// How to create a new query key https://tkdodo.eu/blog/effective-react-query-keys#effective-react-query-keys
export const queryFactory = {
  projectGroup: {
    // maybe change to project, validate this when the model is finished
    // Generic keys are used to invalidate many queries at once
    all: ['projectGroup'] as const,
    lists: () => [...queryFactory.projectGroup.all, 'list'] as const,
    nodes: () => [...queryFactory.projectGroup.all, 'node'] as const,
    nodeSummaries: () => [...queryFactory.projectGroup.all, 'node-summary'] as const,
    pendingNodeSummaries: () => [...queryFactory.projectGroup.all, 'pending-node-summary'] as const,
    benchmarksStatistics: () => [...queryFactory.projectGroup.all, 'benchmark-statistics'] as const,
    sxsQualitySignals: () => [...queryFactory.projectGroup.all, 'sxs-quality-signals'] as const,
    slaGroupDataKey: () => [...queryFactory.projectConfigs.all, 'slaGroupData'] as const,
    projectGroupPrioritiesKey: () =>
      [...queryFactory.projectGroup.all, 'projectGroupPriorities'] as const,
    attemptsByReviewLevel: () =>
      [...queryFactory.projectGroup.all, 'attempt-by-review-level'] as const,
    // Specific query keys and query functions are used to fetch data or invalidate specific queries
    list: () => ({
      queryKey: [...queryFactory.projectGroup.lists()] as const,
      queryFn: () => CommunicationService.findProjectGroups(),
    }),
    node: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projectGroup.nodes(), projectId] as const,
      queryFn: () => CommunicationService.findProjectGroupNode(projectId),
      enabled: !!projectId,
    }),
    nodeSummary: ({ projectId, filters }: { projectId: string; filters: PipelineNodeFilters }) => ({
      queryKey: [
        ...queryFactory.projectGroup.nodeSummaries(),
        projectId,
        filters.batchId,
        filters.specializations,
        filters.workerSkillSpecializations,
        filters.creationDateRange,
        filters.updatedDateRange,
        filters.workStarted,
      ] as const,
      queryFn: () => CommunicationService.fetchNodeSummary(projectId, filters),
    }),
    pendingNodeSummary: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projectGroup.pendingNodeSummaries(), projectId] as const,
      queryFn: () => CommunicationService.fetchPendingNodeSummary(projectId),
    }),
    benchmarkStatistics: ({
      projectId,
      startDate,
      endDate,
      workerSpecialization,
    }: {
      projectId: string;
      startDate?: Date;
      endDate?: Date;
      workerSpecialization?: string;
    }) => ({
      queryKey: [
        ...queryFactory.projectGroup.benchmarksStatistics(),
        projectId,
        startDate,
        endDate,
        workerSpecialization,
      ] as const,
      queryFn: () =>
        CommunicationService.fetchBenchmarkStatistics(
          projectId,
          startDate,
          endDate,
          workerSpecialization,
        ),
    }),
    sxsQualitySignal: ({
      projectId,
      startDate,
      endDate,
    }: {
      projectId: string;
      startDate: Date;
      endDate: Date;
    }) => ({
      queryKey: [
        ...queryFactory.projectGroup.sxsQualitySignals(),
        projectId,
        startDate,
        endDate,
      ] as const,
      queryFn: () =>
        CommunicationService.getProjectSxsQualitySignals({ projectId, startDate, endDate }),
    }),
    attemptByReviewLevel: ({ projectId, dateRange, filters }: IGetByReviewLevel) => ({
      queryKey: [
        ...queryFactory.projectGroup.attemptsByReviewLevel(),
        projectId,
        dateRange,
        filters,
      ] as const,
      queryFn: () =>
        CommunicationService.getAttemptsByReviewLevel({ projectId, dateRange, filters }),
    }),
    attemptsByReviewLevelByDate: ({ projectId, dateRange, filters }: IGetByReviewLevel) => ({
      queryKey: [
        ...queryFactory.projectGroup.attemptsByReviewLevel(),
        projectId,
        dateRange.toString(),
        filters,
        'by-date',
      ] as const,
      queryFn: () =>
        CommunicationService.getAttemptsByReviewLevelByDate({ projectId, dateRange, filters }),
    }),
    batches: ({
      projectId,
      search,
      status,
    }: {
      projectId: string;
      search?: any;
      status?: TaskBatchStatus;
    }) => ({
      queryKey: [...queryFactory.projectGroup.all, projectId, 'batches', search, status] as const,
      queryFn: () => CommunicationService.getBatches(projectId, search, status),
    }),
    projectGroupPriorities: ({ page, pageSize }: { page: number; pageSize: number }) => ({
      queryKey: [...queryFactory.projectGroup.projectGroupPrioritiesKey(), page, pageSize] as const,
      queryFn: () => CommunicationService.fetchProjectGroupPriorities(page, pageSize),
    }),
  },
  projectConfigs: {
    all: ['projectConfigs'] as const,
    tags: () => [...queryFactory.projectConfigs.all, 'tags'] as const,
    tagsNames: () => [...queryFactory.projectConfigs.all, 'tagsNames'] as const,
    tagsNamesOrIds: () => [...queryFactory.projectConfigs.all, 'tagsNamesOrIds'] as const,
    find: ({
      projectIds,
      configsToFetch,
    }: {
      projectIds: string[];
      configsToFetch?: string[];
    }) => ({
      queryKey: [...queryFactory.projectConfigs.all, projectIds, configsToFetch] as const,
      queryFn: () => CommunicationService.findProjectConfigs(projectIds, configsToFetch),
      enabled: !!projectIds?.length,
    }),
    metadata: () => ({
      queryKey: [...queryFactory.projectConfigs.all, 'metadata'] as const,
      queryFn: () => CommunicationService.getConfigMetadata(),
    }),
    data: ({
      configMeta,
      configAttributes,
      configsPaths,
    }: {
      configMeta: IConfigMeta;
      configAttributes: ConfigAttributes;
      configsPaths?: string;
    }) => ({
      queryKey: [
        ...queryFactory.projectConfigs.all,
        'data',
        configMeta,
        configAttributes,
        configsPaths,
      ] as const,
      queryFn: () => CommunicationService.getConfigData(configMeta, configAttributes, configsPaths),
    }),
    tagsById: (tagsIds: string[]) => ({
      queryFn: async () => await CommunicationService.getTagsById(tagsIds),
      queryKey: [...queryFactory.projectConfigs.tags(), tagsIds] as const,
    }),
    tagsByNameOrId: (search: string) => ({
      queryFn: async () => {
        if (/[0-9a-hA-H]{24}/.test(search)) {
          return await CommunicationService.getTagsById([search]);
        } else {
          return await CommunicationService.getTagsByName(search);
        }
      },
      enabled: !!search,
      queryKey: [...queryFactory.projectConfigs.tagsNamesOrIds(), search] as const,
    }),
    hubstaffProjects: () => ({
      all: ['hubstaffProjects'] as const,
      fetch: ({
        projectHubstaff,
        createdProjectId,
      }: {
        projectHubstaff: string;
        createdProjectId: string;
      }) => ({
        queryKey: [...queryFactory.projectConfigs.all, 'hubstaffProjects'] as const,
        queryFn: () =>
          CommunicationService.updateHubstaffProject(projectHubstaff, createdProjectId),
      }),
    }),
  },
  llmCosts: {
    getLLMCosts: ({ projectId }: { projectId: string }) => ({
      queryKey: ['llmCosts', projectId] as const,
      queryFn: async () => {
        const llmCosts = await apiFetch(null, `corp-api/llm-costs/${projectId}`);
        return llmCosts;
      },
    }),
  },
  devErrorsTesting: {
    all: ['devErrorsTesting'] as const,
    devError: ({ projectId, status }: { projectId: string; status: DevErrorStatus }) => ({
      queryKey: [...queryFactory.devErrorsTesting.all, projectId] as const,
      queryFn: () => CommunicationService.getDevError(projectId, status), // Modify the status to test different query errors.
    }),
  },
  attemptAudit: {
    all: ['attemptAudits'] as const,
    attemptAuditLookups: () => [...queryFactory.attemptAudit.all, 'lookup'] as const,
    attemptAuditLookup: ({ taskId }: { taskId: string }) => ({
      queryKey: [...queryFactory.attemptAudit.attemptAuditLookups(), taskId] as const,
      queryFn: () => CommunicationService.attemptAuditLookup(taskId),
    }),
  },
  tasks: {
    all: ['tasks'],
    translateTasks: () => [...queryFactory.tasks.all, 'translateTasks'] as const,
    translateTask: ({ task }: { task: any }) => ({
      queryKey: [...queryFactory.tasks.translateTasks(), task?._id] as const,
      queryFn: () => CommunicationService.translateTask(task),
      enabled: false, // We just want the query and saved it in the cache, we don't want to translate every task all the time
    }),
    getTasksV2FlattenedResponse: ({
      taskIds,
      deliveryBuilderConfigId,
    }: {
      taskIds: string[];
      deliveryBuilderConfigId: string;
    }) => ({
      queryKey: [
        ...queryFactory.tasks.all,
        'getTasksV2FlattenedResponse',
        taskIds,
        deliveryBuilderConfigId,
      ] as const,
      queryFn: () =>
        CommunicationService.getApiV2FlattenedTasks({ taskIds, deliveryBuilderConfigId }),
      enabled: !!taskIds?.length && !!deliveryBuilderConfigId,
    }),
  },
  subtasks: {
    all: ['substasks'] as const,
    unclaimedSubstasksResponses: () =>
      [...queryFactory.projectGroup.all, 'unclaimed-responses'] as const,
    unclaimedSubstasksResponse: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.subtasks.unclaimedSubstasksResponses(), projectId] as const,
      queryFn: () => CommunicationService.findUnclaimedSubstasksResponse(projectId),
    }),
  },
  taskAttempts: {
    all: ['taskAttempts'] as const,
    forTaskLookup: () => [...queryFactory.taskAttempts.all, 'forTaskLookup'] as const,
    getForTaskLookup: (taskId: string) => ({
      queryKey: [...queryFactory.taskAttempts.forTaskLookup(), taskId] as const,
      queryFn: () => CommunicationService.getTaskAttemptForTaskLookup(taskId),
      enabled: !!taskId,
    }),
  },
  projects: {
    all: ['projects'] as const,
    details: () => [...queryFactory.projects.all, 'details'] as const,
    taskers: () => [...queryFactory.projects.all, 'taskers'] as const,
    isActive: () => [...queryFactory.projects.all, 'isActive'] as const,
    linkedProjects: (projectIds: string[]) => ({
      queryKey: [...queryFactory.projects.all, 'linkedProjects', projectIds] as const,
      queryFn: () => CommunicationService.getLinkedProjectOptionsFromIds(projectIds),
    }),
    projectRoles: () => [...queryFactory.projects.all, 'projectRoles'] as const,
    exampleSubtasks: () => [...queryFactory.projects.all, 'subtasks'] as const,
    qmProjects: () => [...queryFactory.projects.all, 'qmProjects'] as const,
    projectCosts: () => [...queryFactory.projects.all, 'projectCosts'] as const,
    lintersRatesStatsKey: () => [...queryFactory.projects.all, 'lintersRatesStats'] as const,
    l8EscalatedTasksRateKey: () => [...queryFactory.projects.all, 'l8ScalatedTasksRate'] as const,
    cheatingFlaggedTasksKey: () => [...queryFactory.projects.all, 'cheatingFlaggedTasks'] as const,
    searches: () => [...queryFactory.projects.all, 'search'] as const,
    taskersWithStrikeTagsKey: () =>
      [...queryFactory.projects.all, 'taskersWithStrikeTags'] as const,
    projectFinanceGuardrails: () =>
      [...queryFactory.projects.all, 'projectFinanceGuardrails'] as const,
    projectFinanceAlerts: () => [...queryFactory.projects.all, 'projectFinanceAlerts'] as const,
    slaGroupKey: () => [...queryFactory.projects.all, 'slaGroup'] as const,
    workerTeamsKey: () => [...queryFactory.projects.all, 'workerTeams'] as const,
    workerTeamsDataByProjectIdKey: () =>
      [...queryFactory.projects.all, 'workerTeamsDataByProjectId'] as const,
    assignmentsDataByProjectIdKey: () =>
      [...queryFactory.projects.all, 'assignmentsDataByProjectId'] as const,
    chatTaskFeatureConfigKey: () =>
      [...queryFactory.projects.all, 'chatTaskFeatureConfig'] as const,
    deliveryBuilderConfigKey: () =>
      [...queryFactory.projects.all, 'deliveryBuilderConfig'] as const,
    projectsNames: () => [...queryFactory.projects.all, 'projectsNames'] as const,
    slaGroups: () => [...queryFactory.projects.all, 'slaGroups'] as const,
    projectPipelineV3Config: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, 'projectPipelineV3Config'] as const,
      queryFn: () => CommunicationService.fetchProjectPipelineV3Config({ projectId }),
    }),
    projectConfigs: ({
      projectId,
      configAttributes,
    }: {
      projectId: string;
      configAttributes: Record<string, string>;
    }) => ({
      queryKey: [...queryFactory.projects.all, 'projectConfigs', projectId] as const,
      queryFn: () => CommunicationService.fetchProjectConfigs({ projectId, configAttributes }),
    }),
    projectPriorities: (
      workerSkills?: string[],
      projectGroups?: string[],
      layer?: ProjectPriorityLayer,
      alpha?: string,
      statuses?: string[],
      searchTerm?: string,
      page?: number,
      pageSize?: number,
      matchAnyOption?: boolean,
      enabled?: boolean,
    ) => ({
      queryKey: [
        ...queryFactory.projects.all,
        'projectPriorities',
        workerSkills?.toString(),
        projectGroups?.toString(),
        layer,
        alpha,
        statuses?.toString(),
        searchTerm,
        page,
        pageSize,
        matchAnyOption,
      ] as const,
      queryFn: () =>
        CommunicationService.fetchProjectPriorities(
          workerSkills,
          projectGroups,
          layer,
          alpha,
          statuses,
          searchTerm,
          page,
          pageSize,
          matchAnyOption,
        ),
      enabled: enabled ?? true,
    }),
    projectPriority: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, 'projectPriority', projectId] as const,
      queryFn: () => CommunicationService.fetchProjectPriority({ projectId }),
    }),
    allProjectPrioritiesForProject: ({ projectId }: { projectId: string }) => ({
      queryKey: [
        ...queryFactory.projects.all,
        'allProjectPrioritiesForProject',
        projectId,
      ] as const,
      queryFn: () => CommunicationService.fetchAllProjectPrioritiesForProject({ projectId }),
    }),
    projectThroughput: ({
      projectId,
      throughputType,
      scrollStartDateTime,
      scrollEndDateTime,
    }: {
      projectId: string;
      throughputType: ThroughputType;
      scrollStartDateTime?: Date;
      scrollEndDateTime?: Date;
    }) => ({
      queryKey: [
        ...queryFactory.projects.all,
        'projectThroughput',
        projectId,
        throughputType,
        scrollStartDateTime,
        scrollEndDateTime,
      ] as const,
      queryFn: () =>
        CommunicationService.fetchProjectThroughput({
          projectId,
          throughputType,
          scrollStartDateTime,
          scrollEndDateTime,
        }),
    }),
    projectHistoricalRampPlanData: ({
      projectId,
      startDate,
      endDate,
    }: {
      projectId: string;
      startDate: Date;
      endDate: Date;
    }) => ({
      queryKey: [
        ...queryFactory.projects.all,
        'historicalRampPlan',
        projectId,
        startDate,
        endDate,
      ] as const,
      queryFn: () =>
        CommunicationService.fetchHistoricalRampPlanData({ projectId, startDate, endDate }),
    }),
    tasker: ({ projectId, query }: { projectId: string; query: SpecializedReviewTaskerQuery }) => ({
      queryKey: [
        ...queryFactory.projects.taskers(),
        projectId,
        query.offset,
        query.limit,
        query.emailOrId,
      ] as const,
      queryFn: () => CommunicationService.findProjectTaskers(projectId, query),
      keepPreviousData: true,
    }),
    exampleSubtask: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.exampleSubtasks(), projectId] as const,
      queryFn: () => CommunicationService.findProjectExampleSubtasks(projectId),
    }),
    detail: ({ projectId }: { projectId?: string }) => ({
      queryKey: [...queryFactory.projects.details(), projectId] as const,
      queryFn: () => CommunicationService.getProjectById(projectId!),
      enabled: !!projectId,
    }),
    projectName: (projectIds: string[]) => ({
      queryKey: [...queryFactory.projects.projectsNames(), projectIds] as const,
      queryFn: () => CommunicationService.getProjectsByIds(projectIds),
      enabled: !!projectIds?.length,
    }),
    qmProject: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.qmProjects(), projectId] as const,
      queryFn: () => CommunicationService.getQMProject(projectId),
      enabled: !!projectId,
    }),
    projectDefaultErrorCategories: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, projectId, 'projectDefaultErrorCategories'] as const,
      queryFn: () => CommunicationService.getProjectDefaultErrorCategories(projectId),
    }),
    projectSpecificErrorCategories: ({ projectId }: { projectId: string }) => ({
      queryKey: [
        ...queryFactory.projects.all,
        projectId,
        'projectSpecificErrorCategories',
      ] as const,
      queryFn: () => CommunicationService.getProjectSpecificErrorCategories(projectId),
    }),
    defaultErrorCategoriesEnabled: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, projectId, 'defaultErrorCategoriesEnabled'] as const,
      queryFn: () => CommunicationService.getUseDefaultErrorCategories(projectId),
    }),
    projectFieldIds: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, projectId, 'projectFieldIds'] as const,
      queryFn: () => CommunicationService.getProjectFieldIds(projectId),
    }),
    projectIsEligibleForApiV2: ({
      projectId,
      taskIds,
      templateVersions,
    }: {
      projectId: string;
      taskIds?: string[];
      templateVersions?: number[];
    }) => ({
      queryKey: [
        ...queryFactory.projects.all,
        'projectIsEligibleForApiV2',
        projectId,
        ...(templateVersions ?? []),
        ...(taskIds ?? []),
      ] as const,
      queryFn: () =>
        CommunicationService.getProjectIsEligibleForApiV2({
          projectId,
          taskIds,
          templateVersions,
        }),
    }),
    projectCostsByDate: ({
      fromDate,
      toDate,
      projectId,
    }: {
      fromDate: Date;
      toDate: Date;
      projectId?: string;
    }) => ({
      queryKey: [...queryFactory.projects.projectCosts(), projectId, fromDate, toDate] as const,
      queryFn: () => CommunicationService.getProjectCostsByDate(fromDate, toDate, projectId),
    }),
    projectCostsDaily: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.projectCosts(), projectId, 'daily'] as const,
      queryFn: () => CommunicationService.getProjectCostsYesterdayAndToday(projectId),
    }),
    projectCostsCumulative: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.projectCosts(), projectId, 'cumulative'] as const,
      queryFn: () => CommunicationService.getProjectCostsCumulative(projectId),
    }),
    projectCostsPerTask: ({
      projectId,
      forceUpdate,
    }: {
      projectId: string;
      forceUpdate: boolean;
    }) => ({
      queryKey: [...queryFactory.projects.projectCosts(), projectId, 'perTask'] as const,
      queryFn: () => CommunicationService.getProjectCostsPerTask(projectId, forceUpdate),
    }),
    projectCostsPerAttemptByReviewLevel: ({ projectId }: { projectId: string }) => ({
      queryKey: [
        ...queryFactory.projects.projectCosts(),
        projectId,
        'perAttemptByReviewLevel',
      ] as const,
      queryFn: () => CommunicationService.getProjectCostPerAttemptByReviewLevel(projectId),
    }),
    projectFinanceAlertConfigs: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.projectFinanceAlerts(), projectId] as const,
      queryFn: () => CommunicationService.getProjectFinanceAlertConfigs(projectId),
    }),
    projectPausedHistory: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, projectId, 'projectPausedHistory'] as const,
      queryFn: () => CommunicationService.getProjectPausedHistory(projectId),
    }),
    projectNotificationsSlackChannel: ({ projectId }: { projectId: string }) => ({
      queryKey: [
        ...queryFactory.projects.all,
        projectId,
        'projectNotificationsSlackChannel',
      ] as const,
      queryFn: () => CommunicationService.getProjectNotificationsSlackChannel(projectId),
    }),
    lintersRatesStats: ({ projectId, dateRange }: { projectId: string; dateRange: Date[] }) => ({
      queryKey: [...queryFactory.projects.lintersRatesStatsKey(), projectId, dateRange],
      queryFn: () =>
        CommunicationService.getLinterRatesStats({
          projectId,
          from: dateRange[0],
          to: dateRange[1],
        }),
    }),
    l8EscalatedTasksRate: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.l8EscalatedTasksRateKey(), projectId],
      queryFn: () =>
        CommunicationService.getL8EscalatedTasksRate({
          projectId,
        }),
    }),
    taskersWithStrikeTags: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.taskersWithStrikeTagsKey(), projectId],
      queryFn: () =>
        CommunicationService.getTaskersWithStrikeTags({
          projectId,
        }),
    }),
    cheatingFlaggedTasks: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.cheatingFlaggedTasksKey(), projectId] as const,
      queryFn: () =>
        CommunicationService.getCheatingFlaggedTasks({
          projectId,
        }),
    }),
    tasksTouchedByConfirmedCheaters: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, projectId, 'useTasksTouchedByConfirmedCheaters'],
      queryFn: () =>
        CommunicationService.getTasksTouchedByConfirmedCheaters({
          projectId,
        }),
    }),
    workersDeactivatedForCheating: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, projectId, 'workersDeactivatedForCheating'],
      queryFn: () =>
        CommunicationService.getWorkersDeactivatedForCheating({
          projectId,
        }),
    }),
    search: ({
      searchText,
      includeNonGenAIProjects,
    }: {
      searchText: string;
      includeNonGenAIProjects?: boolean;
    }) => ({
      queryKey: [...queryFactory.projects.all, 'search', searchText] as const,
      queryFn: () => CommunicationService.findGenAIProjects(searchText, includeNonGenAIProjects),
      enabled: !!searchText,
    }),
    projectLibraryEntry: ({ filters }: { filters: FilterObject | undefined }) => ({
      queryKey: ['getProjectLibraryEntries', filters] as const,
      queryFn: () => CommunicationService.fetchProjectLibraryEntries(filters),
    }),
    status: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.isActive(), projectId] as const,
      queryFn: () => CommunicationService.getIsProjectActive(projectId),
    }),
    prebuildStatus: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.all, 'prebuildStatus', projectId] as const,
      queryFn: () => CommunicationService.getProjectPrebuildStatus(projectId),
    }),
    marketplaceStatus: ({
      projectId,
      query,
    }: {
      projectId: string;
      query?: Record<string, any>;
    }) => ({
      queryKey: [...queryFactory.projects.all, 'marketplaceStatus', projectId, query] as const,
      queryFn: () =>
        CommunicationService.getProjectMarketplaceStatus({
          projectId,
          query,
        }),
    }),
    marketplaceStatusByWorkerSkill: ({ projectId }: { projectId: string }) => ({
      queryKey: [
        ...queryFactory.projects.all,
        'marketplaceStatusByWorkerSkill',
        projectId,
      ] as const,
      queryFn: () => CommunicationService.getProjectMarketplaceStatusByWorkerSkill({ projectId }),
    }),
    roles: ({ projectId, filters }: { projectId: string; filters?: Role[] }) => ({
      queryKey: [...queryFactory.projects.projectRoles(), projectId, filters] as const,
      queryFn: () => CommunicationService.getProjectRoles(projectId, filters),
    }),
    slaGroup: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.slaGroupKey(), projectId] as const,
      queryFn: () => CommunicationService.getSLAGroupByProjectId(projectId),
    }),
    slaGroupParentData: ({ projectGroupId }: { projectGroupId: string }) => ({
      queryKey: [...queryFactory.projectGroup.slaGroupDataKey(), projectGroupId] as const,
      queryFn: () => CommunicationService.getSLAGroupByProjectGroupId(projectGroupId),
    }),
    workerTeams: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.workerTeamsKey(), projectId] as const,
      queryFn: () => CommunicationService.getWorkerTeams(projectId),
    }),
    allWorkerTeams: () => ({
      queryKey: [...queryFactory.projects.all, 'allWorkerTeams'] as const,
      queryFn: () => CommunicationService.getAllWorkerTeams(),
    }),
    projectLibraryEntryById: ({ projectIds }: { projectIds: string[] }) => ({
      queryKey: ['getProjectLibraryEntriesByIds', projectIds] as const,
      queryFn: () => CommunicationService.fetchProjectLibraryEntriesByIds(projectIds),
    }),
    chatTaskFeatureConfig: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.chatTaskFeatureConfigKey(), projectId] as const,
      queryFn: () => CommunicationService.fetchChatTaskFeatureConfig(projectId),
    }),
    deliveryBuilderConfigs: (projectId: string) => ({
      queryKey: [...queryFactory.projects.deliveryBuilderConfigKey(), projectId] as const,
      queryFn: () => CommunicationService.fetchDeliveryBuilderConfigs(projectId),
    }),
    deliveryBuilderConfigById: (configId: string) => ({
      queryKey: [...queryFactory.projects.deliveryBuilderConfigKey(), 'byId', configId] as const,
      queryFn: () => CommunicationService.fetchDeliveryBuilderConfigById(configId),
    }),
    taskTemplate: ({ projectId }: { projectId: string }) => ({
      queryKey: ['taskTemplate', projectId] as const,
      queryFn: () => CommunicationService.fetchTaskTemplate(projectId),
    }),
    workspaceContainerFields: ({ projectId }: { projectId: string }) => ({
      queryKey: ['taskTemplate', 'workspaceContainerFields', projectId] as const,
      queryFn: () => parseWorkspaceContainers(projectId),
    }),
    workerTeamsDataByProjectId: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.workerTeamsDataByProjectIdKey(), projectId],
      queryFn: () => CommunicationService.getWorkerTeamsDataByProjectId(projectId),
    }),
    assignmentsDataByProjectId: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projects.assignmentsDataByProjectIdKey(), projectId],
      queryFn: () => CommunicationService.assignmentsDataByProjectId(projectId),
    }),
    reviewLevels: ({ projectId }: { projectId: string }) => ({
      queryKey: ['reviewLevels', projectId] as const,
      queryFn: () => CommunicationService.getReviewLevels(projectId),
    }),
    taskModels: ({ projectId }: { projectId: string }) => ({
      queryKey: [projectId] as const,
      queryFn: () => CommunicationService.getProjectTaskModels(projectId),
    }),
    throttleStats: ({ projectId }: { projectId: string }) => ({
      queryKey: ['throttleStats', projectId] as const,
      queryFn: () => CommunicationService.getThrottleStats(projectId),
    }),
    configChangelog: ({ projectId }: { projectId: string }) => ({
      queryKey: ['configChangelog', projectId] as const,
      queryFn: () => CommunicationService.getConfigChangelog(projectId),
    }),
    projectChangelog: ({ projectId }: { projectId: string }) => ({
      queryKey: ['projectChangelog', projectId] as const,
      queryFn: () => CommunicationService.getProjectChangelog(projectId),
    }),
    validExperimentQualifications: () => ({
      queryKey: ['validExperimentQualifications'] as const,
      queryFn: () => CommunicationService.getValidExperimentQualifications(),
    }),
    validExperimentGroupNames: ({ experimentName }: { experimentName: string }) => ({
      queryKey: ['validExperimentGroupNames', experimentName] as const,
      queryFn: () => CommunicationService.getValidExperimentGroupNames(experimentName),
    }),
    branchingQualificationDetails: ({ qualificationListId }: { qualificationListId: string }) => ({
      queryKey: ['branchingQualificationDetails', qualificationListId] as const,
      queryFn: () => CommunicationService.getBranchingQualificationDetails(qualificationListId),
    }),
  },
  templates: {
    projectSetupDefaultTemplates: () => ({
      queryKey: [] as const,
      queryFn: () => CommunicationService.getProjectSetupDefaultTemplates(),
    }),
  },
  taskReplications: {
    all: ['taskReplications'] as const,
    fetch: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.taskReplications.all, projectId] as const,
      queryFn: () => CommunicationService.fetchTaskReplications(projectId),
    }),
  },
  audits: {
    all: ['audits'] as const,
    chatBulkAudits: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.audits.all, projectId] as const,
      queryFn: () => CommunicationService.getChatBulkAudits(projectId),
    }),
    chatBulkAuditsForUser: ({ projectId, userId }: { projectId: string; userId: string }) => ({
      queryKey: [...queryFactory.audits.all, projectId, userId] as const,
      queryFn: () => CommunicationService.getChatBulkAuditForUser(projectId, userId),
    }),
    getAttemptAuditScores: ({ attemptAuditId }: { attemptAuditId: string }) => ({
      queryKey: [...queryFactory.audits.all, attemptAuditId] as const,
      queryFn: () => CommunicationService.getAttemptAuditScores({ attemptAuditId }),
    }),
  },
  componentValidations: {
    all: ['componentValidations'] as const,
    getComponentValidations: ({ attemptAuditId }: { attemptAuditId: string }) => ({
      queryKey: [...queryFactory.componentValidations.all, attemptAuditId] as const,
      queryFn: () => CommunicationService.getComponentValidations({ attemptAuditId }),
    }),
  },
  projectSquads: {
    all: ['projectSquads'] as const,
    getProjectSquads: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projectSquads.all, projectId] as const,
      queryFn: () => CommunicationService.getProjectSquads(projectId),
    }),
    getProjectSquadTaskStats: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projectSquads.all, projectId, 'taskStats'] as const,
      queryFn: () => CommunicationService.getProjectSquadTaskStats(projectId),
    }),
    getSquad: ({ squadId, projectIds }: { squadId: string; projectIds: string[] }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadId] as const,
      queryFn: () => CommunicationService.getSquad(squadId, projectIds),
    }),
    getSquadTaskStats: ({ squadId, projectIds }: { squadId: string; projectIds: string[] }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadId, 'stats'] as const,
      queryFn: () => CommunicationService.getSquadTaskStats(squadId, projectIds),
    }),
    getSquadAudits: ({ squadId, projectIds }: { squadId: string; projectIds: string[] }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadId, 'audits'] as const,
      queryFn: () => CommunicationService.getSquadAudits(squadId, projectIds),
    }),
    getSquadHealth: ({ squadId, projectIds }: { squadId: string; projectIds: string[] }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadId, 'health'] as const,
      queryFn: () => CommunicationService.getSquadHealth(squadId, projectIds),
    }),
    getProjectSquadsHealth: ({
      projectId,
      squadIds,
    }: {
      projectId: string;
      squadIds: string[];
    }) => ({
      queryKey: [...queryFactory.projectSquads.all, projectId, 'health'] as const,
      queryFn: () => CommunicationService.getProjectSquadsHealth(projectId, squadIds),
    }),
    getSquadPendingTasks: ({ squadId, projectIds }: { squadId: string; projectIds: string[] }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadId, 'pendingTasks'] as const,
      queryFn: () => CommunicationService.getSquadPendingTasks(squadId, projectIds),
    }),
    checkAssignableSquads: ({
      sourceProjectId,
      destinationProjectId,
    }: {
      sourceProjectId: string;
      destinationProjectId: string;
    }) => ({
      queryKey: [...queryFactory.projectSquads.all, sourceProjectId, destinationProjectId] as const,
      queryFn: () =>
        CommunicationService.checkAssignableSquads(sourceProjectId, destinationProjectId),
    }),
    getSquadMemberAttempts: ({
      squadId,
      projectIds,
    }: {
      squadId: string;
      projectIds: string[];
    }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadId, 'memberAttempts'] as const,
      queryFn: () => CommunicationService.getSquadMemberAttempts(squadId, projectIds),
    }),
    getReviewerSquads: () => ({
      queryKey: [...queryFactory.projectSquads.all, 'userSquads'] as const,
      queryFn: () => CommunicationService.getReviewerSquads(),
    }),
    getSquadMemberMetadata: ({ squadMembers }: { squadMembers: string[] }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadMembers] as const,
      queryFn: () => CommunicationService.getSquadMemberMetadata(squadMembers),
    }),
    searchSquadUsers: ({ search }: { search: string }) => ({
      queryKey: [...queryFactory.projectSquads.all, search] as const,
      queryFn: () => CommunicationService.searchSquadUsers(search),
    }),
    lookupSquadUsers: ({
      projectId,
      idsOrEmails,
    }: {
      projectId: string;
      idsOrEmails: string[];
    }) => ({
      queryKey: [...queryFactory.projectSquads.all, idsOrEmails] as const,
      queryFn: () => CommunicationService.lookupSquadUsers(projectId, idsOrEmails),
    }),
    validateSquadName: ({ squadName }: { squadName: string }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadName] as const,
      queryFn: () => CommunicationService.validateSquadName(squadName),
    }),
    lookupSquadAttempt: ({ attemptId }: { attemptId: string }) => ({
      queryKey: [...queryFactory.projectSquads.all, attemptId] as const,
      queryFn: () => CommunicationService.lookupSquadAttempt(attemptId),
    }),
    lookupSquadResponsibleAdmins: ({ idsOrEmails }: { idsOrEmails: string[] }) => ({
      queryKey: [...queryFactory.projectSquads.all, 'responsibleAdmins', idsOrEmails] as const,
      queryFn: () => CommunicationService.lookupSquadResponsibleAdmins(idsOrEmails),
    }),
    getSquadProjects: ({ squadId }: { squadId: string }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadId, 'projects'] as const,
      queryFn: () => CommunicationService.getSquadProjects(squadId),
    }),
    getUserProjectOwnership: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projectSquads.all, projectId, 'userProjectOwnership'] as const,
      queryFn: () => CommunicationService.getUserProjectOwnership(projectId),
    }),
    getSquadInternalProjects: ({ squadId }: { squadId: string }) => ({
      queryKey: [...queryFactory.projectSquads.all, squadId, 'internalProjects'] as const,
      queryFn: () => CommunicationService.getSquadInternalProjects(squadId),
    }),
    validateUnassignSquadFromProject: ({
      projectId,
      squadIds,
    }: {
      projectId: string;
      squadIds: string[];
    }) => ({
      queryKey: [
        ...queryFactory.projectSquads.all,
        'validateUnassignSquadFromProject',
        projectId,
        squadIds,
      ] as const,
      queryFn: () => CommunicationService.validateUnassignSquadFromProject(projectId, squadIds),
    }),
  },
  projectSquadsConfig: {
    all: ['projectSquadsConfig'] as const,
    getProjectSquadsConfig: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.projectSquadsConfig.all, projectId] as const,
      queryFn: () => CommunicationService.getProjectSquadsConfig(projectId),
    }),
  },
  linter: {
    all: ['linter'] as const,
    getLinterRuns: ({ projectId, linterName }: { projectId: string; linterName: string }) => ({
      queryKey: [...queryFactory.linter.all, projectId, linterName] as const,
      queryFn: () => CommunicationService.getLinterRuns({ projectId, linterName }),
    }),
    getLinterPerformance: ({
      projectId,
      linterName,
    }: {
      projectId: string;
      linterName: string;
    }) => ({
      queryKey: [...queryFactory.linter.all, projectId, linterName, 'performance'] as const,
      queryFn: () => CommunicationService.getLinterPerformance({ projectId, linterName }),
    }),
    getLinterCosts: ({ projectId, startDate, endDate }: LinterCostsRequest) => ({
      queryKey: [...queryFactory.linter.all, projectId, startDate, endDate, 'costs'] as const,
      queryFn: () => CommunicationService.getLinterCosts({ projectId, startDate, endDate }),
    }),
  },
  qcSpecDoc: {
    archieSpecDoc: ['archieSpecDoc'] as const,
    gqaScores: ['gqaScores'] as const,
    precisionRecallResults: ['precisionRecallResults'] as const,
    calculatedGroundTruthMetrics: ['calculatedGroundTruthMetrics'] as const,
    recentProjectAttempts: ['recentProjectAttempts'] as const,
    getGQADataForAttempts: ({
      projectId,
      attemptIds,
    }: {
      projectId: string;
      attemptIds: string[];
    }) => ({
      queryKey: [...queryFactory.qcSpecDoc.gqaScores, projectId, attemptIds] as const,
      queryFn: () => CommunicationService.getGQADataForAttempts(projectId, attemptIds),
      staleTime: 1000 * 10,
    }),
    getArchieQCSpecDoc: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.qcSpecDoc.archieSpecDoc, projectId] as const,
      queryFn: () => CommunicationService.getArchieQCSpecDoc(projectId),
      staleTime: 1000 * 10,
    }),
    getLiveArchieQCSpecDoc: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.qcSpecDoc.archieSpecDoc, projectId, 'live'] as const,
      queryFn: () => CommunicationService.getLiveArchieQCSpecDoc(projectId),
      staleTime: 1000 * 10,
    }),
    getAllPublishedArchieQCSpecDocs: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.qcSpecDoc.archieSpecDoc, projectId, 'allPublished'] as const,
      queryFn: () => CommunicationService.getAllPublishedArchieQCSpecDocs(projectId),
      staleTime: 1000 * 10,
    }),
    getCalculatedGroundTruthMetrics: ({
      projectId,
      groundTruthVersion,
      specDocVersion,
    }: {
      projectId: string;
      groundTruthVersion: number;
      specDocVersion: number;
    }) => ({
      queryKey: [
        ...queryFactory.qcSpecDoc.calculatedGroundTruthMetrics,
        projectId,
        groundTruthVersion,
        specDocVersion,
      ] as const,
      queryFn: () =>
        CommunicationService.getCalculatedGroundTruthMetrics(
          projectId,
          groundTruthVersion,
          specDocVersion,
        ),
    }),
    getArchieQCTaxonomyMapping: ({
      projectId,
      attemptId,
      includeValueReference = false,
      includeDictWithValue = false,
    }: {
      projectId: string;
      attemptId: string;
      includeValueReference?: boolean;
      includeDictWithValue?: boolean;
    }) => ({
      queryKey: [
        ...queryFactory.qcSpecDoc.archieSpecDoc,
        projectId,
        attemptId,
        includeValueReference,
        includeDictWithValue,
      ] as const,
      queryFn: () =>
        CommunicationService.getArchieQCTaxonomyMapping(
          projectId,
          attemptId,
          includeValueReference,
          includeDictWithValue,
        ),
    }),
    getArchieQCAgents: () => ({
      queryKey: [...queryFactory.qcSpecDoc.archieSpecDoc, 'agents'] as const,
      queryFn: () => CommunicationService.getArchieQCAgents(),
    }),
    getRecentProjectAttempts: ({
      projectId,
      numAttempts,
      reviewLevel,
    }: {
      projectId: string;
      numAttempts: number;
      reviewLevel: ReviewLevel;
    }) => ({
      queryKey: [
        ...queryFactory.qcSpecDoc.recentProjectAttempts,
        projectId,
        numAttempts,
        reviewLevel,
      ] as const,
      queryFn: () =>
        CommunicationService.getRecentProjectAttempts(projectId, numAttempts, reviewLevel),
    }),
  },
  qcEndpointResponse: {
    all: ['qcEndpointResponse'] as const,
    getArchieQCEndpointResponse: ({
      projectId,
      attemptIds,
      requirements,
      specDocVersion,
      launchTaskIdHandle,
      filterOutAttemptIdsWithoutQCResults,
      saveAsGroundTruthMetrics,
      customData,
    }: {
      projectId: string;
      attemptIds: string[];
      requirements: ArchieQCRequirementSpecDocInput[] | null;
      specDocVersion: number;
      launchTaskIdHandle: string | undefined;
      filterOutAttemptIdsWithoutQCResults: boolean;
      saveAsGroundTruthMetrics?: boolean;
      customData?: CustomData;
    }) => ({
      queryKey: [...queryFactory.qcEndpointResponse.all] as const,
      queryFn: () =>
        CommunicationService.getArchieQCEndpointResponse(
          projectId,
          attemptIds,
          requirements,
          specDocVersion,
          launchTaskIdHandle,
          filterOutAttemptIdsWithoutQCResults,
          saveAsGroundTruthMetrics,
          customData,
        ),
      staleTime: 5000 * 1,
    }),
  },
  archieAssistantEndpointResponse: {
    all: ['archieAssistantEndpointResponse'] as const,
    runArchieAssistantEndpoint: ({
      projectId,
      action,
      prompt,
      spec_doc_url,
      instruction_doc_url,
      taxonomy,
      clarifications,
    }: {
      projectId: string;
      action: ArchieAssistantActions;
      prompt?: string;
      spec_doc_url?: string;
      instruction_doc_url?: string;
      taxonomy?: string;
      clarifications?: ClarificationQA[];
    }) => ({
      queryKey: [
        ...queryFactory.archieAssistantEndpointResponse.all,
        projectId,
        action,
        prompt,
        spec_doc_url,
        instruction_doc_url,
        taxonomy,
        clarifications,
      ] as const,
      queryFn: () =>
        CommunicationService.runArchieAssistantEndpoint(
          projectId,
          action,
          prompt,
          spec_doc_url,
          instruction_doc_url,
          taxonomy,
          clarifications,
        ),
      staleTime: 5000 * 1,
    }),
  },
  // Contributor Copilot
  copilot: {
    configuration: ['copilot-configuration'] as const,
    check: ['copilot-check'] as const,
    hillclimbing: ['copilot-hillclimbing'] as const,
    getCopilotConfiguration: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.copilot.configuration, projectId] as const,
      queryFn: () => CommunicationService.getCopilotConfiguration(projectId),
    }),
    getCopilotConfigurationAggregateStats: ({ projectId }: { projectId: string }) => ({
      queryKey: ['copilot-configuration-aggregate-stats', projectId] as const,
      queryFn: () => CommunicationService.getCopilotConfigurationAggregateStats(projectId),
    }),
    getCopilotConfigurationAllStats: ({
      checkId,
      timeFilter,
    }: {
      checkId: string;
      timeFilter: CopilotStatsTimeFilter;
    }) => ({
      queryKey: ['copilot-configuration-all-stats', checkId, timeFilter] as const,
      queryFn: () => CommunicationService.getCopilotConfigurationAllStats(checkId, timeFilter),
    }),
    getCopilotCheckLastKRuns: ({
      checkId,
      filter,
      k,
    }: {
      checkId: string;
      filter: CopilotLastKRunsFilter;
      k: number;
    }) => ({
      queryKey: ['copilot-check-last-k-runs', checkId, filter, k] as const,
      queryFn: () => CommunicationService.getCopilotCheckLastKRuns(checkId, filter, k),
    }),
    getCopilotAgentDatabase: () => ({
      queryKey: ['copilot-agent-database'] as const,
      queryFn: () => CommunicationService.getCopilotAgentDatabase(),
    }),
    toggleCopilotCheckMetricsMode: ({
      projectId,
      checkId,
      metricsMode,
    }: {
      projectId: string;
      checkId: string;
      metricsMode: CopilotMetricsMode;
    }) => ({
      queryKey: [
        ...queryFactory.copilot.hillclimbing,
        'metrics-mode',
        projectId,
        checkId,
        metricsMode,
      ] as const,
      queryFn: () =>
        CommunicationService.toggleCopilotCheckMetricsMode({ projectId, checkId, metricsMode }),
    }),
    getCopilotHillclimbingQueryParamsForCheckRunIds: ({
      configurationId,
      checkId,
      checkRunIds,
    }: {
      configurationId: string;
      checkId: string;
      checkRunIds: string[];
    }) => ({
      queryKey: [
        ...queryFactory.copilot.hillclimbing,
        configurationId,
        checkId,
        checkRunIds,
      ] as const,
      queryFn: () =>
        CommunicationService.getCopilotHillclimbingQueryParamsForCheckRunIds({
          configurationId,
          checkId,
          checkRunIds,
        }),
    }),
    getCopilotCheck: ({ projectId, checkId }: { projectId: string; checkId: string }) => ({
      queryKey: [...queryFactory.copilot.check, checkId] as const,
      queryFn: () => CommunicationService.getCopilotCheck({ projectId, checkId }),
    }),
    getCopilotHillclimbingData: ({
      projectId,
      checkId,
    }: {
      projectId: string;
      checkId: string;
    }) => ({
      queryKey: [...queryFactory.copilot.hillclimbing, 'data', projectId, checkId] as const,
      queryFn: () => CommunicationService.getCopilotHillclimbingData({ projectId, checkId }),
    }),
  },
  groundTruthDisagreements: {
    all: ['groundTruthDisagreements'] as const,
    summarizeAQCDisagreements: ({
      projectId,
      disagreementData,
    }: {
      projectId: string;
      disagreementData: any;
    }) => ({
      queryKey: [
        ...queryFactory.groundTruthDisagreements.all,
        projectId,
        disagreementData,
      ] as const,
      queryFn: () => CommunicationService.summarizeAQCDisagreements(projectId, disagreementData),
      staleTime: 5000 * 1,
    }),
  },
  bmCreationEndpointResponse: {
    all: ['bmCreationEndpointResponse'] as const,
    runArchieBMCreationEndpointResponse: ({ projectId }: { projectId: string }) => ({
      queryKey: [...queryFactory.bmCreationEndpointResponse.all] as const,
      queryFn: () => CommunicationService.runArchieBMCreationEndpointResponse(projectId),
      staleTime: 5000 * 1,
    }),
  },
  users: {
    all: ['users'] as const,
    scaliensKey: () => [...queryFactory.users.all] as const,
    isUserFromQcTeam: () => [...queryFactory.users.all, 'isUserFromQcTeam'] as const,
    lookupScaliens: ({ email }: { email: string }) => ({
      queryKey: [...queryFactory.users.scaliensKey(), email] as const,
      queryFn: () => CommunicationService.lookupScaliens(email),
    }),
    lookupUsersByEmail: ({ email }: { email: string }) => ({
      queryKey: [...queryFactory.users.all, 'lookupUsersByEmail', email] as const,
      queryFn: () => CommunicationService.lookupUsersByEmail(email),
    }),
    fetchUserEmailsByIds: ({ userIds }: { userIds: string[] }) => ({
      queryKey: [...queryFactory.users.all, 'fetchUserEmailsByIds', userIds] as const,
      queryFn: () => CommunicationService.fetchUserEmailsByIds(userIds),
    }),
    fetchUser: ({ userId }: { userId: string }) => ({
      queryKey: [...queryFactory.users.all, userId] as const,
      queryFn: () => CommunicationService.fetchUser(userId),
    }),
    fetchSkillEntries: ({ workerId, filter }: IFetchSkillEntriesParams) => ({
      queryKey: [...queryFactory.users.all, 'skillEntries', workerId, !!filter] as const,
      queryFn: () =>
        CommunicationService.getWorkerSkillEntriesByWorkerId({ workerId, filter: !!filter }),
    }),
    fetchWorkerSkillTaskData: ({ workerId, workerSkillId }: IFetchWorkerSkillTaskDataParams) => ({
      queryKey: [
        ...queryFactory.users.all,
        'workerSkillTaskData',
        workerId,
        workerSkillId,
      ] as const,
      queryFn: () => CommunicationService.getWorkerSkillTaskData({ workerId, workerSkillId }),
    }),
    searchTags: ({ search }: { search: string }) => ({
      queryKey: [...queryFactory.users.all, 'search-tags', search] as const,
      queryFn: () => CommunicationService.searchTags(search),
      enabled: !!search,
    }),
    findCustomers: ({ objectIds }: { objectIds: string }) => ({
      queryKey: [...queryFactory.users.all, 'customers', objectIds] as const,
      queryFn: () => CommunicationService.findCustomers(objectIds),
    }),
    linkedinVanityName: ({ userId }: { userId: string }) => ({
      queryKey: [...queryFactory.users.all, 'linkedin-vanity-name', userId] as const,
      queryFn: () => CommunicationService.getLinkedinVanityName(userId),
    }),
    fraudProfile: ({ userId }: { userId?: string }) => ({
      queryKey: [...queryFactory.users.all, 'fraud-profile', userId] as const,
      queryFn: () => fetchFraudProfile(userId!),
      enabled: !!userId,
    }),
    onboardingState: ({ userId }: { userId?: string }) => ({
      queryKey: [...queryFactory.users.all, 'onboarding-state', userId] as const,
      queryFn: () => CommunicationService.tryToFetchUserOnboardingState(userId!),
      enabled: !!userId,
    }),
    userFromQCTeam: () => ({
      queryKey: [...queryFactory.users.isUserFromQcTeam()] as const,
      queryFn: () => CommunicationService.isUserFromQCTeam(),
    }),
    validateHasRole: ({ corpRole }: { corpRole: string }) => ({
      queryKey: [...queryFactory.users.all, 'validateHasRole', corpRole] as const,
      queryFn: () => CommunicationService.validateHasRole({ corpRole }),
    }),
    validateHasPermission: ({ permission }: { permission: AccessPermission }) => ({
      queryKey: [...queryFactory.users.all, 'validateHasPermission', permission] as const,
      queryFn: () => CommunicationService.validateHasPermission({ permission }),
    }),
    getFullstory: ({ userId }: { userId?: string }) => ({
      queryKey: [...queryFactory.users.all, 'fullstory', userId] as const,
      queryFn: () => CommunicationService.getUserFullstory(userId!),
      enabled: !!userId,
    }),
  },
  teams: {
    all: ['teams'] as const,
    fetchTeams: () => ({
      queryKey: [...queryFactory.teams.all] as const,
      queryFn: () => CommunicationService.getAllWorkerTeams(),
    }),
    searchTeams: ({ searchText }: { searchText: string }) => ({
      queryKey: [...queryFactory.teams.all, 'search', searchText] as const,
      queryFn: () => CommunicationService.searchTeams(searchText),
    }),
    fetchTeam: ({ teamId }: { teamId: string }) => ({
      queryKey: [...queryFactory.teams.all, teamId] as const,
      queryFn: () => CommunicationService.fetchTeam(teamId),
    }),
    fetchTeamContributors: ({ teamId }: { teamId: string }) => ({
      queryKey: [...queryFactory.teams.all, 'contributors', teamId] as const,
      queryFn: () => CommunicationService.fetchTeamContributors(teamId),
    }),
    fetchTeamConfig: ({ teamId }: { teamId: string }) => ({
      queryKey: [...queryFactory.teams.all, 'config', teamId] as const,
      queryFn: () => CommunicationService.fetchTeamConfig(teamId),
    }),
    fetchTeamAssignmentsWithExtras: ({
      teamId,
      includeInherited,
    }: {
      teamId: string;
      includeInherited: boolean;
    }) => ({
      queryKey: [...queryFactory.teams.all, 'assignments', teamId, includeInherited] as const,
      queryFn: () => CommunicationService.fetchTeamAssignmentsWithExtras(teamId, includeInherited),
    }),
    fetchTeamAssignmentEvents: ({ teamId }: { teamId: string }) => ({
      queryKey: [...queryFactory.teams.all, 'assignmentEvents', teamId] as const,
      queryFn: () => CommunicationService.fetchTeamAssignmentEvents(teamId),
    }),
    fetchTeamContributorEvents: ({ teamId }: { teamId: string }) => ({
      queryKey: [...queryFactory.teams.all, 'contributorEvents', teamId] as const,
      queryFn: () => CommunicationService.fetchTeamContributorEvents(teamId),
    }),
  },
  admin: {
    all: ['admin'] as const,
    finances: () => [...queryFactory.admin.all, 'finances'] as const,
    financeGlobalConfigs: () => {
      return {
        queryKey: [...queryFactory.admin.finances(), 'globalConfigs'] as const,
        queryFn: () => CommunicationService.getFinanceGlobalConfigs(),
      };
    },
    financeGuardrailConfigs: ({ projectId }: { projectId: string }) => {
      return {
        queryKey: [...queryFactory.admin.finances(), 'guardrailConfigs', projectId] as const,
        queryFn: () => CommunicationService.getFinanceGuardrailConfigs(projectId),
      };
    },
    billingApprovalRequests: () => {
      return {
        queryKey: [...queryFactory.admin.finances(), 'approvalRequests'] as const, // .billing() needs def
        queryFn: () => CommunicationService.getAllBillingApprovalRequests(),
      };
    },
    financeApprovalRequests: () => {
      return {
        queryKey: [...queryFactory.admin.finances(), 'approvalRequests'] as const,
        queryFn: () => CommunicationService.getAllFinanceApprovalRequests(),
      };
    },
    financeApprovalRequestByProjectId: ({ projectId }: { projectId: string }) => {
      return {
        queryKey: [...queryFactory.admin.financeApprovalRequests().queryKey, projectId] as const,
        queryFn: () => CommunicationService.getFinanceApprovalRequestsByProjectId(projectId),
      };
    },
    createFinanceApprovalRequest: (request: SaveFinanceApprovalRequestInput) => {
      return {
        queryKey: [...queryFactory.admin.finances(), 'createApprovalRequest'] as const,
        queryFn: () => CommunicationService.createFinanceApprovalRequest(request),
      };
    },
    userAndProjectNames: (approvalRequests: IFinanceApprovalRequest[]) => {
      return {
        queryKey: [
          ...queryFactory.admin.financeApprovalRequests().queryKey,
          'userAndProjectNames',
        ] as const,
        queryFn: () => CommunicationService.getUserAndProjectNames(approvalRequests),
      };
    },
    userAndProjectBillingNames: (approvalRequests: any) => {
      return {
        queryKey: [
          ...queryFactory.admin.financeApprovalRequests().queryKey,
          'userAndProjectNames',
        ] as const,
        queryFn: () => CommunicationService.getUserAndProjectNames(approvalRequests),
      };
    },
    permittedEmails: () => {
      return {
        queryKey: [...queryFactory.admin.all, 'permittedEmails'] as const,
        queryFn: () => CommunicationService.getPermittedEmails(),
      };
    },
    defaults: () => {
      return {
        queryKey: [...queryFactory.admin.all, 'defaults'] as const,
        queryFn: () => CommunicationService.getAdminFinanceDefaults(),
      };
    },
  },
  jobs: {
    all: ['jobs'] as const,
    byQuery: (query: IQueryCorpJobs) => ({
      queryKey: [...queryFactory.jobs.all, query] as const,
      queryFn: () => CommunicationService.fetchJobsByQuery(query),
    }),
    cache: ({ corpJobId }: { corpJobId?: string }) => ({
      queryKey: [...queryFactory.jobs.all, corpJobId] as const,
      queryFn: () => CommunicationService.fetchCorpJobCache(corpJobId),
    }),
  },
  redash: {
    all: ['redash'] as const,
    runQuery: ({ queryUrl, queryParams }: IUseRedashQueryProps) => ({
      queryKey: [...queryFactory.redash.all, queryUrl, JSON.stringify(queryParams)] as const,
      queryFn: () => CommunicationService.runRedashQuery({ queryUrl, queryParams }),
    }),
  },
  courses: {
    courseById: ({ courseId }: { courseId: string | undefined }) => ({
      queryKey: ['getCourseById', courseId] as const,
      queryFn: () => CommunicationService.getCourseById(courseId || ''),
      enabled: !!courseId,
    }),
  },
  coursesv2: {
    all: ['coursesv2'] as const,
    courseV2LibraryEntryById: ({
      projectId,
      limit,
      page,
      searchString,
      archived,
    }: {
      projectId: string;
      limit?: number;
      page?: number;
      searchString?: string;
      archived?: boolean;
    }) => ({
      queryKey: [
        'getCourseV2LibraryEntriesById',
        projectId,
        limit,
        page,
        searchString,
        archived,
      ] as const,
      queryFn: () =>
        CommunicationService.fetchCourseV2LibraryEntriesById(
          projectId,
          limit,
          page,
          searchString,
          archived,
        ),
    }),
    createOrUpdateCourseV2: (data: any) => ({
      queryKey: ['createOrUpdateCourseV2', data] as const,
      queryFn: () => CommunicationService.createOrUpdateCourseV2(data),
    }),
    courseV2ById: ({ courseId }: { courseId: string }) => ({
      queryKey: ['getCourseV2ById', courseId] as const,
      queryFn: () => CommunicationService.getCourseV2ById(courseId),
    }),
    courseV2Templates: () => ({
      queryKey: ['getCourseV2Templates'] as const,
      queryFn: () => CommunicationService.getAllCourseV2Templates(),
    }),
    courseV2VersionHistory: ({ courseId }: { courseId: string }) => ({
      queryKey: ['getCourseV2VersionHistory', courseId] as const,
      queryFn: () => CommunicationService.getCourseV2VersionHistory({ courseId }),
    }),
    revertToCourseV2Version: (params: PostCourseV2VersionRevertParams) => ({
      queryKey: ['revertToCourseV2Version', params] as const,
      queryFn: () => CommunicationService.revertToCourseV2Version(params),
    }),
    existingQualificationByCourseName: ({ name }: { name: string }) => ({
      queryKey: ['existingQualificationByCourseName'] as const,
      queryFn: () => CommunicationService.getQualificationByCourseName(name),
    }),
    courseV2ReadOnlyChatTask: ({ taskId }: { taskId: string }) => ({
      queryKey: ['courseV2ReadOnlyChatTask', taskId] as const,
      queryFn: () => CommunicationService.getCourseV2ReadOnlyChatTask(taskId),
    }),
    courseV2ReadOnlyChatV2Task: ({ taskId }: { taskId: string }) => ({
      queryKey: ['courseV2ReadOnlyChatV2Task', taskId] as const,
      queryFn: () => CommunicationService.getCourseV2ReadOnlyChatV2Task(taskId),
    }),
    courseV2ReadOnlyTaskAttempt: ({ attemptId }: { attemptId: string }) => ({
      queryKey: ['courseV2ReadOnlyTaskAttempt', attemptId] as const,
      queryFn: () => CommunicationService.getCourseV2ReadOnlyTaskAttempt(attemptId),
    }),
    courseV2Results: ({ courseId }: { courseId: string }) => ({
      queryKey: ['courseV2Results', courseId] as const,
      queryFn: () => CommunicationService.getCourseV2Results(courseId),
    }),
    courseV2AssignedUsers: ({ courseId }: { courseId: string }) => ({
      queryKey: ['courseV2AssignedUsers', courseId] as const,
      queryFn: () => CommunicationService.getCourseV2AssignedUsers(courseId),
    }),
    courseV2UserStatus: ({ courseId }: { courseId: string }) => ({
      queryKey: ['courseV2UserStatus', courseId] as const,
      queryFn: () => CommunicationService.getCourseV2UserStatus(courseId),
    }),
    courseV2ProblemBreakdown: ({ courseId }: { courseId: string }) => ({
      queryKey: ['courseV2ProblemBreakdown', courseId] as const,
      queryFn: () => CommunicationService.getCourseV2ProblemBreakdown(courseId),
    }),
    courseV2Stats: ({ courseId, workerId }: { courseId: string; workerId?: string }) => ({
      queryKey: ['courseV2Stats', courseId, workerId] as const,
      queryFn: () => CommunicationService.getCourseV2Stats(courseId, workerId),
    }),
    courseV2UserProgress: ({
      courseId,
      workerId,
      isCompleted,
      page,
      pageSize,
      sortBy,
      sortOrder,
    }: {
      courseId: string;
      workerId?: string;
      isCompleted?: boolean;
      page?: number;
      pageSize?: number;
      sortBy?: string;
      sortOrder?: string;
    }) => ({
      queryKey: [
        'courseV2UserProgress',
        courseId,
        workerId,
        isCompleted,
        page,
        pageSize,
        sortBy,
        sortOrder,
      ] as const,
      queryFn: () =>
        CommunicationService.getCourseV2UserProgress(
          courseId,
          workerId,
          isCompleted,
          page,
          pageSize,
          sortBy,
          sortOrder,
        ),
    }),
  },
  projectRoles: {
    all: ['projectRoles'] as const,
    projectRolesByUser: (userId: string) => ({
      queryKey: [...queryFactory.projectRoles.all, userId] as const,
      queryFn: () => CommunicationService.getProjectRolesByUser(userId),
      enabled: !!userId,
    }),
    qmsByProjects: (projectIds: string[]) => ({
      queryKey: [...queryFactory.projectRoles.all, 'qmsByProjects', projectIds] as const,
      queryFn: () => CommunicationService.getQMsByProjects(projectIds),
      enabled: !!projectIds?.length,
    }),
    flagsForStaleRoles: (userId: string) => ({
      queryKey: [...queryFactory.projectRoles.all, 'flagsForStaleRoles', userId] as const,
      queryFn: () => CommunicationService.getFlagsForStaleRoles(userId),
    }),
    operationsRoster: () => ({
      queryKey: [...queryFactory.projectRoles.all, 'operationsRoster'] as const,
      queryFn: () => CommunicationService.getOperationsRoster(),
    }),
    operationsRosterForWeeklyRolePopUp: () => ({
      queryKey: [...queryFactory.projectRoles.all, 'operationsRosterForWeeklyRolePopUp'] as const,
      queryFn: () => CommunicationService.getOperationsRosterForWeeklyRolePopUp(),
    }),
    stoRoster: () => ({
      queryKey: [...queryFactory.projectRoles.all, 'stoRoster'] as const,
      queryFn: () => CommunicationService.getSTORoster(),
    }),
  },
  lookup: {
    all: ['lookup'] as const,
    search: ({ searchText }: { searchText: string }) => ({
      queryKey: [...queryFactory.lookup.all, 'search', searchText] as const,
      queryFn: () => CommunicationService.lookupSearch(searchText),
    }),
    object: (id: string) => ({
      queryKey: [...queryFactory.lookup.all, id] as const,
      queryFn: () => CommunicationService.lookupObject(id),
    }),
    task: (taskId: string) => ({
      queryKey: [...queryFactory.lookup.all, 'task', taskId] as const,
      queryFn: () => CommunicationService.lookupTask(taskId),
    }),
    taskLite: (taskId: string) => ({
      queryKey: [...queryFactory.lookup.all, 'taskLite', taskId] as const,
      queryFn: () => CommunicationService.lookupTaskLite(taskId),
    }),
    attempt: (attemptId: string) => ({
      queryKey: [...queryFactory.lookup.all, 'attempt', attemptId] as const,
      queryFn: () => CommunicationService.lookupAttempt(attemptId),
    }),
  },
  pipelineV3: {
    all: ['pipeline-v3'] as const,
    history: (objectId: string) => ({
      queryKey: [...queryFactory.pipelineV3.all, objectId] as const,
      queryFn: () => CommunicationService.getPipelineV3History(objectId),
    }),
  },
  billing: {
    projectBillingPartitions: ({ projectId }: { projectId: string }) => ({
      queryKey: ['projectBillingPartitions', projectId] as const,
      queryFn: () => CommunicationService.getProjectBillingPartitions(projectId),
    }),
    projectBillingProposalApprovers: ({ projectId }: { projectId: string }) => ({
      queryKey: ['projectBillingProposalApprovers', projectId] as const,
      queryFn: () => CommunicationService.getProjectBillingProposalApprovers(projectId),
    }),
  },
  payPerTask: {
    getPayPerTaskInfo: ({ projectId }: { projectId: string }) => ({
      queryKey: ['getPayPerTaskInfo', projectId] as const,
      queryFn: () => CommunicationService.getPayPerTaskInfo(projectId),
    }),
    updatePayPerTaskInfo: ({
      projectId,
      isPayPerTaskEnabled,
      isDynamicPricingEnabled,
      isBasePayRangeEnabled,
      isAssessmentRateBonusEnabled,
    }: {
      projectId: string;
      isPayPerTaskEnabled: boolean;
      isDynamicPricingEnabled: boolean;
      isBasePayRangeEnabled: boolean;
      isAssessmentRateBonusEnabled: boolean;
    }) => ({
      queryKey: ['updatePayPerTaskInfo', projectId] as const,
      queryFn: () =>
        CommunicationService.updatePayPerTaskInfo(
          projectId,
          isPayPerTaskEnabled,
          isDynamicPricingEnabled,
          isBasePayRangeEnabled,
          isAssessmentRateBonusEnabled,
        ),
    }),
  },
  projectPayConfigs: {
    getProjectPayConfigs: ({ projectId }: { projectId: string }) => ({
      queryKey: ['getProjectPayConfigs', projectId] as const,
      queryFn: () => CommunicationService.getProjectPayConfigs(projectId),
    }),
  },
  modelRegistry: {
    getModelEndpoints: () => ({
      queryKey: ['modelEndpoints'] as const,
      queryFn: () => CommunicationService.getModelEndpoints(),
    }),
  },
  schemaRegistry: {
    getJSONSchemas: () => ({
      queryKey: ['jsonSchemas'] as const,
      queryFn: () => CommunicationService.getJSONSchemas(),
    }),
    getProjectSchemaId: ({ projectId }: { projectId: string }) => ({
      queryKey: ['projectSchemaId', projectId] as const,
      queryFn: () => CommunicationService.getProjectSchemaId(projectId),
    }),
    getProjectSchema: ({ projectId }: { projectId: string }) => ({
      queryKey: ['projectSchema', projectId] as const,
      queryFn: () => CommunicationService.getProjectSchema(projectId),
    }),
    getSchemaById: ({ schemaId }: { schemaId: string }) => ({
      queryKey: ['schemaById', schemaId] as const,
      queryFn: () => CommunicationService.getSchemaById(schemaId),
    }),
    validateSingleJSON: ({ schemaId, json }: { schemaId: string; json: any }) => ({
      queryKey: ['validateSingleJSON', schemaId, json] as const,
      queryFn: () => CommunicationService.validateSingleJSON(schemaId, json),
    }),
  },
  CSAT: {
    all: ['CSAT'] as const,
    getResponsesFromLastWeekByCampaignName: ({ campaignName }: { campaignName: string }) => ({
      queryKey: [
        ...queryFactory.CSAT.all,
        'getResponsesFromLastWeekByCampaignName',
        campaignName,
      ] as const,
      queryFn: () => CommunicationService.getCSATResponsesFromLastWeekByCampaignName(campaignName),
    }),
  },
  questionBank: {
    all: ['questionBank'] as const,
    getQuestionBankByProjectId: ({ projectId }: { projectId: string }) => ({
      queryKey: ['getQuestionBankByProjectId', projectId] as const,
      queryFn: () => CommunicationService.getQuestionBankByProjectId(projectId),
    }),
    getQuestionBanks: () => ({
      queryKey: ['getQuestionBanks'] as const,
      queryFn: () => CommunicationService.getQuestionBanks(),
    }),
    getQuestionsByQuestionBankId: ({ questionBankId }: { questionBankId: string }) => ({
      queryKey: ['getQuestionsByQuestionBankId', questionBankId] as const,
      queryFn: () => CommunicationService.getQuestionsByQuestionBankId(questionBankId),
    }),
    getProjectsByQuestionBankId: ({ questionBankId }: { questionBankId: string }) => ({
      queryKey: ['getProjectsByQuestionBankId', questionBankId] as const,
      queryFn: () => CommunicationService.getProjectsByQuestionBankId(questionBankId),
    }),
    getProjectsForQuestion: ({ questionId }: { questionId: string }) => ({
      queryKey: ['getProjectsForQuestion', questionId] as const,
      queryFn: () => CommunicationService.getProjectsForQuestion(questionId),
      enabled: !!questionId,
    }),
    getQuestionsForProject: ({ projectId }: { projectId: string }) => ({
      queryKey: ['getQuestionsForProject', projectId] as const,
      queryFn: () => CommunicationService.getQuestionsForProject(projectId),
      enabled: !!projectId,
    }),
  },
  chatCompletion: {
    generatePythonDataTransformationScript: (body: CreateCompletionRequest) => ({
      queryKey: [
        'generatePythonDataTransformationScript',
        body.strategyName,
        body.messages[0].content,
      ] as const,
      queryFn: () => CommunicationService.chatCompletion(body),
      enabled: false,
    }),
  },
  randomConfigs: {
    getRandomConfig: ({ randomConfigName }: { randomConfigName: string }) => ({
      queryKey: ['getRandomConfig', randomConfigName] as const,
      queryFn: () => CommunicationService.getRandomConfig(randomConfigName),
    }),
    getDisputeCategoryDefinitions: () => ({
      queryKey: ['getDisputeCategoryDefinitions'] as const,
      queryFn: () => CommunicationService.getDisputeCategoryDefinitions(),
    }),
  },
  workerSkills: {
    getAllWorkerSkills: (tree: boolean, treeLevels?: number) => ({
      queryKey: ['workerSkills', tree, treeLevels] as const,
      queryFn: () => CommunicationService.getAllWorkerSkills(tree, treeLevels),
    }),
  },
  customerUpload: {
    getStagingFilesForDelivery: ({
      customer,
      datestamp,
    }: {
      customer: string;
      datestamp: string;
    }) => ({
      queryKey: ['getStagingFilesForDelivery', customer, datestamp] as const,
      queryFn: () => CommunicationService.getStagingFilesForDelivery(customer, datestamp),
    }),
    getExpectedFilesForDelivery: ({
      customer,
      datestamp,
    }: {
      customer: string;
      datestamp: string;
    }) => ({
      queryKey: ['getExpectedFilesForDelivery', customer, datestamp] as const,
      queryFn: () => CommunicationService.getExpectedFilesForDelivery(customer, datestamp),
    }),
  },
  trustAndSafety: {
    getDeliveryOpsTaskAttempts: ({
      filters,
    }: {
      filters: { projectId: string; statuses?: AuditStatus[] };
    }) => ({
      queryKey: ['getDeliveryOpsTaskAttempts', filters] as const,
      queryFn: () => CommunicationService.getDeliveryOpsTaskAttempts(filters),
    }),
    getDeliveryOpsTaskAttemptAuditById: ({ auditId }: { auditId: string }) => ({
      queryKey: ['getDeliveryOpsTaskAttempts', auditId] as const,
      queryFn: () => CommunicationService.getDeliveryOpsTaskAttemptAuditById(auditId),
    }),
    getAuditById: ({ auditId }: { auditId: string }) => ({
      queryKey: ['getAuditById', auditId] as const,
      queryFn: () => CommunicationService.getAuditById(auditId),
    }),
    getAuditsByTypeAndStatus: ({
      auditTypes,
      auditStatuses,
    }: {
      auditTypes?: AuditType[];
      auditStatuses?: AuditStatus[];
    }) => ({
      queryKey: ['getAuditsByTypeAndStatus'],
      queryFn: () => CommunicationService.getAuditsByTypeAndStatus(auditTypes, auditStatuses),
    }),
    getAuditAttachmentUrls: ({ auditId }: { auditId: string }) => ({
      queryKey: ['getAuditAttachments', auditId] as const,
      queryFn: () => CommunicationService.getAuditAttachmentUrls(auditId),
    }),
  },
  quickGrab: {
    quickGrabTasksByReviewLevel: ({ projectId }: { projectId: string }) => ({
      queryKey: ['getQuickGrabTasksByReviewLevel', projectId] as const,
      queryFn: () => CommunicationService.getQuickGrabTasksByReviewLevel(projectId),
      enabled: !!projectId,
    }),
  },
  financeAccess: {
    getFinanceAccess: ({ projectId, userId }: { projectId: string; userId: string }) => ({
      queryKey: ['getFinanceAccess', projectId, userId] as const,
      queryFn: () => CommunicationService.getFinanceAccess(projectId, userId),
    }),
  },
  archieOffPlatformData: {
    syncOffPlatformData: ({ projectId, customData }: { projectId: string; customData: any }) => ({
      mutationFn: () => CommunicationService.syncOffPlatformData(projectId, customData),
    }),
  },
};

// For testing & demonstrating error handling.
export const useGetDevError = (projectId: string, status: DevErrorStatus) => {
  return useQuery({
    ...queryFactory.devErrorsTesting.devError({ projectId, status }),
  });
};

export const useGetTasksV2FlattenedResponse = ({
  taskIds,
  deliveryBuilderConfigId,
  enabled,
}: {
  taskIds: string[];
  deliveryBuilderConfigId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    ...queryFactory.tasks.getTasksV2FlattenedResponse({ taskIds, deliveryBuilderConfigId }),
    enabled,
  });
};

export const useProjectIsEligibleForApiV2 = ({
  projectId,
  taskIds,
  templateVersions,
  makeAPICall = true,
}: {
  projectId: string;
  taskIds?: string[];
  templateVersions?: number[];
  makeAPICall?: boolean;
}) => {
  return useQuery({
    ...queryFactory.projects.projectIsEligibleForApiV2({ projectId, taskIds, templateVersions }),
    enabled: makeAPICall,
  });
};

export const useGetRandomConfig = ({ randomConfigName }: { randomConfigName: string }) => {
  return useQuery({
    ...queryFactory.randomConfigs.getRandomConfig({ randomConfigName }),
  });
};

// For testing & demonstrating error handling.
export const useMutateDevError = (projectId: string) => {
  return useMutation({
    mutationFn: ({ status, project }: { status: DevErrorStatus; project: any }) => {
      return CommunicationService.mutateDevError(status, project);
    },
    onMutate: async () => {
      return {
        previousData: {
          someOldValue: 'oldValue',
          someOtherOldValue: 'otherOldValue',
        },
      };
    },
    onError: () => {
      // Do anything here that is specific to this mutation, e.g. invalidating caches.
      // This specific onError function will be called, as well as the global onError in mutationCache.
      queryClient.invalidateQueries({
        queryKey: queryFactory.devErrorsTesting.devError({
          projectId,
          status: DevErrorStatus.Success,
        }).queryKey,
      });
    },
  });
};

export const useGetSlaGroup = (projectId: string) => {
  return useQuery({
    ...queryFactory.projects.slaGroup({ projectId }),
  });
};

export const useGetS3DeliveryFiles = (
  projectId: string,
  source: 'staging' | 'customer' = 'customer',
) => {
  return useQuery({
    queryKey: ['getS3DeliveryFiles', projectId, source],
    queryFn: async () => {
      return await CommunicationService.getS3DeliveryFiles(projectId, source);
    },
  });
};

export const useLookupTasksInDeliveryState = () => {
  return useMutation({
    mutationFn: async ({ projectId, taskIds }: { projectId: string; taskIds: string[] }) => {
      return await CommunicationService.lookupTasksInDeliveryState(projectId, taskIds);
    },
  });
};

export const useGetS3DeliveryFilesByPrefix = (
  prefix: string,
  startDate: moment.Moment | null,
  endDate: moment.Moment | null,
  enabled: boolean = true,
) => {
  const startDateString = startDate ? startDate.format('YYYYMMDD') : '';
  const endDateString = endDate ? endDate.format('YYYYMMDD') : '';

  return useQuery({
    queryKey: ['getS3DeliveryFilesByPrefix', prefix, startDateString, endDateString],
    queryFn: async () => {
      return await CommunicationService.getS3DeliveryFilesByPrefix(
        prefix,
        startDateString,
        endDateString,
      );
    },
    enabled, // Allow queries with empty prefix for breakdown mode
  });
};

export const useProjectGroupNode = (params: { projectId: string }) => {
  return useQuery<CorpProjectGroupNode | undefined, Error>({
    ...queryFactory.projectGroup.node(params),
  });
};

export const useQMProject = (projectId: string) => {
  return useQuery<QMShowProjectResponse>({
    ...queryFactory.projects.qmProject({ projectId }),
  });
};

export const useProjectDefaultErrorCategories = (projectId: string) => {
  return useQuery<IAuditErrorCategory[]>({
    ...queryFactory.projects.projectDefaultErrorCategories({ projectId }),
  });
};

export const useProjectSpecificErrorCategories = (projectId: string) => {
  return useQuery<IAuditErrorCategory[]>({
    ...queryFactory.projects.projectSpecificErrorCategories({ projectId }),
  });
};

export const useAddProjectSpecificErrorCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      category,
    }: {
      projectId: string;
      category: IAuditErrorCategoryInput;
    }) => CommunicationService.addProjectSpecificErrorCategory(projectId, category),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.projects.projectSpecificErrorCategories({ projectId }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: ['auditErrorCategories', projectId],
      });
    },
    onError: (error: any) => {
      toast(`Failed to add error category: ${error.response?.message}`, {
        type: 'error',
        autoClose: 5000,
        position: 'top-right',
      });
    },
  });
};

export const useDefaultErrorCategoriesEnabled = (projectId: string) => {
  return useQuery<boolean>({
    ...queryFactory.projects.defaultErrorCategoriesEnabled({ projectId }),
  });
};

export const useProjectFieldIds = (projectId: string) => {
  return useQuery<string[]>({
    ...queryFactory.projects.projectFieldIds({ projectId }),
  });
};

export const useGetProjectPausedHistory = (projectId: string) => {
  return useQuery<PausedHistoryResponse>({
    ...queryFactory.projects.projectPausedHistory({ projectId }),
  });
};

export const useGetProjectCosts = (fromDate: Date, toDate: Date, projectId?: string) => {
  return useQuery<ProjectCostsResponse>({
    ...queryFactory.projects.projectCostsByDate({ fromDate, toDate, projectId }),
  });
};

export const useGetProjectCostsDaily = (projectId: string) => {
  return useQuery<ProjectCostsDailyResponse>({
    ...queryFactory.projects.projectCostsDaily({ projectId }),
  });
};

export const useGetProjectCostsCumulative = (projectId: string) => {
  return useQuery<ProjectCostsCumulativeResponse>({
    ...queryFactory.projects.projectCostsCumulative({ projectId }),
  });
};

export const useGetProjectCostsPerTask = (projectId: string, forceUpdate = false) => {
  return useQuery<ProjectCostsPerTaskResponse>({
    ...queryFactory.projects.projectCostsPerTask({ projectId, forceUpdate }),
  });
};

export const useGetProjectCostsPerAttemptByReviewLevel = (projectId: string) => {
  return useQuery<ProjectCostPerAttemptLevelResponse>({
    ...queryFactory.projects.projectCostsPerAttemptByReviewLevel({ projectId }),
  });
};

export const useGetProjectFinanceAlertConfigs = (projectId: string) => {
  return useQuery<FinanceAlertConfigResponse>({
    ...queryFactory.projects.projectFinanceAlertConfigs({ projectId }),
  });
};

export const useGetFinanceGlobalConfigs = () => {
  return useQuery<FinanceGlobalConfigResponse>({
    ...queryFactory.admin.financeGlobalConfigs(),
  });
};

export const useGetFinanceGuardrailConfigs = (projectId: string) => {
  return useQuery<FinanceGuardrailConfigResponse>({
    ...queryFactory.admin.financeGuardrailConfigs({ projectId }),
  });
};

export const useGetFinanceApprovalRequests = () => {
  return useQuery<FinanceApprovalRequestResponse>({
    ...queryFactory.admin.financeApprovalRequests(),
  });
};

export const useGetBillingApprovalRequests = () => {
  return useQuery<BillingApprovalRequestResponse>({
    ...queryFactory.admin.billingApprovalRequests(),
  });
};

export const useGetUserAndProjectNames = (approvalRequests: IFinanceApprovalRequest[]) => {
  return useQuery<FinanceIdNameMapResponse>({
    ...queryFactory.admin.userAndProjectNames(approvalRequests),
  });
};

export const useGetUserAndProjectBillingNames = (approvalRequests: IBillingApprovalRequest[]) => {
  return useQuery<FinanceIdNameMapResponse>({
    ...queryFactory.admin.userAndProjectBillingNames(approvalRequests),
  });
};

export const useGetProjectApprovalRequests = (projectId: string) => {
  return useQuery<FinanceApprovalRequestResponse>({
    ...queryFactory.admin.financeApprovalRequestByProjectId({ projectId }),
  });
};

export const useGetPodLeadUsers = () => {
  return useQuery<{ name: string; value: PodLeadUser[] }>({
    ...queryFactory.admin.permittedEmails(),
  });
};

export const useGetAdminFinanceDefaults = () => {
  return useQuery<AdminFinanceDefaultsResponse>({
    ...queryFactory.admin.defaults(),
  });
};

export const useUpdateAdminFinanceDefaults = () => {
  return useMutation({
    mutationFn: ({
      pagerDutyUserEmail,
      slackPodLeadEmails,
    }: {
      pagerDutyUserEmail: string;
      slackPodLeadEmails: string[];
    }) => {
      return CommunicationService.updateAdminFinanceDefaults({
        pagerDutyUserEmail,
        slackPodLeadEmails,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.defaults().queryKey,
      });
      toast('Finance Defaults were updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useProcessGuardrailUpload = () => {
  return useMutation({
    mutationFn: (request: BulkUploadGuardrailRequest) => {
      return CommunicationService.processGuardrailUpload(request);
    },
  });
};

export const useCreateFinanceApprovalRequest = () => {
  return useMutation({
    mutationFn: ({ request }: { request: SaveFinanceApprovalRequestInput }) => {
      return CommunicationService.createFinanceApprovalRequest(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeApprovalRequests().queryKey,
      });
      toast('Budget request was created successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeApprovalRequests().queryKey,
      });
    },
  });
};

export const useUpdateFinanceApprovalRequest = () => {
  return useMutation({
    mutationFn: ({ request, id }: { request: SaveFinanceApprovalRequestInput; id: string }) => {
      return CommunicationService.updateFinanceApprovalRequest(request, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeApprovalRequests().queryKey,
      });
      toast('Budget request was updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeApprovalRequests().queryKey,
      });
    },
  });
};

export const useProjectConfigs = ({
  projectIds,
  configsToFetch,
}: {
  projectIds: string[];
  configsToFetch?: string[];
}) => {
  return useQuery<CorpProjectGroupProjectGroupNode[] | undefined>({
    ...queryFactory.projectConfigs.find({ projectIds, configsToFetch }),
  });
};

export const useDeleteUserAutenticators = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => CommunicationService.deleteUserAuthenticators(userId),
    onSuccess: () => {
      toast.success('MFA data deleted successfully');
      queryClient.invalidateQueries(queryFactory.lookup.object(userId).queryKey);
    },
  });
};

export const useChangeBPOUniversalStaffingPermission = (userId: string, allow: boolean) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => CommunicationService.changeBPOUniversalStaffingPermission(userId, allow),
    onSuccess: () => {
      toast.success('Universal staffing permissions updated successfully');
      queryClient.invalidateQueries(queryFactory.lookup.object(userId).queryKey);
    },
  });
};

export const useUpdateWhiteListSettings = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ipWhitelist: string[]) =>
      CommunicationService.updateWhiteListSettings(userId, ipWhitelist),
    onSuccess: () => {
      toast.success('User IP white list updated successfully');
      queryClient.invalidateQueries(queryFactory.lookup.object(userId).queryKey);
    },
  });
};

export const useToggleBPOAdmin = (userId: string, isBPOAdmin: boolean) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => CommunicationService.toggleBPOAdmin(userId, isBPOAdmin),
    onSuccess: () => {
      toast.success('User BPO Admin status updated successfully');
      queryClient.invalidateQueries(queryFactory.lookup.object(userId).queryKey);
    },
  });
};

export const useUpdateTags = (params: {
  tagsToAdd: string[];
  tagsToRemove: string[];
  userId: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => CommunicationService.updateTags(params),
    onSuccess: () => {
      toast.success('User tags updated successfully');
      queryClient.invalidateQueries(queryFactory.lookup.object(params.userId).queryKey);
    },
  });
};

export const useUpdateSingleTag = (params: {
  tagId: string;
  tag: {
    description: string;
    trustedTagMetadata: { qualityType: string; projects: string[]; workerSkills: string[] };
  };
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => CommunicationService.updateSingleTag(params),
    onSuccess: () => {
      toast.success('Tag updated successfully');
      queryClient.invalidateQueries(queryFactory.lookup.object(params.tagId).queryKey);
    },
  });
};

export const useCreateTag = (params: CreateTagParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => CommunicationService.createTag(params),
    onSuccess: () => {
      toast.success('User tag created successfully');
      if (params.userId) {
        queryClient.invalidateQueries(queryFactory.lookup.object(params.userId).queryKey);
      }
    },
  });
};

export const useGetAllTrustedTags = () => {
  return useQuery({
    queryKey: [...queryFactory.projectConfigs.all, 'getAllTrustedTags'] as const,
    queryFn: async () => {
      const response = await apiFetch(null, 'corp-api/tags/getAllTrustedTags', {
        method: 'GET',
      });
      return response;
    },
  });
};

export const useUpdateFinanceAlertConfig = () => {
  return useMutation({
    mutationFn: ({ financeAlertConfig }: { financeAlertConfig: FinanceAlertConfig }) => {
      return CommunicationService.updateFinanceAlertConfig(financeAlertConfig);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeGlobalConfigs().queryKey,
      });
      toast('Finance Alert was updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useUpdateFinanceGuardrailConfig = () => {
  return useMutation({
    mutationFn: ({
      financeGuardrailConfig,
    }: {
      financeGuardrailConfig: FinanceGuardrailConfig;
    }) => {
      return CommunicationService.updateFinanceGuardrailConfig(financeGuardrailConfig);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeGlobalConfigs().queryKey,
      });
      toast('Finance Guardrail was updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useSearchTags = ({ search }: { search: string }) => {
  return useQuery({
    ...queryFactory.users.searchTags({ search }),
  });
};

export const useTaskTemplate = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projects.taskTemplate({ projectId }),
  });
};

export const useWorkspaceContainers = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projects.workspaceContainerFields({ projectId }),
  });
};

export const useChatTaskFeatureConfig = (
  { projectId }: { projectId: string },
  options?: UseQueryOptions<{ chatTaskFeatureConfig: IChatTaskFeatureConfig } | undefined>,
) => {
  return useQuery<{ chatTaskFeatureConfig: IChatTaskFeatureConfig } | undefined>({
    ...queryFactory.projects.chatTaskFeatureConfig({ projectId }),
    ...options,
  });
};

export const useUpdateChatTaskFeatureConfig = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      chatTaskFeatureConfig,
    }: {
      projectId: string;
      chatTaskFeatureConfig: IChatTaskFeatureConfig;
    }) => {
      return await CommunicationService.updateChatTaskFeatureConfig(
        projectId,
        chatTaskFeatureConfig,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.projects.chatTaskFeatureConfigKey(),
        exact: false,
      });
    },
  });
};

export const useGetDeliveryBuilderConfigs = (
  projectId: string,
  options?: UseQueryOptions<DeliveryBuilderConfigDTO[] | undefined>,
) => {
  return useQuery<DeliveryBuilderConfigDTO[]>({
    ...queryFactory.projects.deliveryBuilderConfigs(projectId),
    enabled: options?.enabled,
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useGetDeliveryBuilderConfigById = (
  configId: string | undefined,
  options?: UseQueryOptions<DeliveryBuilderConfigDTO | undefined>,
) => {
  return useQuery<DeliveryBuilderConfigDTO>({
    ...queryFactory.projects.deliveryBuilderConfigById(configId!),
    enabled: !!configId && (options?.enabled ?? true),
    staleTime: 60000, // Cache for 1 minute since config rarely changes
    refetchOnMount: false,
  });
};

export const useSetDeliveryBuilderConfigMutation = () => {
  return useMutation({
    mutationFn: ({
      deliveryBuilderConfig,
    }: {
      deliveryBuilderConfig: DeliveryBuilderConfigCreate;
    }) => {
      return CommunicationService.setDeliveryBuilderConfig(deliveryBuilderConfig);
    },
    onSuccess: (_, { deliveryBuilderConfig }) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...queryFactory.projects.deliveryBuilderConfigKey(),
          deliveryBuilderConfig.projectId,
        ],
      });
    },
  });
};

export const useDisableDeliveryBuilderConfigMutation = () => {
  return useMutation({
    mutationFn: (deliveryBuilderConfigId: string) => {
      return CommunicationService.disableDeliveryBuilderConfig(deliveryBuilderConfigId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.projects.deliveryBuilderConfigKey(),
      });
    },
  });
};

export const useUpdatePipelineV3HookConfig = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      pipelineV3HookConfig,
    }: {
      projectId: string;
      pipelineV3HookConfig: any;
    }) => {
      return await CommunicationService.updatePipelineV3HookConfig(projectId, pipelineV3HookConfig);
    },
  });
};

export const useUpdatePipelineV3Config = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      pipelineV3Config,
    }: {
      projectId: string;
      pipelineV3Config: any;
    }) => {
      return await CommunicationService.updatePipelineV3Config(projectId, pipelineV3Config);
    },
  });
};

export const useNodeSummary = ({
  projectId,
  filters,
}: {
  projectId: string;
  filters: PipelineNodeFilters;
}) => {
  return useQuery<PipelineV3HumanNodesSummaryResponse | undefined>({
    ...queryFactory.projectGroup.nodeSummary({ projectId, filters }),
    staleTime: 1000 * 60 * 5,
  });
};

export const usePendingNodeSummary = ({ projectId }: { projectId: string }) => {
  return useQuery<PipelineV3PendingNodesResponse | undefined>({
    ...queryFactory.projectGroup.pendingNodeSummary({ projectId }),
  });
};

export const useTranslateTask = (task: any) => {
  return useQuery({
    ...queryFactory.tasks.translateTask({ task }),
  });
};

export function useGetAttemptsByReviewLevel(params: IGetByReviewLevel) {
  return useQuery<{ [key: string]: TaskAttemptInfo[] }>({
    ...queryFactory.projectGroup.attemptByReviewLevel(params),
  });
}

export const useGetRecentProjectAttempts = ({
  projectId,
  numAttempts,
  reviewLevel,
}: {
  projectId: string;
  numAttempts: number;
  reviewLevel: ReviewLevel;
}) => {
  return useQuery({
    ...queryFactory.qcSpecDoc.getRecentProjectAttempts({ projectId, numAttempts, reviewLevel }),
  });
};

export const useSyncOffPlatformData = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...queryFactory.archieOffPlatformData.syncOffPlatformData({ projectId, customData: {} }),
    mutationFn: (customData: any) =>
      CommunicationService.syncOffPlatformData(projectId, customData),
    onSuccess: () => {
      // Invalidate schema and spec doc queries
      queryClient.invalidateQueries({
        queryKey: [...queryFactory.qcSpecDoc.archieSpecDoc, projectId],
      });
    },
  });
};

export const useGetGQADataForAttempts = ({
  projectId,
  attemptIds,
}: {
  projectId: string;
  attemptIds: string[];
}) => {
  return useQuery({
    ...queryFactory.qcSpecDoc.getGQADataForAttempts({ projectId, attemptIds }),
  });
};

export const useGetArchieQCSpecDoc = ({ projectId }: { projectId: string }) => {
  const queryResult = useQuery({
    ...queryFactory.qcSpecDoc.getArchieQCSpecDoc({ projectId }),
  });
  return {
    ...queryResult,
    refetch: queryResult.refetch,
  };
};

export const useGetLiveArchieQCSpecDoc = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.qcSpecDoc.getLiveArchieQCSpecDoc({ projectId }),
  });
};

export const useValidateArchieTaskAttempts = (projectId: string, taskAttemptIds?: string[]) => {
  return useMutation({
    mutationFn: () => CommunicationService.validateArchieTaskAttempts(projectId, taskAttemptIds),
    onSuccess: () => {
      const queryKey = [...queryFactory.qcSpecDoc.archieSpecDoc, projectId];
      queryClient.invalidateQueries({ queryKey });
      queryClient.refetchQueries({ queryKey });
    },
  });
};

export const useGetAllPublishedArchieQCSpecDocs = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.qcSpecDoc.getAllPublishedArchieQCSpecDocs({ projectId }),
  });
};

export const useCreateArchieQCSpecDoc = ({ projectId }: { projectId: string }) => {
  return useMutation({
    mutationFn: () => CommunicationService.createArchieQCSpecDoc(projectId),
    onSuccess: () => {
      const queryKey = [...queryFactory.qcSpecDoc.archieSpecDoc, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useUpdateArchieQCSpecDoc = ({ projectId }: { projectId: string }) => {
  return useMutation({
    mutationFn: ({ requirements }: { requirements: any }) =>
      CommunicationService.updateArchieQCSpecDoc(projectId, requirements),
    onSuccess: () => {
      const queryKey = [...queryFactory.qcSpecDoc.archieSpecDoc, projectId];
      queryClient.invalidateQueries({ queryKey });
      queryClient.refetchQueries({ queryKey });
    },
  });
};

export const usePublishArchieQCSpecDoc = ({
  projectId,
  publishNotes,
}: {
  projectId: string;
  publishNotes: string;
}) => {
  return useMutation({
    mutationFn: () => CommunicationService.publishArchieQCSpecDoc(projectId, publishNotes),
    onSuccess: () => {
      const queryKey = [...queryFactory.qcSpecDoc.archieSpecDoc, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useRevertToPublishedVersion = ({
  projectId,
  publishedVersion,
}: {
  projectId: string;
  publishedVersion: number | undefined;
}) => {
  return useMutation({
    mutationFn: () => {
      if (publishedVersion === undefined) {
        throw new Error('Published version is required to revert');
      }
      return CommunicationService.revertToPublishedVersion(projectId, publishedVersion);
    },
    onSuccess: () => {
      const queryKey = [...queryFactory.qcSpecDoc.archieSpecDoc, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

const ASYNC_HANDLER_RETURNED_MESSAGE = 'Async handler returned';
export const useGetArchieQCEndpointResponse = ({ projectId }: { projectId: string }) => {
  const launchTaskIdHandleRef = useRef<string | undefined>(undefined);

  return useMutation({
    mutationFn: async ({
      attemptIds,
      requirements,
      specDocVersion,
      filterOutAttemptIdsWithoutQCResults,
      saveAsGroundTruthMetrics = false,
      customData,
    }: {
      attemptIds: string[];
      requirements: ArchieQCRequirementSpecDocInput[] | null;
      specDocVersion: number;
      filterOutAttemptIdsWithoutQCResults: boolean;
      saveAsGroundTruthMetrics?: boolean;
      customData?: CustomData;
    }) => {
      const response = await CommunicationService.getArchieQCEndpointResponse(
        projectId,
        attemptIds,
        requirements,
        specDocVersion,
        launchTaskIdHandleRef.current,
        filterOutAttemptIdsWithoutQCResults,
        saveAsGroundTruthMetrics,
        customData,
      );

      if (response?.launchTaskIdHandle) {
        launchTaskIdHandleRef.current = response.launchTaskIdHandle;
        throw new Error(ASYNC_HANDLER_RETURNED_MESSAGE, {
          cause: { launchTaskIdHandle: response.launchTaskIdHandle },
        });
      } else {
        launchTaskIdHandleRef.current = undefined;
        return response;
      }
    },
    retry: (failureCount: number, error: any) => {
      if (error?.message === ASYNC_HANDLER_RETURNED_MESSAGE && failureCount < 30) {
        return true;
      }
      return false;
    },
    onSuccess: () => {
      const queryKey = [...queryFactory.qcEndpointResponse.all, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const usePersistOffPlatformResults = ({ projectId }: { projectId: string }) => {
  return useMutation({
    mutationFn: (archieQCEvalOutput: ArchieQCEnrichedEvalOutput) =>
      CommunicationService.persistOffPlatformResults(projectId, archieQCEvalOutput),
  });
};

export const useRunArchieAssistantEndpoint = ({ projectId }: { projectId: string }) => {
  const launchTaskIdHandleRef = useRef<string | undefined>(undefined);

  return useMutation({
    mutationFn: async ({
      action,
      prompt,
      spec_doc_url,
      instruction_doc_url,
      taxonomy,
      clarifications,
    }: {
      action: ArchieAssistantActions;
      prompt?: string;
      spec_doc_url?: string;
      instruction_doc_url?: string;
      taxonomy?: string;
      clarifications?: ClarificationQA[];
    }) => {
      const response = await CommunicationService.runArchieAssistantEndpoint(
        projectId,
        action,
        prompt,
        spec_doc_url,
        instruction_doc_url,
        taxonomy,
        clarifications,
        launchTaskIdHandleRef.current,
      );

      if (response?.launchTaskIdHandle) {
        launchTaskIdHandleRef.current = response.launchTaskIdHandle;
        throw new Error(ASYNC_HANDLER_RETURNED_MESSAGE, {
          cause: { launchTaskIdHandle: response.launchTaskIdHandle },
        });
      } else {
        launchTaskIdHandleRef.current = undefined;
        return response;
      }
    },
    retry: (failureCount: number, error: any) => {
      if (error?.message === ASYNC_HANDLER_RETURNED_MESSAGE && failureCount < 30) {
        return true;
      }
      return false;
    },
    onSuccess: () => {
      const queryKey = [...queryFactory.archieAssistantEndpointResponse.all, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useGetArchieQCTaxonomyMapping = ({
  projectId,
  attemptId,
  includeValueReference = false,
  includeDictWithValue = false,
}: {
  projectId: string;
  attemptId: string;
  includeValueReference?: boolean;
  includeDictWithValue?: boolean;
}) => {
  return useQuery({
    ...queryFactory.qcSpecDoc.getArchieQCTaxonomyMapping({
      projectId,
      attemptId,
      includeValueReference,
      includeDictWithValue,
    }),
    enabled: attemptId !== '',
  });
};

export const useGetArchieQCAgents = () => {
  return useQuery({
    ...queryFactory.qcSpecDoc.getArchieQCAgents(),
  });
};

export const useGetCalculatedGroundTruthMetrics = ({
  projectId,
  groundTruthVersion,
  specDocVersion,
}: {
  projectId: string;
  groundTruthVersion: number;
  specDocVersion: number;
}) => {
  return useQuery({
    ...queryFactory.qcSpecDoc.getCalculatedGroundTruthMetrics({
      projectId,
      groundTruthVersion,
      specDocVersion,
    }),
  });
};

// Contributor Copilot
export const useGetCopilotConfiguration = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.copilot.getCopilotConfiguration({ projectId }),
  });
};

export const useGetCopilotConfigurationAggregateStats = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.copilot.getCopilotConfigurationAggregateStats({ projectId }),
  });
};

export const useGetCopilotConfigurationAllStats = ({
  checkId,
  timeFilter,
}: {
  checkId: string;
  timeFilter: CopilotStatsTimeFilter;
}) => {
  return useQuery({
    ...queryFactory.copilot.getCopilotConfigurationAllStats({ checkId, timeFilter }),
  });
};

export const useGetCopilotCheckLastKRuns = ({
  checkId,
  filter,
  k,
}: {
  checkId: string;
  filter: CopilotLastKRunsFilter;
  k: number;
}) => {
  return useQuery({
    ...queryFactory.copilot.getCopilotCheckLastKRuns({ checkId, filter, k }),
  });
};

export const useUpdateCopilotConfigurationCheck = ({ projectId }: { projectId: string }) => {
  return useMutation({
    mutationFn: ({ checkId, copilotCheck }: { checkId: string; copilotCheck: CopilotCheck }) =>
      CommunicationService.updateCopilotConfigurationCheck(projectId, checkId, copilotCheck),
    onSuccess: (_, variables) => {
      const queryKey = [...queryFactory.copilot.configuration, projectId];
      queryClient.invalidateQueries({ queryKey });

      const copilotCheckQueryKey = [...queryFactory.copilot.check, variables.checkId];
      queryClient.invalidateQueries({ queryKey: copilotCheckQueryKey });
    },
  });
};

export const useCreateCopilotConfigurationCheck = ({ projectId }: { projectId: string }) => {
  return useMutation({
    mutationFn: (check: CopilotCheck) =>
      CommunicationService.createCopilotConfigurationCheck(
        projectId,
        check.name,
        check.enabled,
        check.checkType,
        check.requirement,
        check.severity,
        check.timeoutSeconds,
      ),
    onSuccess: () => {
      const queryKey = [...queryFactory.copilot.configuration, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useDeleteCopilotConfigurationCheck = ({ projectId }: { projectId: string }) => {
  return useMutation({
    mutationFn: (checkId: string) =>
      CommunicationService.deleteCopilotConfigurationCheck(projectId, checkId),
    onSuccess: () => {
      const queryKey = [...queryFactory.copilot.configuration, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useImportCopilotConfigurationCheck = ({ projectId }: { projectId: string }) => {
  return useMutation({
    mutationFn: ({
      sourceProjectId,
      sourceCheckId,
    }: {
      sourceProjectId: string;
      sourceCheckId: string;
    }) =>
      CommunicationService.importCopilotConfigurationCheck({
        targetProjectId: projectId,
        sourceProjectId,
        sourceCheckId,
      }),
    onSuccess: () => {
      const queryKey = [...queryFactory.copilot.configuration, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useGetCopilotAgentDatabase = () => {
  return useQuery({
    ...queryFactory.copilot.getCopilotAgentDatabase(),
  });
};

export const useGetCopilotTaxonomy = () => {
  return useMutation({
    mutationFn: (attemptId: string) => CommunicationService.getCopilotTaxonomy(attemptId),
  });
};

export const useLaunchCopilotHillclimbingTasksForCheckRunIds = () => {
  return useMutation({
    mutationFn: ({
      projectId,
      configurationId,
      checkId,
      checkRunIds,
      requirementOverride,
      cacheMode,
    }: {
      projectId: string;
      configurationId: string;
      checkId: string;
      checkRunIds: string[];
      requirementOverride?: any;
      cacheMode?: ArchieCopilotCacheMode;
    }) =>
      CommunicationService.launchCopilotHillclimbingTasksForCheckRunIds({
        projectId,
        configurationId,
        checkId,
        checkRunIds,
        requirementOverride,
        cacheMode,
      }),
  });
};

export const usePollCopilotHillclimbingTasks = () => {
  return useMutation({
    mutationFn: ({
      projectId,
      checkId,
      tasks,
    }: {
      projectId: string;
      checkId: string;
      tasks: { checkRunId: string; taskId: string }[];
    }) =>
      CommunicationService.pollCopilotHillclimbingTasks({
        projectId,
        checkId,
        tasks,
      }),
  });
};

export const useGetCopilotHillclimbingQueryParamsForCheckRunIds = ({
  configurationId,
  checkId,
  checkRunIds,
}: {
  configurationId: string;
  checkId: string;
  checkRunIds: string[];
}) => {
  return useQuery({
    ...queryFactory.copilot.getCopilotHillclimbingQueryParamsForCheckRunIds({
      configurationId,
      checkId,
      checkRunIds,
    }),
    staleTime: Infinity,
    enabled: checkRunIds.length > 0,
  });
};

export const useGetCopilotHillclimbingData = ({
  projectId,
  checkId,
}: {
  projectId: string;
  checkId: string;
}) => {
  return useQuery({
    ...queryFactory.copilot.getCopilotHillclimbingData({
      projectId,
      checkId,
    }),
  });
};

export const useUpdateCopilotHillclimbingExpectedResults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      checkId,
      updates,
    }: {
      projectId: string;
      checkId: string;
      updates: ExpectedCopilotResult[];
    }) =>
      CommunicationService.updateCopilotHillclimbingExpectedResults({
        projectId,
        checkId,
        updates,
      }),
    onSuccess: (_, { projectId, checkId }) => {
      return queryClient.invalidateQueries({
        queryKey: queryFactory.copilot.getCopilotHillclimbingData({ projectId, checkId }).queryKey,
      });
    },
  });
};

export const useInitializeCopilotHillclimbingDataForAttempts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      configurationId,
      checkId,
      attemptIds,
      prepopulatedExpectedResults,
    }: {
      projectId: string;
      configurationId: string;
      checkId: string;
      attemptIds: string[];
      prepopulatedExpectedResults: Record<string, CopilotResult>;
    }) =>
      CommunicationService.initializeCopilotHillclimbingDataForAttempts({
        projectId,
        configurationId,
        checkId,
        attemptIds,
        prepopulatedExpectedResults,
      }),
    onSuccess: (_, { projectId, checkId }) => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.copilot.getCopilotHillclimbingData({ projectId, checkId }).queryKey,
      });
    },
  });
};

export const useUploadHillclimbingCsv = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      checkId,
      csvContent,
    }: {
      projectId: string;
      checkId: string;
      csvContent: string;
    }) =>
      CommunicationService.uploadHillclimbingCsv({
        projectId,
        checkId,
        csvContent,
      }),
    onSuccess: (_, { projectId, checkId }) => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.copilot.getCopilotHillclimbingData({ projectId, checkId }).queryKey,
      });
    },
  });
};

export const useDeleteCopilotAttemptsByAttemptId = (configurationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      checkId,
      attemptIds,
    }: {
      projectId: string;
      checkId: string;
      attemptIds: string[];
    }) =>
      CommunicationService.deleteCopilotAttemptsByAttemptId({
        projectId,
        checkId,
        attemptIds,
      }),
    onSuccess: (_, { projectId, checkId }) => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.copilot.getCopilotHillclimbingData({ projectId, checkId }).queryKey,
      });

      // Invalidate all cached hillclimb query-param batches for this configuration/check.
      // Using a prefix key causes React Query to invalidate any key that starts with this prefix.
      queryClient.invalidateQueries({
        queryKey: [...queryFactory.copilot.hillclimbing, configurationId, checkId],
      });
    },
  });
};

export const useDeleteCheckRunOutcome = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      checkId,
      checkRunId,
    }: {
      projectId: string;
      checkId: string;
      checkRunId: string;
    }) =>
      CommunicationService.deleteCheckRunOutcome({
        projectId,
        checkId,
        checkRunId,
      }),
    onSuccess: (_, { projectId, checkId }) => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.copilot.getCopilotHillclimbingData({ projectId, checkId }).queryKey,
      });
    },
  });
};

export const useGetCopilotCheck = ({
  projectId,
  checkId,
}: {
  projectId: string;
  checkId: string;
}) => {
  return useQuery({
    ...queryFactory.copilot.getCopilotCheck({ projectId, checkId }),
  });
};

export const useToggleCopilotCheckMetricsMode = (projectId: string, checkId: string) => {
  return useMutation({
    mutationFn: (metricsMode: CopilotMetricsMode) =>
      CommunicationService.toggleCopilotCheckMetricsMode({ projectId, checkId, metricsMode }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.copilot.getCopilotCheck({ projectId, checkId }).queryKey,
      });
    },
  });
};

export const useSummarizeAQCDisagreements = ({ projectId }: { projectId: string }) => {
  const launchTaskIdHandleRef = useRef<string | undefined>(undefined);

  return useMutation({
    mutationFn: async (disagreementData: ArchieQCDisagreementData) => {
      const response = await CommunicationService.summarizeAQCDisagreements(
        projectId,
        disagreementData,
        launchTaskIdHandleRef.current,
      );

      if (response?.launchTaskIdHandle) {
        launchTaskIdHandleRef.current = response.launchTaskIdHandle;
        throw new Error(ASYNC_HANDLER_RETURNED_MESSAGE, {
          cause: { launchTaskIdHandle: response.launchTaskIdHandle },
        });
      } else {
        launchTaskIdHandleRef.current = undefined;
        return response;
      }
    },
    retry: (failureCount: number, error: any) => {
      if (error?.message === ASYNC_HANDLER_RETURNED_MESSAGE && failureCount < 15) {
        return true;
      }
      return false;
    },
    onSuccess: () => {
      const queryKey = [...queryFactory.groundTruthDisagreements.all, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useRunArchieBMCreationEndpointResponse = ({ projectId }: { projectId: string }) => {
  return useMutation({
    mutationFn: () => CommunicationService.runArchieBMCreationEndpointResponse(projectId),
    onSuccess: () => {
      const queryKey = [...queryFactory.bmCreationEndpointResponse.all, projectId];
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useGetProjectSquads = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projectSquads.getProjectSquads({ projectId }),
  });
};

export const useGetProjectSquadTaskStats = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projectSquads.getProjectSquadTaskStats({ projectId }),
  });
};

export const useGetSquad = ({ squadId, projectIds }: { squadId: string; projectIds: string[] }) => {
  return useQuery({
    ...queryFactory.projectSquads.getSquad({ squadId, projectIds }),
    enabled: projectIds.length > 0,
  });
};

export const useGetSquadTaskStats = ({
  squadId,
  projectIds,
}: {
  squadId: string;
  projectIds: string[];
}) => {
  return useQuery({
    ...queryFactory.projectSquads.getSquadTaskStats({ squadId, projectIds }),
    enabled: projectIds.length > 0,
  });
};

export const useGetSquadAudits = ({
  squadId,
  projectIds,
}: {
  squadId: string;
  projectIds: string[];
}) => {
  return useQuery({
    ...queryFactory.projectSquads.getSquadAudits({ squadId, projectIds }),
    enabled: projectIds.length > 0,
  });
};

export const useGetSquadHealth = ({
  squadId,
  projectIds,
}: {
  squadId: string;
  projectIds: string[];
}) => {
  return useQuery({
    ...queryFactory.projectSquads.getSquadHealth({ squadId, projectIds }),
    enabled: projectIds.length > 0,
  });
};

export const useGetProjectSquadsHealth = ({
  projectId,
  squadIds,
}: {
  projectId: string;
  squadIds: string[];
}) => {
  return useQuery({
    ...queryFactory.projectSquads.getProjectSquadsHealth({ projectId, squadIds }),
    enabled: squadIds.length > 0,
  });
};

export const useGetSquadPendingTasks = ({
  squadId,
  projectIds,
}: {
  squadId: string;
  projectIds: string[];
}) => {
  return useQuery({
    ...queryFactory.projectSquads.getSquadPendingTasks({ squadId, projectIds }),
    enabled: projectIds.length > 0,
  });
};

export const useCheckAssignableSquads = ({
  sourceProjectId,
  destinationProjectId,
}: {
  sourceProjectId: string;
  destinationProjectId: string;
}) => {
  return useQuery({
    ...queryFactory.projectSquads.checkAssignableSquads({ sourceProjectId, destinationProjectId }),
    enabled: !!sourceProjectId && !!destinationProjectId,
  });
};

export const useGetSquadMemberAttempts = ({
  squadId,
  projectIds,
}: {
  squadId: string;
  projectIds: string[];
}) => {
  return useQuery({
    ...queryFactory.projectSquads.getSquadMemberAttempts({ squadId, projectIds }),
  });
};

export const useGetReviewerSquads = () => {
  return useQuery({
    ...queryFactory.projectSquads.getReviewerSquads(),
  });
};

export const useGetSquadMemberMetadata = ({ squadMembers }: { squadMembers: string[] }) => {
  return useQuery({
    ...queryFactory.projectSquads.getSquadMemberMetadata({ squadMembers }),
  });
};

export const useSearchSquadUsers = ({ search }: { search: string }) => {
  return useQuery({
    ...queryFactory.projectSquads.searchSquadUsers({ search }),
  });
};

export const useLookupSquadUsers = ({
  projectId,
  idsOrEmails,
}: {
  projectId: string;
  idsOrEmails: string[];
}) => {
  return useQuery({
    ...queryFactory.projectSquads.lookupSquadUsers({ projectId, idsOrEmails }),
  });
};

export const useValidateSquadName = ({ squadName }: { squadName: string }) => {
  return useQuery({
    ...queryFactory.projectSquads.validateSquadName({ squadName }),
  });
};

export const useLookupSquadAttempt = ({ attemptId }: { attemptId: string }) => {
  return useQuery({
    ...queryFactory.projectSquads.lookupSquadAttempt({ attemptId }),
    enabled: !!attemptId,
  });
};

export const useGetProjectSquadsConfig = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projectSquadsConfig.getProjectSquadsConfig({ projectId }),
  });
};

export const useGetSquadProjects = ({ squadId }: { squadId: string }) => {
  return useQuery({
    ...queryFactory.projectSquads.getSquadProjects({ squadId }),
  });
};

export const useGetUserProjectOwnership = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projectSquads.getUserProjectOwnership({ projectId }),
  });
};

export const useGetSquadInternalProjectNames = ({ squadIds }: { squadIds: string[] }) => {
  const results = useQueries({
    queries: squadIds.map(squadId => ({
      ...queryFactory.projectSquads.getSquadInternalProjects({ squadId }),
    })),
  });
  return {
    data: squadIds.map((squadId, index) => ({
      squadId,
      projects: results[index].data,
    })),
    isLoading: results.some(result => result.isLoading),
    refetch: () => {
      return Promise.all(results.map(result => result.refetch()));
    },
    isRefetching: results.some(result => result.isRefetching),
  };
};

export const useCreateProjectSquad = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: (result: CreateProjectSquadResult) => void;
}) => {
  return useMutation({
    mutationFn: (params: Omit<CreateProjectSquadParams, 'projectId'>) =>
      CommunicationService.createProjectSquad({ projectId, ...params }),
    onSuccess: (result: CreateProjectSquadResult) => {
      queryClient.invalidateQueries({ queryKey: [...queryFactory.projectSquads.all, projectId] });
      onSuccessCallback(result);
    },
  });
};

export const useLookupSquadResponsibleAdmins = () => {
  return useMutation({
    mutationFn: ({ idsOrEmails }: { idsOrEmails: string[] }) =>
      CommunicationService.lookupSquadResponsibleAdmins(idsOrEmails),
  });
};

export const useRemoveUsersFromExistingSquads = () => {
  return useMutation({
    mutationFn: ({ userIds }: { userIds: string[] }) =>
      CommunicationService.removeUsersFromExistingSquads(userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryFactory.projectSquads.all],
        exact: false,
      });
      toast.success('Successfully removed users from existing squads');
    },
  });
};

export const useValidateBulkCreateSquadInput = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: (result: ValidateBulkCreateSquadInputResult) => void;
}) => {
  return useMutation({
    mutationFn: (rows: SquadCSVUploadDataRow[]) =>
      CommunicationService.validateBulkCreateSquadInput({ projectId, rows }),
    onSuccess: (result: ValidateBulkCreateSquadInputResult) => {
      onSuccessCallback(result);
    },
  });
};

export const useValidateBulkUpdateSquadInput = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: (result: ValidateBulkUpdateSquadInputResult) => void;
}) => {
  return useMutation({
    mutationFn: (rows: SquadCSVUpdateDataRow[]) =>
      CommunicationService.validateBulkUpdateSquadInput({ projectId, rows }),
    onSuccess: (result: ValidateBulkUpdateSquadInputResult) => {
      onSuccessCallback(result);
    },
  });
};

export const useBulkCreateSquads = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: (result: BulkCreateSquadsResult) => void;
}) => {
  return useMutation({
    mutationFn: (rows: ParsedSquadCSVUploadDataRow[]) =>
      CommunicationService.bulkCreateSquads({ projectId, rows }),
    onSuccess: (result: BulkCreateSquadsResult) => {
      queryClient.invalidateQueries({ queryKey: [...queryFactory.projectSquads.all, projectId] });
      onSuccessCallback(result);
    },
  });
};

export const useBulkUpdateSquads = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: () => void;
}) => {
  return useMutation({
    mutationFn: (rows: ParsedSquadCSVUpdateDataRow[]) =>
      CommunicationService.bulkUpdateSquads({ projectId, rows }),
    onSuccess: (result: BulkUpdateSquadsResult) => {
      const numSquads = result.squadIds.length;
      toast.success(`${numSquads} squad${numSquads > 1 ? 's' : ''} updated successfully`);
      queryClient.invalidateQueries({ queryKey: [...queryFactory.projectSquads.all, projectId] });
      onSuccessCallback();
    },
  });
};

export const useUpdateProjectSquad = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: (result: UpdateProjectSquadResult) => void;
}) => {
  return useMutation({
    mutationFn: (params: UpdateProjectSquadParams) =>
      CommunicationService.updateProjectSquad({ ...params }),
    onSuccess: (result: UpdateProjectSquadResult) => {
      queryClient.invalidateQueries({ queryKey: [...queryFactory.projectSquads.all, projectId] });
      onSuccessCallback(result);
    },
  });
};

export const useAssignSquadsToProject = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: () => void;
}) => {
  return useMutation({
    mutationFn: (squadIds: string[]) =>
      CommunicationService.assignSquadsToProject({ projectId, squadIds }),
    onSuccess: (result: AssignSquadsToProjectResult) => {
      const numSquads = result.successSquadIds.length;
      toast.success(
        `Assigned ${numSquads || 0} squad${numSquads !== 1 ? 's' : ''} to this project`,
      );
      queryClient.invalidateQueries({ queryKey: [...queryFactory.projectSquads.all, projectId] });
      onSuccessCallback();
    },
  });
};

export const useRerunQueue = ({
  onSuccessCallback,
}: {
  onSuccessCallback: (newStatus: UserLevelHealthStatus) => void;
}) => {
  return useMutation({
    mutationFn: (request: RerunQueueAndGetHealthRequest) =>
      CommunicationService.rerunQueue(request),
    onSuccess: (newStatus: UserLevelHealthStatus) => onSuccessCallback(newStatus),
  });
};

export const useUpdateProjectSquadsConfig = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: () => void;
}) => {
  return useMutation({
    mutationFn: ({ squadsConfig }: { squadsConfig: SquadsConfig }) =>
      CommunicationService.updateProjectSquadsConfig(projectId, squadsConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.projectSquadsConfig.getProjectSquadsConfig({ projectId }).queryKey,
      });
      toast.success('Channel updated successfully');
      onSuccessCallback();
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.projectSquadsConfig.getProjectSquadsConfig({ projectId }).queryKey,
      });
    },
  });
};

export const useCreateSquadReviewersCommunityChannel = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: () => void;
}) => {
  return useMutation({
    mutationFn: () => CommunicationService.createSquadReviewersCommunityChannel(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.projectSquadsConfig.getProjectSquadsConfig({ projectId }).queryKey,
      });
      toast.success('Channel created and linked successfully');
      onSuccessCallback();
    },
  });
};

export const useArchiveProjectSquad = ({
  projectId,
  onSuccessCallback,
}: {
  projectId: string;
  onSuccessCallback: (result: ArchiveProjectSquadResult) => void;
}) => {
  return useMutation({
    mutationFn: ({ squadId, reason, notes }: { squadId: string; reason: string; notes: string }) =>
      CommunicationService.archiveProjectSquad(projectId, squadId, reason, notes),
    onSuccess: (result: ArchiveProjectSquadResult) => {
      if (!result.error) {
        queryClient.invalidateQueries({ queryKey: [...queryFactory.projectSquads.all, projectId] });
      }
      onSuccessCallback(result);
    },
  });
};

export const useValidateUnassignSquadFromProject = ({
  projectId,
  squadIds,
}: {
  projectId: string;
  squadIds: string[];
}) => {
  return useQuery({
    ...queryFactory.projectSquads.validateUnassignSquadFromProject({ projectId, squadIds }),
  });
};

export const useUnassignSquadsFromProject = ({
  projectId,
  squadIds,
  onSuccessCallback,
}: {
  projectId: string;
  squadIds: string[];
  onSuccessCallback: () => void;
}) => {
  return useMutation({
    mutationFn: () => CommunicationService.unassignSquadsFromProject(projectId, squadIds),
    onSuccess: () => {
      toast.success(`Unassigned ${squadIds.length} squads from this project`);
      queryClient.invalidateQueries({ queryKey: [...queryFactory.projectSquads.all, projectId] });
      squadIds.forEach(squadId => {
        queryClient.invalidateQueries({
          queryKey: [...queryFactory.projectSquads.all, squadId, 'internalProjects'],
        });
      });
      onSuccessCallback();
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: [...queryFactory.projectSquads.all, projectId] });
      squadIds.forEach(squadId => {
        queryClient.invalidateQueries({
          queryKey: [...queryFactory.projectSquads.all, squadId, 'projects'],
        });
      });
    },
  });
};

export function useGetChatBulkAudits({ projectId }: { projectId: string }) {
  return useQuery({
    ...queryFactory.audits.chatBulkAudits({ projectId }),
  });
}

export function invalidateUserChatBulkAuditsQuery(projectId: string, userId: string) {
  queryClient.invalidateQueries({
    queryKey: queryFactory.audits.chatBulkAuditsForUser({ projectId, userId }).queryKey,
  });
}

export function useGetChatBulkAuditsForUser({
  projectId,
  userId,
}: {
  projectId: string;
  userId: string;
}) {
  return useQuery({
    ...queryFactory.audits.chatBulkAuditsForUser({ projectId, userId }),
  });
}

export const useCreateChatBulkAuditBatch = ({
  projectId,
  auditName,
  shouldClaim = false,
}: {
  projectId: string;
  auditName: string;
  shouldClaim?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (objectIds: string) =>
      CommunicationService.createChatBulkAuditBatch(projectId, objectIds, auditName, shouldClaim),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audits', projectId] });
    },
  });
};

export const useChatBulkAuditFilteredTasks = ({
  projectId,
  filters,
  selectedFilter,
}: {
  projectId: string;
  filters: CustomTaskFilters[];
  selectedFilter: string;
}) => {
  filters = filters.filter(filter => filter.secondary && filter.tertiary);
  return useMutation({
    mutationFn: async (sampleNumber: number | undefined) =>
      CommunicationService.chatBulkAuditFilteredTasks({
        projectId,
        filters,
        sampleNumber,
        selectedFilter,
      }),
  });
};

export const useChatBulkAuditObjectIdsWithAttempts = () => {
  return useMutation({
    mutationFn: (objectIds: string[]) =>
      CommunicationService.chatBulkAuditObjectIdsWithAttempts(objectIds),
  });
};

export const useGetAttemptAuditScores = ({ attemptAuditId }: { attemptAuditId: string }) => {
  return useQuery({
    ...queryFactory.audits.getAttemptAuditScores({ attemptAuditId }),
  });
};

export const useGetProjectReviewLevels = ({ projectId }: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projects.reviewLevels({ projectId }),
  });
};

export const useGetLinkedProjectOptions = (projectIds: string[]) => {
  return useQuery({
    ...queryFactory.projects.linkedProjects(projectIds),
  });
};

export function useProjectBatches(params: {
  projectId: string;
  search?: any;
  status?: TaskBatchStatus;
}) {
  return useQuery<BatchResults>({
    ...queryFactory.projectGroup.batches(params),
  });
}

export const useGetComponentValidations = ({ attemptAuditId }: { attemptAuditId: string }) => {
  return useQuery({
    ...queryFactory.componentValidations.getComponentValidations({ attemptAuditId }),
  });
};

export const useUpdateComponentValidations = ({
  attemptAuditId,
  auditRubricId,
}: {
  attemptAuditId: string;
  auditRubricId?: string;
}) => {
  return useMutation({
    mutationFn: async ({
      updatedFields,
    }: {
      updatedFields: (Partial<IQualityValidationComponentJson> & {
        attemptAuditScoreId: string;
      })[];
    }) => {
      if (!auditRubricId) {
        throw new Error('Cannot update component validations without auditRubricId');
      }
      return CommunicationService.updateComponentValidations({
        updatedFields,
        attemptAuditId,
        auditRubricId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.componentValidations.getComponentValidations({
          attemptAuditId,
        }).queryKey,
      });
    },
  });
};

export const useUpdateQualitySignalValidations = ({
  projectId,
  changes,
  dateRange,
}: {
  projectId: string;
  changes: Record<string, Partial<IQualityValidationJson>>;
  dateRange: Date[];
}) => {
  const queryClient = useQueryClient();

  const mergeValidationChangesByAttemptAuditId = (
    baseChanges: Record<string, Partial<IQualityValidationJson>>,
    overrideChanges: Record<string, Partial<IQualityValidationJson>>,
  ): Record<string, Partial<IQualityValidationJson>> => {
    const merged: Record<string, Partial<IQualityValidationJson>> = {};

    const keys = new Set<string>([
      ...Object.keys(baseChanges ?? {}),
      ...Object.keys(overrideChanges ?? {}),
    ]);

    for (const key of keys) {
      merged[key] = {
        ...(baseChanges?.[key] ?? {}),
        ...(overrideChanges?.[key] ?? {}),
      };
    }

    return merged;
  };

  return useMutation({
    mutationFn: (overrideChanges: Record<string, Partial<IQualityValidationJson>> = {}) =>
      CommunicationService.updateQualitySignalValidations({
        changes: mergeValidationChangesByAttemptAuditId(changes, overrideChanges),
      }),
    onMutate: (overrideChanges: Record<string, Partial<IQualityValidationJson>> = {}) => {
      const mergedChanges = mergeValidationChangesByAttemptAuditId(changes, overrideChanges);
      return optimisticUpdateProjectDataProviderQuery(
        projectId,
        queryClient,
        ['projectQCValidations'],
        dateRange,
        (oldConfig: Record<string, IQualityValidationJson> = {}) => {
          const producedChanges = produce(oldConfig, (draftConfig: any) => {
            if (!draftConfig.projectQCValidations) {
              draftConfig.projectQCValidations = [];
            }
            Object.entries(mergedChanges).forEach(([key, val]) => {
              const oldValue =
                draftConfig.projectQCValidations.find(
                  (qValidation: IQualityValidationJson) => qValidation.attemptAuditId === key,
                ) ?? {};
              const indexOfKey = draftConfig.projectQCValidations.findIndex(
                (val: IQualityValidationJson) => val.attemptAuditId === key,
              );
              if (indexOfKey >= 0) {
                draftConfig.projectQCValidations[indexOfKey] = { ...oldValue, ...val };
              } else {
                draftConfig.projectQCValidations.push({ ...oldValue, ...val });
              }
            });
            return draftConfig;
          });
          return producedChanges;
        },
      );
    },
    onSuccess: () => {
      invalidateProjectDataProviderQuery(projectId, queryClient, dateRange, [
        'projectQCValidations',
      ]);
      invalidateProjectDataProviderQuery(projectId, queryClient, dateRange, [
        'projectQCValidationsGoldenFlow',
      ]);
      invalidateProjectDataProviderQuery(projectId, queryClient, dateRange, ['projectQCAverage']);
    },
    onError(error, variables, previousState) {
      setProjectDataProviderQueryData(
        queryClient,
        projectId,
        () => previousState,
        ['projectQCValidations'],
        dateRange,
      );
    },
  });
};

export const useCreateOrUpdateQualitySignalVerifications = ({
  projectId,
  changes,
  dateRange,
}: {
  projectId: string;
  changes: Record<string, QualitySignalVerification>;
  dateRange: Date[];
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      CommunicationService.createOrUpdateQualitySignalVerification({
        changes,
      }),
    onMutate: () => {
      return optimisticUpdateProjectDataProviderQuery(
        projectId,
        queryClient,
        ['projectQualitySignalVerifications'],
        dateRange,
        (oldConfig: QualitySignalVerificationByQualitySignalId = {}) => {
          const parsedChanges = Object.entries(changes).reduce((acc, [key, val]) => {
            const oldData: any = oldConfig?.projectQualitySignalVerifications?.[key] ?? {};
            acc[key] = { ...oldData, ...val };
            return acc;
          }, {} as any);
          const producedChanges = produce(oldConfig, draftConfig => {
            if (!draftConfig.projectQualitySignalVerifications) {
              draftConfig.projectQualitySignalVerifications = {};
            }
            draftConfig.projectQualitySignalVerifications = {
              ...draftConfig.projectQualitySignalVerifications,
              ...parsedChanges,
            };
            return draftConfig;
          });
          return producedChanges;
        },
      );
    },
    onSuccess: () => {
      invalidateProjectDataProviderQuery(projectId, queryClient, dateRange, [
        'projectQualitySignalVerifications',
      ]);
      invalidateProjectDataProviderQuery(projectId, queryClient, dateRange, ['projectGQAAverage']);
    },
    onError(error, variables, previousState) {
      setProjectDataProviderQueryData(
        queryClient,
        projectId,
        () => previousState,
        ['projectQualitySignalVerifications'],
        dateRange,
      );
    },
  });
};

export const useCreateProject = (requestOpts: GenAIProjectSetupRequest) => {
  return useMutation({
    mutationFn: () => CommunicationService.createProject(requestOpts),
  });
};

// [SandboxTestProject]
export const useCreateTasks = (requestOpts: { projectId: string; numberOfTasks?: number }) => {
  return useMutation({
    mutationFn: () => CommunicationService.createTasks(requestOpts),
    onSuccess: ({ message }) => {
      toast(message, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
    onError: ({ message }) => {
      toast(message, {
        type: 'error',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

// [SandboxTestProject]
export const useCreateSandboxTestUsers = (requestOpts: {
  projectId: string;
  sandboxUsersCreateConfigs: SandboxUsersCreateConfig[];
}) => {
  return useMutation({
    mutationFn: () => CommunicationService.createSandboxTestUsers(requestOpts),
    onSuccess: ({ message }) => {
      toast(message, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
    onError: ({ message }) => {
      toast(message, {
        type: 'error',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useImportProject = () => {
  return useMutation({
    mutationFn: ({ projectId, taskType }: { projectId: string; taskType?: TaskType }) =>
      CommunicationService.importProject(projectId, taskType),
    onError: (error: any, variables, context: any) => {
      const userMessage =
        error.userMessage ??
        'An error occurred. If the problem persists, please Slack @genai-ops-app-oncall in #genai-ops-hub.';
      const serverMessage = error.serverMessage ?? error.response?.message;
      const clientMessage = error.clientMessage ?? error.toString();
      const logId = uuid.v4();
      if (
        error.status === 500 &&
        serverMessage.includes('No qualification lists found for the project.')
      ) {
        noop();
      } else {
        logger.error('GenAI Ops Hub mutation error', {
          logId,
          statusCode: error.statusCode ?? 'Unknown status code.',
          userMessage,
          serverMessage,
          clientMessage,
          error,
          variables,
          context,
        });
        errorToast(
          logId,
          'mutation',
          userMessage,
          serverMessage,
          clientMessage,
          error.status,
          error.url,
          variables,
          context,
        );
      }
    },
    meta: {
      overrideGlobalErrorToast: true,
      ignoreGlobalErrorLogging: true,
    },
  });
};

export const useProjectTaskers = (projectId: string, query: SpecializedReviewTaskerQuery) => {
  return useQuery<SpecializedReviewTaskersResponse>({
    ...queryFactory.projects.tasker({ projectId, query }),
  });
};

export const useProjectExampleSubtasks = (projectId: string) => {
  return useQuery<SpecializedReviewExampleSubtaskResponse>({
    ...queryFactory.projects.exampleSubtask({ projectId }),
  });
};

export function useGetAttemptsByReviewLevelByDate(params: IGetByReviewLevel) {
  return useQuery<LineChartDataType>({
    ...queryFactory.projectGroup.attemptsByReviewLevelByDate(params),
  });
}

export const useProject = (params: { projectId?: string }) => {
  return useQuery<IProjectJson>({
    ...queryFactory.projects.detail(params),
  });
};

export const useGetProjects = (projectIds: string[]) => {
  return useQuery<ProjectNameProps[]>({
    ...queryFactory.projects.projectName(projectIds),
  });
};

export const useProjectSetupDefaultTemplates = () => {
  return useQuery<Record<string, MultiTurnInteractionConfiguration>>({
    ...queryFactory.templates.projectSetupDefaultTemplates(),
  });
};

// This was created based on corp/components/JsonSchemaConfigs/ConfigMetadata/useConfigMetadata.ts
export const useConfigMetadata = () => {
  return useQuery<IConfigMetadata[]>({
    ...queryFactory.projectConfigs.metadata(),
  });
};

// This was created based on corp/components/JsonSchemaConfigs/useConfigData.ts
export const useConfigData = ({
  configMeta,
  configAttributes,
  configsPaths,
}: {
  configMeta: IConfigMeta;
  configAttributes: ConfigAttributes | null;
  configsPaths?: string;
}) => {
  return useQuery<{ config: Record<string, any>; version: number }>({
    ...queryFactory.projectConfigs.data({
      configMeta,
      configAttributes: configAttributes!,
      configsPaths,
    }),
    enabled: !!configAttributes,
  });
};

// This was created based on corp/components/JsonSchemaConfigs/useConfigData.ts
export const useUpdateConfigData = ({
  configMeta,
  configAttributes,
  lastVersion,
  configJsonSchema,
}: {
  configMeta: IConfigMeta;
  configAttributes: ConfigAttributes;
  lastVersion: number;
  configJsonSchema: any;
}) => {
  return useMutation({
    mutationFn: ({ changes }: { changes: Record<string, any> }) =>
      CommunicationService.updateConfigData({
        configMeta,
        configAttributes,
        lastVersion,
        configJsonSchema,
        changes,
      }),
    onSuccess: () => {
      toast.success('Saved changes to project config!');
      queryClient.invalidateQueries(
        queryFactory.projectConfigs.data({
          configMeta,
          configAttributes: configAttributes!,
        }).queryKey,
      );
    },
  });
};

export const useGetProjectChangelog = (projectId: string) => {
  return useQuery({
    ...queryFactory.projects.projectChangelog({ projectId }),
  });
};

// wrapper around useUpdateConfigData
export const useUpdateProjectConfig = ({ projectId }: { projectId: string }) => {
  const user = useUser();
  const { data: project, isLoading: isLoadingProject } = useProject({ projectId });
  const projectTaskType = useMemo<TaskType | undefined>(() => project?.taskType, [project]);
  const configMeta = useMemo<IConfigMeta>(
    () => ({
      type: ConfigType.Project,
      id: projectId,
      taskType: projectTaskType,
    }),
    [projectId, projectTaskType],
  );
  const configAttributes = useConfigAttributes(configMeta, project, user);
  const { data: configData, isLoading: isLoadingConfigData } = useConfigData({
    configMeta,
    configAttributes,
  });
  const updateConfigMutation = useUpdateConfigData({
    configMeta,
    configAttributes: configAttributes!,
    lastVersion: configData?.version!,
    configJsonSchema,
  });
  return {
    mutation: updateConfigMutation,
    isLoadingMutation: isLoadingProject || isLoadingConfigData,
  };
};

export function useLintersRatesStats({
  projectId,
  dateRange,
}: {
  projectId: string;
  dateRange: Date[];
}) {
  return useQuery<LinterRatesStatsResponse>({
    ...queryFactory.projects.lintersRatesStats({
      projectId,
      dateRange,
    }),
  });
}

type StreamLinterWithAgentHookParams = {
  projectId: string;
  addMessage: (message: Message) => void;
};

type StreamLinterWithAgentHookMutationParams = {
  chatHistory: Message[];
  currentLintRule: LintRule<ScriptLinterCondition>;
};

export function useStreamLinterWithAgent({
  projectId,
  addMessage,
}: StreamLinterWithAgentHookParams) {
  return useMutation({
    mutationFn: async ({
      chatHistory,
      currentLintRule,
    }: StreamLinterWithAgentHookMutationParams) => {
      return CommunicationService.streamLinterWithAgent({
        projectId,
        chatHistory,
        currentLintRule,
        addMessage,
      });
    },
  });
}

export function useTestSingleLinter({ projectId }: { projectId: string }) {
  return useMutation({
    mutationFn: async ({
      attemptIds,
      localLintRule,
      stepId,
      previewMode,
      turnNumber,
      stepType,
      extendedTimeoutEnabled,
    }: {
      attemptIds: AttemptId[];
      localLintRule: LintRule<ScriptLinterCondition>;
      stepId: string;
      previewMode?: boolean;
      turnNumber?: number;
      stepType: string;
      extendedTimeoutEnabled?: boolean;
    }) => {
      return CommunicationService.testSingleLinter({
        projectId,
        attemptIds,
        localLintRule,
        stepId,
        previewMode,
        turnNumber,
        stepType,
        extendedTimeoutEnabled,
      });
    },
  });
}

export function useGetLinterRuns({
  projectId,
  linterName,
}: {
  projectId: string;
  linterName: string;
}) {
  return useQuery<any>({
    ...queryFactory.linter.getLinterRuns({ projectId, linterName }),
  });
}

export function useGetLinterPerformance({
  projectId,
  linterName,
}: {
  projectId: string;
  linterName: string;
}) {
  return useQuery<LinterPerformance[], Error>({
    ...queryFactory.linter.getLinterPerformance({ projectId, linterName }),
  });
}

export function useGetLinterCosts({ projectId, startDate, endDate }: LinterCostsRequest) {
  return useQuery<LinterCostsResponse, Error>({
    ...queryFactory.linter.getLinterCosts({ projectId, startDate, endDate }),
  });
}

export function useL8EscalatedTaskRate({ projectId }: { projectId: string }) {
  return useQuery<L8EscalatedTaskRate>({
    ...queryFactory.projects.l8EscalatedTasksRate({
      projectId,
    }),
  });
}

export function useWorkersDeactivatedForCheating({ projectId }: { projectId: string }) {
  return useQuery<WorkersDeactivatedForCheatingResponse>({
    ...queryFactory.projects.workersDeactivatedForCheating({
      projectId,
    }),
  });
}

export function useTaskersWithStrikeTags({ projectId }: { projectId: string }) {
  return useQuery<TaskersWithStrikeTagsResponse>({
    ...queryFactory.projects.taskersWithStrikeTags({
      projectId,
    }),
  });
}

export function useCheatingFlaggedTasks({ projectId }: { projectId: string }) {
  return useQuery<CheatingFlaggedTasksResponse>({
    ...queryFactory.projects.cheatingFlaggedTasks({
      projectId,
    }),
  });
}

export const useTasksTouchedByConfirmedCheaters = ({ projectId }: { projectId: string }) => {
  return useQuery<TasksTouchedByConfirmedCheatersResponse>({
    ...queryFactory.projects.tasksTouchedByConfirmedCheaters({
      projectId,
    }),
  });
};

export const useSearchProjects = ({
  searchText,
  includeNonGenAIProjects,
}: {
  searchText: string;
  includeNonGenAIProjects?: boolean;
}) => {
  return useQuery<ProjectWithStatus[]>({
    ...queryFactory.projects.search({ searchText, includeNonGenAIProjects }),
  });
};

export const useGetProjectLibraryEntries = (filters?: FilterObject) => {
  return useQuery({
    ...queryFactory.projects.projectLibraryEntry({ filters }),
  });
};

export const useGetProjectLibraryEntriesByIds = (projectIds: string[]) => {
  return useQuery({
    ...queryFactory.projects.projectLibraryEntryById({ projectIds }),
  });
};

export const useGetCourseV2LibraryEntriesById = (
  projectId: string,
  limit?: number,
  page?: number,
  searchString?: string,
  archived?: boolean,
) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2LibraryEntryById({
      projectId,
      limit,
      page,
      searchString,
      archived,
    }),
  });
};

export const useGetQualificationsByCourseName = (name: string, enabled: boolean) => {
  return useQuery({
    ...queryFactory.coursesv2.existingQualificationByCourseName({ name }),
    enabled,
  });
};

export const useGetCourseV2ReadOnlyChatTask = (taskId: string) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2ReadOnlyChatTask({ taskId }),
  });
};

export const useGetCourseV2ReadOnlyChatV2Task = (taskId: string) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2ReadOnlyChatV2Task({ taskId }),
  });
};

export const useGetCourseV2ReadOnlyTaskAttempt = (attemptId: string) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2ReadOnlyTaskAttempt({ attemptId }),
  });
};

export const useFetchCorpJobsByQuery = (props: IQueryCorpJobs) => {
  return useQuery({
    ...queryFactory.jobs.byQuery(props),
  });
};

export const useFetchCorpJobCache = (props: { corpJobId?: string; isJobFinished: boolean }) => {
  const { corpJobId, isJobFinished } = props;

  return useQuery({
    ...queryFactory.jobs.cache({ corpJobId }),
    refetchInterval: 1000 * 10,
    enabled: !!corpJobId && !isJobFinished,
  });
};

export const useRedashQuery = (props: IUseRedashQueryProps) => {
  const { queryUrl, queryParams } = props;

  return useQuery({
    ...queryFactory.redash.runQuery({ queryUrl, queryParams }),
  });
};

export function useGetUserEnableDisableHistory({ userId }: { userId: string }) {
  return useQuery(['user-enable-disable-history', userId], async () => {
    const query = `corp-api/lookup/${userId}/permissioningHistory`;

    return await apiFetch(null, query, {
      method: 'GET',
    });
  });
}

export const useFetchTaskReplications = (projectId: string) => {
  return useQuery({
    ...queryFactory.taskReplications.fetch({ projectId }),
  });
};

export const useIsUserFromQCTeam = () => {
  return useQuery({
    ...queryFactory.users.userFromQCTeam(),
  });
};

export const useGetDisputeCategoryDefinitions = () => {
  return useQuery({
    ...queryFactory.randomConfigs.getDisputeCategoryDefinitions(),
  });
};

export const useRevertAdminAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ actionId }: { actionId: string }) =>
      CommunicationService.revertAdminAction(actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-enable-disable-history'] });
      toast.success('Successfully reverted admin action');
    },
  });
};

export const useQuarantineTaskReplications = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, taskIds }: { projectId: string; taskIds: string[] }) =>
      CommunicationService.quarantineTaskReplications(projectId, taskIds),
    onSuccess: (response: { [key: string]: any }) => {
      queryClient.invalidateQueries({ queryKey: ['taskReplications', response.projectId] });
    },
  });
};

export const useUnquarantineTaskReplications = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, taskIds }: { projectId: string; taskIds: string[] }) =>
      CommunicationService.unquarantineTaskReplications(projectId, taskIds),
    onSuccess: (response: { [key: string]: any }) => {
      queryClient.invalidateQueries({ queryKey: ['taskReplications', response.projectId] });
    },
  });
};

export const useProvisionSphereWorkspaceLinks = () => {
  return useMutation({
    mutationFn: async (params: ProvisionWorkspaceRequest) => {
      return CommunicationService.provisionSphereWorkspaceLinks(params);
    },
  });
};

export const useUpdateProjectDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: GenAIProjectUpdateRequest) =>
      CommunicationService.updateProjectDetails(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryFactory.projects.qmProjects() });
      queryClient.invalidateQueries({ queryKey: ['getProjectLibraryEntriesByIds'] });
      toast('Project updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useTagsById = (tagsIds: string[]) => {
  return useQuery({
    ...queryFactory.projectConfigs.tagsById(tagsIds),
    enabled: !!tagsIds?.length,
    initialData:
      queryClient.getQueryData(queryFactory.projectConfigs.tags(), {
        exact: false,
      }) ?? undefined,
  });
};

export function useGetLLMCosts({ projectId }: { projectId: string }) {
  return useQuery<{ llmCosts: OverallProjectLLMCostData }>({
    ...queryFactory.llmCosts.getLLMCosts({ projectId }),
  });
}

export const useTagsByNameOrId = (searchValue: string) => {
  return useQuery({
    ...queryFactory.projectConfigs.tagsByNameOrId(searchValue),
  });
};

export const useProjectRoles = ({
  projectId,
  filters,
}: {
  projectId: string;
  filters?: Role[];
}) => {
  return useQuery({
    ...queryFactory.projects.roles({ projectId, filters }),
  });
};

export const useUpdateHubstaffProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectHubstaff,
      createdProjectId,
    }: {
      projectHubstaff: string;
      createdProjectId: string;
    }) => CommunicationService.updateHubstaffProject(projectHubstaff, createdProjectId),
    onSuccess: (response: { [key: string]: any }) => {
      queryClient.invalidateQueries({ queryKey: ['hubstaffProjects', response.createdProjectId] });
    },
  });
};

export const useUpdateProjectSLAGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      body,
      diff,
    }: {
      body: UpdateProjectGroupRequest & { projectGroupId: string };
      diff: ProjectGroupDiff;
    }) => CommunicationService.updateProjectSLAGroup(body, diff),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [...queryFactory.projectGroup.slaGroupDataKey(), data?.id],
      });
      toast('SLA Group updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useDisableProject = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      userId,
      automaticallyRemoveProjectRoles,
    }: {
      projectId: string;
      userId: string;
      automaticallyRemoveProjectRoles: boolean;
    }) => CommunicationService.archiveProject(projectId, userId, automaticallyRemoveProjectRoles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryFactory.projects.searches() });
      invalidateProjectRoles(queryClient);
      toast('Project disabled successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useBumpProjectActivity = () => {
  return useMutation({
    mutationFn: async ({ projectId }: { projectId: string }) =>
      CommunicationService.runLookupCommand(projectId, 'bumpProjectActivity'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryFactory.projects.searches() });
      toast(`Project's activity bumped successfully`, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useEnableProject = () => {
  return useMutation({
    mutationFn: async ({ projectId, maxTaskCount }: { projectId: string; maxTaskCount: number }) =>
      CommunicationService.enableProject(projectId, maxTaskCount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryFactory.projects.searches() });
      toast('Project enabled successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const usePauseOrUnpauseProject = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      userEmail,
      userId,
      action,
      automaticallyRemoveProjectRoles,
    }: {
      projectId: string;
      userEmail: string;
      userId: string;
      action: PauseAction;
      automaticallyRemoveProjectRoles: boolean;
    }) =>
      CommunicationService.updateProjectPauseStatus(
        projectId,
        userEmail,
        userId,
        action,
        automaticallyRemoveProjectRoles,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryFactory.projects.searches() });
      toast('Project pause status updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useApproveFinanceApprovalRequest = () => {
  return useMutation({
    mutationFn: async ({
      requestId,
      reviewerUserId,
    }: {
      requestId: string;
      reviewerUserId: string;
    }) => CommunicationService.approveFinanceApprovalRequest(requestId, reviewerUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeApprovalRequests().queryKey,
      });
      toast('Finance approval request approved successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useRejectFinanceApprovalRequest = () => {
  return useMutation({
    mutationFn: async ({
      requestId,
      reviewerUserId,
    }: {
      requestId: string;
      reviewerUserId: string;
    }) => CommunicationService.rejectFinanceApprovalRequest(requestId, reviewerUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeApprovalRequests().queryKey,
      });
      toast('Finance approval request rejected successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useCommentFinanceApprovalRequest = () => {
  return useMutation({
    mutationFn: async ({
      requestId,
      commenterUserId,
      commentString,
    }: {
      requestId: string;
      commenterUserId: string;
      commentString: string;
    }) =>
      CommunicationService.commentFinanceApprovalRequest(requestId, commenterUserId, commentString),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeApprovalRequests().queryKey,
      });
    },
  });
};

export const useCancelFinanceApprovalRequest = () => {
  return useMutation({
    mutationFn: async ({ requestId }: { requestId: string }) =>
      CommunicationService.cancelFinanceApprovalRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.admin.financeApprovalRequests().queryKey,
      });
      toast('Finance approval request cancelled successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useCreateProjectSLAGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: UpdateProjectGroupRequest & {
        endActiveProjectRoles?: boolean;
      },
    ) => CommunicationService.createProjectSLAGroup(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryFactory.projects.slaGroupKey() });
      toast('SLA Group created/updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};
export const useGetSLAGroupByGroupId = (projectGroupId: string) => {
  return useQuery({
    ...queryFactory.projects.slaGroupParentData({ projectGroupId }),
  });
};

export const useGetWorkerTeams = (projectId: string) => {
  return useQuery({
    ...queryFactory.projects.workerTeams({ projectId }),
  });
};

export const useGetAllWorkerTeams = () => {
  return useQuery({
    ...queryFactory.projects.allWorkerTeams(),
  });
};

export const useGetWorkerTeamsDataByProjectId = ({
  projectId,
  enabled,
}: {
  projectId: string;
  enabled: boolean;
}) => {
  return useQuery({
    ...queryFactory.projects.workerTeamsDataByProjectId({ projectId }),
    enabled,
  });
};
export const useAddNewRoles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ newProjectRoles }: { newProjectRoles: ProjectRoleAddRequest }) =>
      CommunicationService.addProjectRoles(newProjectRoles),
    onSuccess: () => {
      invalidateProjectRoles(queryClient);
      toast('Roles added successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};
export const useGetAssignmentsDataByProjectId = ({
  projectId,
  enabled,
}: {
  projectId: string;
  enabled: boolean;
}) => {
  return useQuery({
    ...queryFactory.projects.assignmentsDataByProjectId({ projectId }),
    enabled,
  });
};

export const invalidateProjectRoles = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: queryFactory.projects.projectRoles() });
};

export const useUpdateRoles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      rolesToUpdate,
    }: {
      rolesToUpdate: {
        projectRoleId: string;
        newStartDate: string;
        newEndDate: string;
      }[];
    }) => CommunicationService.updateProjectRoles(rolesToUpdate),
    onSuccess: updatedDate => {
      // workaround to handle the errors that come from a 200 response
      if (updatedDate?.some?.((modifiedOwner: { updatedAt: any }) => modifiedOwner?.updatedAt)) {
        invalidateProjectRoles(queryClient);
        toast('Roles modified successfully', {
          type: 'success',
          autoClose: 2000,
          position: 'top-right',
        });
      } else {
        const error = updatedDate?.[0]?.message || 'Error updating roles';
        toast(error, {
          type: 'error',
          autoClose: 4000,
          position: 'top-right',
        });
      }
    },
  });
};

export const useValidateHasPermission = ({ permission }: { permission: AccessPermission }) => {
  return useQuery({
    ...queryFactory.users.validateHasPermission({ permission }),
  });
};

export const useLookupScaliens = ({ email }: { email: string }) => {
  return useQuery({
    ...queryFactory.users.lookupScaliens({ email }),
  });
};

export const useGetUserEmailsByIds = ({ userIds }: { userIds: string[] }) => {
  return useQuery({
    ...queryFactory.users.fetchUserEmailsByIds({ userIds }),
  });
};

export const useGetWorkerSkillTaskData = ({
  workerId,
  workerSkillId,
}: IFetchWorkerSkillTaskDataParams) => {
  return useQuery({
    ...queryFactory.users.fetchWorkerSkillTaskData({ workerId, workerSkillId }),
  });
};

export const useGetWorkerSkillEntriesByWorkerId = ({
  workerId,
  filter,
}: IFetchSkillEntriesParams) => {
  return useQuery({
    ...queryFactory.users.fetchSkillEntries({ workerId, filter }),
  });
};

export const useFraudProfile = ({ userId }: { userId: string }) => {
  return useQuery({
    ...queryFactory.users.fraudProfile({ userId }),
  });
};

export const useUserOnboardingState = ({ userId }: { userId: string }) => {
  return useQuery({
    ...queryFactory.users.onboardingState({ userId }),
  });
};

export const useUserFullstory = ({ userId }: { userId: string }) => {
  return useQuery({
    ...queryFactory.users.getFullstory({ userId }),
  });
};

export const useLinkedinVanityName = (userId: string) => {
  return useQuery<{ linkedinVanityName?: string }>({
    ...queryFactory.users.linkedinVanityName({ userId }),
  });
};

export const useFetchUser = ({ userId }: { userId: string }) => {
  return useQuery({
    ...queryFactory.users.fetchUser({ userId }),
  });
};

/** TEAMS */

export const useSearchTeams = ({ searchText }: { searchText: string }) => {
  return useQuery<any[]>({
    ...queryFactory.teams.searchTeams({ searchText }),
  });
};

export const useFetchTeam = (teamId: string) => {
  return useQuery({
    ...queryFactory.teams.fetchTeam({ teamId }),
  });
};

export const useFetchTeamContributors = (teamId: string) => {
  return useQuery({
    ...queryFactory.teams.fetchTeamContributors({ teamId }),
  });
};

export const useFetchTeamAssignmentsWithExtras = (
  teamId: string,
  includeInherited: boolean = false,
) => {
  return useQuery({
    ...queryFactory.teams.fetchTeamAssignmentsWithExtras({ teamId, includeInherited }),
  });
};

export const useFetchTeamAssignmentEvents = (teamId: string) => {
  return useQuery({
    ...queryFactory.teams.fetchTeamAssignmentEvents({ teamId }),
  });
};

export const useFetchTeamContributorEvents = (teamId: string) => {
  return useQuery({
    ...queryFactory.teams.fetchTeamContributorEvents({ teamId }),
  });
};

export const useFetchProjectPrebuildStatus = (params: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projects.prebuildStatus(params),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
    onError: () => {
      queryClient.setQueryData(
        queryFactory.projects.prebuildStatus({
          projectId: params.projectId,
        }).queryKey,
        null,
      );
      return null;
    },
  });
};

export const useFetchProjectMarketplaceStatus = (params: {
  projectId: string;
  query?: Record<string, any>;
}) => {
  return useQuery({
    ...queryFactory.projects.marketplaceStatus(params),
    retry: 1,
    retryOnMount: false,
    onError: () => {
      const queryKey = queryFactory.projects.marketplaceStatus({
        projectId: params.projectId,
      }).queryKey;
      queryClient.removeQueries({
        queryKey,
      });
      return null;
    },
  });
};

export const useFetchProjectMarketplaceStatusByWorkerSkill = (params: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projects.marketplaceStatusByWorkerSkill(params),
    retry: 1,
    retryOnMount: false,
    onError: () => {
      const queryKey = queryFactory.projects.marketplaceStatusByWorkerSkill({
        projectId: params.projectId,
      }).queryKey;
      queryClient.removeQueries({
        queryKey,
      });
      return null;
    },
  });
};

export const useFetchProjectPriorities = (params: {
  workerSkills?: string[];
  projectGroups?: string[];
  layer?: ProjectPriorityLayer;
  alpha?: string;
  statuses?: string[];
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  matchAnyOption?: boolean;
  enabled?: boolean;
}) => {
  return useQuery({
    ...queryFactory.projects.projectPriorities(
      params.workerSkills,
      params.projectGroups,
      params.layer,
      params.alpha,
      params.statuses,
      params.searchTerm,
      params.page,
      params.pageSize,
      params.matchAnyOption,
      params.enabled,
    ),
  });
};

export const useFetchProjectPriority = (params: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projects.projectPriority(params),
  });
};

export const useFetchProjectGroupPriorities = (page: number, pageSize: number) => {
  return useQuery({
    ...queryFactory.projectGroup.projectGroupPriorities({ page, pageSize }),
  });
};

export const useFetchAllProjectPrioritiesForProject = (params: { projectId: string }) => {
  return useQuery({
    ...queryFactory.projects.allProjectPrioritiesForProject(params),
    retry: 1,
  });
};

/**
 * Updates an existing project priority
 */
export const useSaveProjectPriority = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      body,
    }: {
      projectId: string;
      throughputType: ThroughputType;
      body: SaveProjectPriorityRequestBody;
    }) => CommunicationService.saveProjectPriority(projectId, body),
    onSuccess: (_, { projectId, throughputType }) => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.projects.projectPriority({ projectId }).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: queryFactory.projects.projectThroughput({ projectId, throughputType }).queryKey,
      });
    },
  });
};

/**
 * Creates a new project priority
 */
export const useCreateOrUpdateProjectPriority = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      body,
    }: {
      projectId: string;
      body: CreateOrUpdateProjectPriorityRequestDTO;
    }) => CommunicationService.createOrUpdateProjectPriority(projectId, body),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.projects.projectPriority({ projectId }).queryKey,
      });
    },
  });
};

/**
 * Bulk updates project priorities (deadline and executive score)
 */
export const useBulkUpdateProjectPriorities = (options?: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: async ({
      projectId,
      body,
    }: {
      projectId: string;
      body: BulkUpdateProjectPrioritiesRequestDTO;
    }) => CommunicationService.bulkUpdateProjectPriorities(projectId, body),
    onSuccess: (_, { projectId }) => {
      // Invalidate project priorities queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: queryFactory.projects.projectPriority({ projectId }).queryKey,
      });

      // Invalidate all project priorities queries (any parameters)
      queryClient.invalidateQueries({
        queryKey: [...queryFactory.projects.all, 'projectPriorities'],
        exact: false,
      });
      toast.success('Project updated successfully');
      // Call the optional onSuccess callback for manual refresh
      options?.onSuccess?.();
    },
    onError: () => {
      toast.error('Failed to update project priority');
    },
  });
};

/**
 * Reranks a project to a new desired rank
 */
export const useRerankProject = (options?: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: async ({ projectId, body }: { projectId: string; body: ReRankProjectRequestDTO }) =>
      CommunicationService.rerankProject(projectId, body),
    onSuccess: (_, { projectId }) => {
      // Invalidate project priorities queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: queryFactory.projects.projectPriority({ projectId }).queryKey,
      });

      // Invalidate all project priorities queries (any parameters)
      queryClient.invalidateQueries({
        queryKey: [...queryFactory.projects.all, 'projectPriorities'],
        exact: false,
      });
      toast.success('Project reranked successfully');
      // Call the optional onSuccess callback for manual refresh
      options?.onSuccess?.();
    },
    onError: () => {
      toast.error('Failed to rerank project');
    },
  });
};

export const useRerankProjectPreview = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      body,
    }: {
      projectId: string;
      body: BulkUpdateProjectPrioritiesRequestDTO;
    }) => CommunicationService.rerankProjectPreview(projectId, body),
    onError: () => {
      toast.error('Failed to preview project rerank');
    },
  });
};

export const useFetchProjectConfigs = ({
  projectId,
  configAttributes,
}: {
  projectId: string;
  configAttributes: Record<string, string>;
}) => {
  return useQuery({
    ...queryFactory.projects.projectConfigs({ projectId, configAttributes }),
  });
};

export const useGetProjectPipelineV3Config = (projectId: string) => {
  return useQuery({
    ...queryFactory.projects.projectPipelineV3Config({ projectId }),
  });
};

export const useFetchProjectThroughput = (params: {
  projectId: string;
  throughputType: ThroughputType;
  scrollStartDateTime?: Date;
  scrollEndDateTime?: Date;
}) => {
  return useQuery({
    ...queryFactory.projects.projectThroughput(params),
  });
};

export const useFetchProjectRampPlanHistoricData = (params: {
  projectId: string;
  startDate: Date;
  endDate: Date;
}) => {
  return useQuery({
    ...queryFactory.projects.projectHistoricalRampPlanData(params),
  });
};

export const useGetValidExperimentQualifications = () => {
  return useQuery({
    ...queryFactory.projects.validExperimentQualifications(),
  });
};

export const useGetValidExperimentGroupNames = ({ experimentName }: { experimentName: string }) => {
  return useQuery({
    ...queryFactory.projects.validExperimentGroupNames({ experimentName }),
  });
};

export const useGetBranchingQualificationDetails = ({
  qualificationListId,
}: {
  qualificationListId: string;
}) => {
  return useQuery({
    ...queryFactory.projects.branchingQualificationDetails({ qualificationListId }),
  });
};

export const useCreateOrUpdateCourseV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateOrUpdateCourseV2Request) =>
      CommunicationService.createOrUpdateCourseV2(body),
    onSuccess: (response: { [key: string]: any }) => {
      queryClient.invalidateQueries({ queryKey: [response.courseId] });
      queryClient.invalidateQueries(['getCourseV2LibraryEntriesById', response.projectId]);
      toast('Course saved successfully... please publish your course to validate', {
        type: 'success',
        autoClose: 5000,
        position: 'top-right',
      });
    },
  });
};

export const useTestFreeTextGrading = () => {
  return useMutation({
    mutationFn: async ({
      graderConfigs,
      variables,
      type,
    }: {
      graderConfigs: IGraderConfig;
      variables: Record<string, string>;
      type: FreeTextGradingType;
    }) => {
      return CommunicationService.testFreeTextGrading({
        graderConfigs,
        variables,
        type,
      });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to run grading test');
    },
  });
};

export const useCourseGeneration = () => {
  return useMutation({
    mutationFn: async (body: { text: string; courseSections?: CourseSectionV2[] }) =>
      CommunicationService.generateCourse(body),
  });
};

export const useGetCourseV2ById = (courseId: string, enabled: boolean) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2ById({ courseId }),
    enabled,
  });
};

export const useGetCourseById = (courseId: string | undefined) => {
  return useQuery({
    ...queryFactory.courses.courseById({ courseId }),
  });
};

export const useGetCourseV2Templates = () => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2Templates(),
  });
};

export const useGetCourseV2VersionHistory = (courseId: string) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2VersionHistory({ courseId }),
  });
};

export const useRevertToCourseV2Version = (params: PostCourseV2VersionRevertParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => CommunicationService.revertToCourseV2Version(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getCourseV2ById', params.courseId] });
      queryClient.invalidateQueries({ queryKey: ['getCourseV2VersionHistory', params.courseId] });
      toast('Course version reverted successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useProjectRolesByUser = (userId: string) => {
  return useQuery({
    ...queryFactory.projectRoles.projectRolesByUser(userId),
  });
};

export const useQMsByProjects = (projectIds: string[]) => {
  return useQuery({
    ...queryFactory.projectRoles.qmsByProjects(projectIds),
  });
};

export const useFlagsForStaleRoles = (userId: string) => {
  return useQuery({
    ...queryFactory.projectRoles.flagsForStaleRoles(userId),
  });
};

export const useOperationsRoster = () => {
  return useQuery({
    ...queryFactory.projectRoles.operationsRoster(),
  });
};

export const useOperationsRosterForWeeklyRolePopUp = () => {
  return useQuery({
    ...queryFactory.projectRoles.operationsRosterForWeeklyRolePopUp(),
  });
};

export const useUpdateLastStaffingTime = () => {
  return useMutation({
    mutationFn: async (userId: string) => CommunicationService.updateLastStaffingTime(userId),
  });
};

export const useUpdateCourseV2Enablement = () => {
  return useMutation((reqBody: { courseId: string; disabled: boolean }) =>
    CommunicationService.updateCourseV2Enablement(reqBody),
  );
};

export const useUpdateCourseV2Archived = () => {
  return useMutation((reqBody: { courseId: string; archived: boolean; updatedBy: string }) =>
    CommunicationService.updateCourseV2Archived(reqBody),
  );
};

export const usePublishCourseV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateOrUpdateCourseV2Request) =>
      CommunicationService.publishCourseV2(body),
    onSuccess: (response: { [key: string]: any }) => {
      // eslint-disable-next-line eqeqeq
      if (response.success === false) {
        toast(response.message);
      } else {
        queryClient.invalidateQueries(['getCourseV2ById', response.courseId]);
        queryClient.invalidateQueries(['getCourseV2LibraryEntriesById', response.projectId]);
        toast('Course published successfully', {
          type: 'success',
          autoClose: 2000,
          position: 'top-right',
        });
      }
    },
  });
};

export const useGetCourseV2Results = (courseId: string) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2Results({ courseId }),
  });
};

export const useGetAssignedUsers = (courseId: string) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2AssignedUsers({ courseId }),
  });
};

export const useGetCourseV2ProblemBreakdown = (courseId: string) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2ProblemBreakdown({ courseId }),
  });
};

export const useGetCourseV2Stats = (courseId: string, workerId?: string) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2Stats({ courseId, workerId }),
  });
};

interface GetCourseV2UserProgressParams {
  courseId: string;
  workerId?: string;
  isCompleted?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
}

export const useGetCourseV2UserProgress = ({
  courseId,
  workerId,
  isCompleted,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: GetCourseV2UserProgressParams) => {
  return useQuery({
    ...queryFactory.coursesv2.courseV2UserProgress({
      courseId,
      workerId,
      isCompleted,
      page,
      pageSize,
      sortBy,
      sortOrder,
    }),
    enabled: !!courseId,
  });
};

export const useCreateCSATResponses = () => {
  return useMutation({
    mutationFn: async (
      responses: {
        campaignName: CampaignName;
        responder: string;
        question: string;
        score: number;
        comments?: string;
        metadata?: Record<string, any>;
      }[],
    ) => CommunicationService.createCSATResponses(responses),
  });
};

export const useGetCSATResponsesFromLastWeekByCampaignName = (campaignName: string) => {
  return useQuery({
    ...queryFactory.CSAT.getResponsesFromLastWeekByCampaignName({ campaignName }),
  });
};

export const useReportIssue = () => {
  return useMutation({
    mutationFn: async (body: ReportIssueReqBody): Promise<ReportIssueResBody> => {
      return await CommunicationService.reportIssue(body);
    },
  });
};

export const useLookupObject = (objectId: string, enabled: boolean = true) => {
  return useQuery({
    ...queryFactory.lookup.object(objectId),
    enabled,
  });
};

export const useLookupTask = (taskId: string) => {
  return useQuery({
    ...queryFactory.lookup.task(taskId),
  });
};

export const useLookupTaskLite = (taskId: string) => {
  return useQuery({
    ...queryFactory.lookup.taskLite(taskId),
  });
};

export const useLookupTaskAttempt = (attemptId: string) => {
  return useQuery({
    ...queryFactory.lookup.attempt(attemptId),
  });
};

export const useGetTaskAttemptForTaskLookup = (taskId: string) => {
  return useQuery({
    ...queryFactory.taskAttempts.getForTaskLookup(taskId),
  });
};

export const usePipelineV3History = (objectId: string) => {
  return useQuery({
    ...queryFactory.pipelineV3.history(objectId),
  });
};

export const useRunLookupCommand = ({ objectId }: { objectId: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      command: CorpCommand;
      successMessage?: string;
      args?: Record<string, any>;
    }) => {
      const { command, successMessage, args } = params;
      await CommunicationService.runLookupCommand(objectId, command, args);
      return { successMessage };
    },
    onSuccess: ({ successMessage }) => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.lookup.object(objectId).queryKey,
      });

      toast(successMessage, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useFindCustomers = (objectIds: string) => {
  return useQuery({
    ...queryFactory.users.findCustomers({ objectIds }),
  });
};

export const useApproveBillingProposal = () => {
  return useMutation({
    mutationFn: async (params: { billingProposal: IProjectBillingSetup }) => {
      return CommunicationService.approveBillingProposal(params.billingProposal);
    },
  });
};

export const useGetProjectBillingPartition = (projectId: string) => {
  return useQuery({
    ...queryFactory.billing.projectBillingPartitions({ projectId }),
  });
};

export const useGetProjectBillingProposalApprovers = (projectId: string) => {
  return useQuery({
    ...queryFactory.billing.projectBillingProposalApprovers({ projectId }),
  });
};

export const useGetPayPerTaskInfo = (projectId: string) => {
  const result = useQuery({
    ...queryFactory.payPerTask.getPayPerTaskInfo({ projectId }),
  });
  return result;
};
export const useGetProjectPayConfigs = (projectId: string) => {
  const result = useQuery({
    ...queryFactory.projectPayConfigs.getProjectPayConfigs({ projectId }),
  });
  return result;
};

export const useUpdatePayPerTaskInfo = () => {
  return useMutation({
    mutationFn: async (params: {
      projectId: string;
      isPayPerTaskEnabled: boolean;
      isDynamicPricingEnabled: boolean;
      isBasePayRangeEnabled: boolean;
      isAssessmentRateBonusEnabled: boolean;
    }) => {
      return CommunicationService.updatePayPerTaskInfo(
        params.projectId,
        params.isPayPerTaskEnabled,
        params.isDynamicPricingEnabled,
        params.isBasePayRangeEnabled,
        params.isAssessmentRateBonusEnabled,
      );
    },
  });
};

export const useLiteLookup = (searchId: string) => {
  return useQuery(['liteLookup', searchId], () => CommunicationService.getLiteLookup(searchId), {
    enabled: !!searchId,
  });
};

export const useProjectTaskModels = ({
  projectId,
  enabled,
}: {
  projectId: string;
  enabled: boolean;
}) => {
  return useQuery({
    ...queryFactory.projects.taskModels({ projectId }),
    retry: false,
    enabled,
  });
};

export const useThrottleEntryStats = (projectId: string) => {
  return useQuery({
    ...queryFactory.projects.throttleStats({ projectId }),
  });
};

export const useUpsertModelEndpoint = (options?: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: (modelEndpoint: ModelRegistryEndpoint) =>
      modelEndpoint.id
        ? CommunicationService.updateModelEndpoint(modelEndpoint)
        : CommunicationService.addModelEndpoint(modelEndpoint),
    onSuccess: (_, variables) => {
      const action = variables.id ? 'updated' : 'added';
      toast(`Model endpoint ${variables.name} ${action} successfully`, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
      options?.onSuccess?.();
    },
  });
};

export const useGetModelEndpoints = () => {
  return useQuery({
    ...queryFactory.modelRegistry.getModelEndpoints(),
  });
};

export const useUpsertJSONSchema = (options?: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: (schema: DeliveryJSONSchema) =>
      schema.id
        ? CommunicationService.updateJSONSchema(schema)
        : CommunicationService.addJSONSchema(schema),
    onSuccess: (_, schema) => {
      const action = schema.id ? 'updated' : 'added';
      toast(`JSON Schema ${schema.name} ${action} successfully`, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
      options?.onSuccess?.();
    },
  });
};

export const useGetJSONSchemas = () => {
  return useQuery({
    ...queryFactory.schemaRegistry.getJSONSchemas(),
  });
};

export const useAssignJSONSchemaToProject = () => {
  return useMutation({
    mutationFn: async ({ schemaId, projectId }: { schemaId: string; projectId: string }) => {
      return CommunicationService.assignJSONSchemaToProject(schemaId, projectId);
    },
    onSuccess: () => {
      toast('JSON Schema assigned to project successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useGetProjectSchemaId = (projectId: string) => {
  return useQuery<{ schemaId: string | null }>({
    ...queryFactory.schemaRegistry.getProjectSchemaId({ projectId }),
  });
};

export const useGetProjectSchema = (projectId: string) => {
  return useQuery<DeliveryJSONSchemaResponse>({
    ...queryFactory.schemaRegistry.getProjectSchema({ projectId }),
  });
};

export const useGetSchemaById = (schemaId?: string) => {
  return useQuery<DeliveryJSONSchemaResponse>({
    ...queryFactory.schemaRegistry.getSchemaById({ schemaId: schemaId ?? '' }),
    enabled: !!schemaId,
  });
};

export const useValidateSingleJSON = (schemaId: string, json: any, options: any) => {
  return useQuery<{ valid: boolean; errors: any[] }>({
    ...queryFactory.schemaRegistry.validateSingleJSON({ schemaId, json }),
    enabled: options?.enabled,
  });
};

export const useValidateDeliveries = () => {
  return useMutation({
    mutationFn: ({
      customer,
      files,
      useO1ReasoningValidation = false,
    }: {
      customer: string;
      files: File[];
      useO1ReasoningValidation?: boolean;
    }) => CommunicationService.validateDelivery(customer, files, useO1ReasoningValidation),
  });
};

export const useExtractFileInfo = (
  files: File[],
  datestamp: moment.Moment,
  options?: { enabled?: boolean },
) => {
  return useQuery(
    ['extractFileInfo', files, datestamp],
    () => CommunicationService.extractFileInfo(files, datestamp),
    {
      enabled: options?.enabled,
    },
  );
};

export const useSummarizeDeliveries = (options?: {
  onSuccess?: (data: { summaryUrl: string }) => void;
}) => {
  return useMutation<{ summaryUrl: string }, Error, { customer: string; files: File[] }>({
    mutationFn: ({ customer, files }) => CommunicationService.summarizeDeliveries(customer, files),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useSyncKratosSchema = (options?: {
  onSuccess?: (data: { schemaIds: string[] }) => void;
}) => {
  return useMutation<
    { schemaIds: string[] },
    Error,
    { projectIds: string[]; useScaleBucket: boolean }
  >({
    mutationFn: ({ projectIds, useScaleBucket }) =>
      CommunicationService.syncKratosSchema(projectIds, useScaleBucket),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useFixKratosDeliveries = (options?: {
  onSuccess?: (data: { jobId: string }) => void;
}) => {
  return useMutation<
    { jobId: string },
    Error,
    {
      prefix: string;
      filename: string;
      isRecallOnly: boolean;
      isTest: boolean;
      isOverride: boolean;
    }
  >({
    mutationFn: ({ prefix, filename, isRecallOnly, isTest, isOverride }) =>
      CommunicationService.fixKratosDeliveries(prefix, filename, isRecallOnly, isTest, isOverride),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useFixKratosDeliveriesByTaskIds = (options?: {
  onSuccess?: (data: { jobId: string }) => void;
}) => {
  return useMutation<
    { jobId: string },
    Error,
    {
      prefix: string;
      filename: string;
      taskIds: string[];
      isTest: boolean;
    }
  >({
    mutationFn: ({ prefix, filename, taskIds, isTest }) =>
      CommunicationService.fixKratosDeliveriesByTaskIds(prefix, filename, taskIds, isTest),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useRedeliverKratosFiles = (options?: {
  onSuccess?: (data: { jobId: string }) => void;
}) => {
  return useMutation<{ jobId: string }, Error, { filePathsToRedeliver: string[] }>({
    mutationFn: ({ filePathsToRedeliver }) =>
      CommunicationService.redeliverKratosFiles(filePathsToRedeliver),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useUpdateCorpProfile = (userId: string) => {
  return useMutation({
    mutationFn: async (newData: CorpProfileProps) => {
      return CommunicationService.updateCorpProfile(userId, newData);
    },
  });
};

export const useGetQuestionBankByProjectId = (projectId: string) => {
  return useQuery<QuestionBankDTO>({
    ...queryFactory.questionBank.getQuestionBankByProjectId({ projectId }),
  });
};

export const useGetQuestionBanks = () => {
  return useQuery<QuestionBankDTO[]>({
    ...queryFactory.questionBank.getQuestionBanks(),
  });
};

export const useCreateQuestionBank = () => {
  return useMutation<QuestionBankDTO, Error, CreateQuestionBankRequest>({
    mutationFn: async (params: CreateQuestionBankRequest) => {
      return CommunicationService.createQuestionBank(params);
    },
    onSuccess: (newBank: QuestionBankDTO) => {
      toast('Question Bank created successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
      return newBank;
    },
  });
};

export const useUpdateQuestionBank = () => {
  return useMutation<QuestionBankDTO, Error, UpdateQuestionBankRequest>({
    mutationFn: async (params: UpdateQuestionBankRequest) => {
      return CommunicationService.updateQuestionBank(params);
    },
    onSuccess: () => {
      toast('Question Bank updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useArchiveQuestionBank = () => {
  return useMutation<void, Error, { questionBankId: string }>({
    mutationFn: ({ questionBankId }) => CommunicationService.archiveQuestionBank(questionBankId),
    onSuccess: () => {
      toast('Question Bank archived successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useGetQuestionBankQuestions = (questionBankId?: string) => {
  return useQuery<QuestionDTO[]>({
    ...queryFactory.questionBank.getQuestionsByQuestionBankId({
      questionBankId: questionBankId || '',
    }),
    enabled: !!questionBankId,
  });
};

export const useCreateQuestionBankQuestion = (
  setSelectedQuestion?: (selectedQuestion: QuestionDTO) => void,
) => {
  return useMutation<QuestionDTO, Error, CreateQuestionRequest>({
    mutationFn: async (params: CreateQuestionRequest) => {
      return CommunicationService.createQuestionBankQuestion(params);
    },
    onSuccess: data => {
      toast('Question created successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
      setSelectedQuestion?.(data);
    },
  });
};

export const useUpdateQuestionBankQuestion = () => {
  return useMutation<QuestionDTO, Error, UpdateQuestionRequest>({
    mutationFn: async (params: UpdateQuestionRequest) => {
      return CommunicationService.updateQuestionBankQuestion(params);
    },
    onSuccess: () => {
      toast('Question updated successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useArchiveQuestionBankQuestion = () => {
  return useMutation<void, Error, { questionId: string }>({
    mutationFn: ({ questionId }) => CommunicationService.archiveQuestionBankQuestion(questionId),
    onSuccess: () => {
      toast('Question archived successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useGetProjectsByQuestionBankId = (questionBankId: string) => {
  return useQuery<QuestionBankProjectInfo[]>({
    ...queryFactory.questionBank.getProjectsByQuestionBankId({ questionBankId }),
  });
};

export const useAssignProjectsToQuestionBank = () => {
  return useMutation<void, Error, { questionBankId: string; projectIds: string[] }>({
    mutationFn: ({ questionBankId, projectIds }) =>
      CommunicationService.assignProjectsToQuestionBank(questionBankId, projectIds),
    onSuccess: () => {
      toast('Project(s) assigned to Question Bank successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useUnAssignProjectsFromQuestionBank = () => {
  return useMutation<void, Error, { questionBankId: string; projectIds: string[] }>({
    mutationFn: ({ questionBankId, projectIds }) =>
      CommunicationService.unAssignProjectsFromQuestionBank(questionBankId, projectIds),
    onSuccess: () => {
      toast('Project(s) unassigned from Question Bank successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useFetchAllWorkerSkills = (tree: boolean, treeLevels?: number) => {
  return useQuery<WorkerSkillResponse[]>({
    ...queryFactory.workerSkills.getAllWorkerSkills(tree, treeLevels),
  });
};

export const useGetQuestionsForProject = (projectId: string) => {
  return useQuery<QuestionDTO[]>({
    ...queryFactory.questionBank.getQuestionsForProject({ projectId }),
    enabled: !!projectId,
  });
};

export const useAssignProjectsToQuestion = () => {
  return useMutation<void, Error, { questionId: string; projectIds: string[] }>({
    mutationFn: ({ questionId, projectIds }) =>
      CommunicationService.assignProjectsToQuestion(questionId, projectIds),
    onSuccess: () => {
      toast('Project(s) assigned to question successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useUnassignProjectsFromQuestion = () => {
  return useMutation<void, Error, { questionId: string; projectIds: string[] }>({
    mutationFn: ({ questionId, projectIds }) =>
      CommunicationService.unassignProjectsFromQuestion(questionId, projectIds),
    onSuccess: () => {
      toast('Project(s) unassigned from question successfully', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useGetStagingFilesForDelivery = (customer: string, datestamp: string) => {
  return useQuery({
    ...queryFactory.customerUpload.getStagingFilesForDelivery({ customer, datestamp }),
  });
};

export const useGetExpectedFilesForDelivery = (customer: string, datestamp: string) => {
  return useQuery({
    ...queryFactory.customerUpload.getExpectedFilesForDelivery({ customer, datestamp }),
  });
};

export const useUploadStagingFilesToCustomer = () => {
  return useMutation<
    string[],
    Error,
    { customer: string; datestamp: string; filePathsToUpload?: string[] }
  >({
    mutationFn: ({ customer, datestamp, filePathsToUpload }) =>
      CommunicationService.uploadStagingFilesToCustomer(customer, datestamp, filePathsToUpload),
    onSuccess: data => {
      toast(`Successfully uploaded ${data.length} files`, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export interface ToolHealthCheckResponse {
  tool: string;
  query: string;
  status: 'healthy' | 'unhealthy' | 'error';
  responseTime: string;
  timestamp: string;
  result?: unknown;
  error?: string;
}

export const useCheckToolHealth = () => {
  return useMutation<
    ToolHealthCheckResponse,
    Error,
    { tool: string; query: string; image?: string }
  >({
    mutationFn: ({ tool, query, image }) =>
      CommunicationService.checkToolHealth(tool, query, image),
  });
};

export const useCombineDeliveriesToStaging = (options?: { onSuccess?: (data: string) => void }) => {
  return useMutation<string, Error, { deliveryIds: string[]; datestamp: string }>({
    mutationFn: ({ deliveryIds, datestamp }) =>
      CommunicationService.combineDeliveriesToStaging(deliveryIds, datestamp),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useSpecialI18nSafetyDelivery = (options?: { onSuccess?: (data: string) => void }) => {
  return useMutation<string, Error, { deliveryIds: string[]; datestamp: string }>({
    mutationFn: ({ deliveryIds, datestamp }) =>
      CommunicationService.specialI18nSafetyDelivery(deliveryIds, datestamp),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useDownloadJsons = () => {
  return useMutation(
    async (params: {
      projectId: string;
      selectedFiles: { date: string; filename: string }[];
      source?: 'staging' | 'customer';
    }) => {
      const response = await CommunicationService.downloadS3Jsons(
        params.projectId,
        params.selectedFiles,
        params.source || 'staging',
      );
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 's3_deliveries.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  );
};

export const useDeleteStagingFiles = (options?: { onSuccess?: (data: string[]) => void }) => {
  return useMutation<string[], Error, { customer: string; filePathsToDelete: string[] }>({
    mutationFn: ({ customer, filePathsToDelete }) =>
      CommunicationService.deleteStagingFiles(customer, filePathsToDelete),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useDeleteCustomerFiles = (options?: { onSuccess?: (data: string[]) => void }) => {
  return useMutation<string[], Error, { customer: string; filePathsToDelete: string[] }>({
    mutationFn: ({ customer, filePathsToDelete }) =>
      CommunicationService.deleteCustomerFiles(customer, filePathsToDelete),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useRecallTasksFromKratosBucket = (options?: {
  onSuccess?: (data: { jobId: string }) => void;
}) => {
  return useMutation<{ jobId: string }, Error, { taskIdsToRecall: string[] }>({
    mutationFn: ({ taskIdsToRecall }) =>
      CommunicationService.recallTasksFromKratosBucket(taskIdsToRecall),
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
  });
};

export const useGetDeliveryOpsTaskAttemptAudits = (filters: {
  projectId: string;
  statuses?: AuditStatus[];
}) => {
  return useQuery({
    ...queryFactory.trustAndSafety.getDeliveryOpsTaskAttempts({ filters }),
  });
};

export const useGetDeliveryOpsTaskAttemptAuditById = (auditId: string) => {
  return useQuery({
    ...queryFactory.trustAndSafety.getDeliveryOpsTaskAttemptAuditById({ auditId }),
  });
};

export const useUpdateDeliveryOpsTaskAttemptAudit = () => {
  return useMutation({
    mutationFn: (params: {
      auditId: string;
      status?: AuditStatus;
      outcomes?: AuditOutcomeEnum[];
      rationale?: string;
    }) => CommunicationService.updateDeliveryOpsTaskAttemptAudit(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          queryFactory.trustAndSafety.getDeliveryOpsTaskAttempts({
            filters: {
              projectId: '',
            },
          }).queryKey[0],
        ],
      });
    },
  });
};

export const useGetAuditById = (auditId: string) => {
  return useQuery({ ...queryFactory.trustAndSafety.getAuditById({ auditId }) });
};

export const useGetAuditsByTypeAndStatus = (
  auditTypes: AuditType[],
  auditStatuses: AuditStatus[],
) => {
  return useQuery({
    ...queryFactory.trustAndSafety.getAuditsByTypeAndStatus({ auditTypes, auditStatuses }),
  });
};

export const useGetAuditAttachmentUrls = (auditId: string) => {
  return useQuery({ ...queryFactory.trustAndSafety.getAuditAttachmentUrls({ auditId }) });
};

export const useSubmitSuspiciousBehaviorAudit = (auditId: string) => {
  return useMutation({
    mutationFn: ({ outcomes, rationale }: { outcomes: AuditOutcomeEnum[]; rationale: string }) => {
      return CommunicationService.submitSuspiciousBehaviorAudit(auditId, outcomes, rationale);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryFactory.trustAndSafety.getAuditsByTypeAndStatus({
          auditTypes: undefined,
          auditStatuses: undefined,
        }).queryKey,
      });
      toast('Successfully submitted audit', {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    },
  });
};

export const useSTORoster = () => {
  const result = useQuery({
    ...queryFactory.projectRoles.stoRoster(),
    onSuccess: () => {
      logger.log('useSTORoster hook success:');
    },
    onError: () => {
      logger.error('useSTORoster hook error:');
    },
  });

  return result;
};

export const useQuickGrabTasksByReviewLevel = (projectId: string) => {
  return useQuery<QuickGrabTasksResponse>({
    ...queryFactory.quickGrab.quickGrabTasksByReviewLevel({ projectId }),
    enabled: !!projectId,
  });
};

export const useGetFinanceAccess = (projectId: string, userId: string) => {
  return useQuery<{ financeAccess: boolean }>({
    ...queryFactory.financeAccess.getFinanceAccess({ projectId, userId }),
    enabled: !!projectId && !!userId,
  });
};

export function invalidateNodeSummaryQuery(projectId: string) {
  queryClient.invalidateQueries({
    queryKey: [...queryFactory.projectGroup.nodeSummaries(), projectId],
    exact: false,
  });
}

export const useUploadCompletedTasksToStaging = (options?: {
  onSuccess?: (data: { jobId: string }) => void;
  onError?: (error: Error) => void;
}) => {
  return useMutation<
    { jobId: string },
    Error,
    { projectIds: string[]; billingDate: string; overrideValidation: boolean }
  >({
    mutationFn: ({ projectIds, billingDate, overrideValidation }) =>
      CommunicationService.uploadCompletedTasksToStaging({
        projectIds,
        billingDate,
        overrideValidation,
      }),
    onSuccess: data => {
      toast.success(
        `Successfully initiated job to upload completed tasks to staging. Job ID: ${data.jobId}`,
        { autoClose: 3000, position: 'top-right' },
      );
      options?.onSuccess?.(data);
    },
    onError: error => {
      toast.error(`Upload failed: ${error.message}`);
      options?.onError?.(error);
    },
  });
};

export const useCountCompletedTasksForStaging = (options?: {
  onSuccess?: (data: { totalTaskCount: number }) => void;
  onError?: (error: Error) => void;
}) => {
  return useMutation<
    { totalTaskCount: number },
    Error,
    { projectIds: string[]; billingDate: string }
  >({
    mutationFn: ({ projectIds, billingDate }) =>
      CommunicationService.countCompletedTasksForStaging({ projectIds, billingDate }),
    onSuccess: data => {
      toast.success(`Successfully fetched task count: ${data.totalTaskCount}`);
      options?.onSuccess?.(data);
    },
    onError: error => {
      toast.error(`Failed to fetch task count: ${error.message}`);
      options?.onError?.(error);
    },
  });
};

export function useCustomerProjectReferenceSpec(projectId: string) {
  return useQuery<InsightReportDTO | null>(
    ['customer-project-reference-spec', projectId],
    async () => {
      try {
        return await apiFetch(
          null,
          `internal/customer_app/insight_reports/reference_specs/${projectId}`,
          {
            method: 'GET',
          },
        );
      } catch (error) {
        return null;
      }
    },
    {
      enabled: !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
}

export function useCustomerProjectInternalSpec(projectId: string) {
  return useQuery<InsightReportDTO | null>(
    ['customer-project-internal-spec', projectId],
    async () => {
      try {
        return await apiFetch(
          null,
          `internal/customer_app/insight_reports/internal_specs/${projectId}`,
          {
            method: 'GET',
          },
        );
      } catch (error) {
        return null;
      }
    },
    {
      enabled: !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
}

export function useNumTaskUploadValidationRequired(projectId: string) {
  return useQuery<any>(
    ['num-task-upload-validation-required', projectId],
    async () => {
      try {
        return await apiFetch(
          null,
          `internal/self_serve/task_template/${projectId}/show-task-upload-warning`,
          {
            method: 'GET',
          },
        );
      } catch (error) {
        return null;
      }
    },
    {
      enabled: !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
}

const USER_ELIGIBILITY_CHUNK_SIZE = 50;
const USER_ELIGIBILITY_CONCURRENCY = 10;

export const useGetUserEligibilityForInviteBatches = ({
  projectId,
  userIds,
  enabled = true,
  chunkSize = USER_ELIGIBILITY_CHUNK_SIZE,
}: {
  projectId: string;
  userIds: string[];
  enabled?: boolean;
  chunkSize?: number;
}) => {
  const debouncedUserIds = useDebouncedValue(userIds, 500);

  return useQuery<UserEligibilityDTO[]>({
    queryKey: ['userEligibilityForInviteBatches', projectId, debouncedUserIds],
    queryFn: () =>
      getUserEligibilityForInviteBatchesByChunks({
        projectId,
        userIdsChunked: _.chunk(debouncedUserIds, chunkSize),
      }),
    enabled: enabled && debouncedUserIds.length > 0,
    staleTime: 1000, // Data becomes stale after 1 second
  });
};

async function getUserEligibilityForInviteBatchesByChunks({
  projectId,
  userIdsChunked,
}: {
  projectId: string;
  userIdsChunked: string[][];
}): Promise<UserEligibilityDTO[]> {
  if (userIdsChunked.length === 0) {
    return [];
  }

  try {
    // Use Bluebird.map with concurrency control (pattern from codebase)
    const results = await Bluebird.map(
      userIdsChunked,
      async userIds => {
        if (userIds.length > 0) {
          return await CommunicationService.getUserEligibilityForInviteBatches(projectId, userIds);
        }
        return [];
      },
      { concurrency: USER_ELIGIBILITY_CONCURRENCY }, // Limit concurrent requests to prevent overwhelming server
    );

    // Combine all results into a single array
    const combinedData = results.flat();

    return combinedData;
  } catch (err) {
    logger.error('Failed to fetch user eligibility for invite batches in chunks', {
      projectId,
      totalChunks: userIdsChunked.length,
      error: err,
    });
    throw err;
  }
}

export const useCreateInviteBatch = () => {
  return useMutation({
    mutationFn: ({
      projectId,
      workerIds,
      createdById,
      expiresAt,
      groupName,
      useLatestBatchExpiresAt,
      insertOrder,
    }: {
      projectId: string;
      workerIds: string[];
      createdById: string;
      expiresAt: Date;
      groupName?: string;
      useLatestBatchExpiresAt?: boolean;
      insertOrder?: number;
    }) =>
      CommunicationService.createInviteBatch(
        projectId,
        workerIds,
        createdById,
        expiresAt,
        insertOrder,
        groupName,
        useLatestBatchExpiresAt,
      ),
  });
};

export const useGetInviteBatchesForProject = (projectId: string) => {
  return useQuery({
    queryKey: ['inviteBatches', projectId],
    queryFn: () => CommunicationService.getInviteBatchesForProject(projectId),
    staleTime: 1000, // Data becomes stale after 1 second
  });
};

export const useTempCopyAudioFiles = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: { jobIds: { jobId: string }[] }) => void;
  onError?: (error: Error) => void;
} = {}) => {
  return useMutation(
    (params: { isStaging: boolean; baseDirectory: string }) =>
      CommunicationService.tempCopyAudioFiles(params),
    {
      onSuccess: data => {
        toast.success(
          `${
            data.jobIds.length
          } audio file copy job(s) initiated successfully. Job IDs: ${data.jobIds
            .map(j => j.jobId)
            .join(', ')}`,
        );
        onSuccess?.(data);
      },
      onError: (error: Error) => {
        toast.error(`Failed to copy audio files: ${error.message}`);
        onError?.(error);
      },
    },
  );
};

export const useGetEligibleContributors = ({
  projectId,
  workerSkillIds,
  enabled = true,
}: {
  projectId: string;
  workerSkillIds?: string[];
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ['eligibleContributors', projectId, workerSkillIds],
    queryFn: () => CommunicationService.getEligibleContributors({ projectId, workerSkillIds }),
    enabled: enabled && !!projectId,
  });
};

export const useGetWorkerSkillSupplyImpact = ({
  userIds,
  enabled = true,
}: {
  userIds: string[];
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ['workerSkillSupplyImpact', userIds],
    queryFn: () => CommunicationService.getWorkerSkillSupplyImpact({ userIds }),
    enabled: enabled && userIds.length > 0,
  });
};

export const useDirectAssignContributors = () => {
  return useMutation({
    mutationFn: (request: DirectAssignContributorsRequestDTO) =>
      CommunicationService.directAssignContributors(request),
    onSuccess: (result: BatchAssignments) => {
      const stats: {
        success: number;
        error: number;
        contributorsWithError: string[];
        errors: string[];
      } = {
        success: 0,
        error: 0,
        contributorsWithError: [],
        errors: [],
      };
      for (const assignment of result) {
        if (assignment.errorReason) {
          stats.error++;
          stats.contributorsWithError.push(assignment.userId);
          stats.errors.push(assignment.errorReason);
        } else {
          stats.success++;
        }
      }
      const errorMessage = stats.contributorsWithError
        .map((contributor, index) => `${contributor}: ${stats.errors[index]}`)
        .join('\n');
      toast.success(
        `Successfully assigned ${stats.success} contributors, ${
          stats.error > 0 ? stats.error + ' failed' : '' + (errorMessage ? `: ${errorMessage}` : '')
        }`,
      );
    },
    onError: (error: Error) => {
      toast.error(`Failed to assign contributors: ${error.message}`);
    },
  });
};

export const useAssignOrMigrateAndAssignContributors = () => {
  return useMutation({
    mutationFn: (request: AssignOrMigrateAndAssignRequestDTO) =>
      CommunicationService.assignOrMigrateAndAssignContributors(request),
    onSuccess: (result: AssignOrMigrateAndAssignResponseDTO) => {
      toast.success(`Successfully assigned ${result.success} contributors`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to assign or migrate and assign contributors: ${error.message}`);
    },
  });
};

// hook for tns
export const useGetLatestTnSSpecDocForProject = (projectId: string) => {
  return useQuery({
    queryKey: ['tns-spec-doc', projectId],
    queryFn: () => CommunicationService.getLatestTnSSpecDocForProject(projectId),
  });
};

export const useGetArchieCheatingAgents = () => {
  return useQuery({
    queryKey: ['archie-cheating-agents'],
    queryFn: () => CommunicationService.getArchieCheatingAgents(),
  });
};

export const useSaveTnSSpecDoc = () => {
  return useMutation({
    mutationFn: ({
      projectId,
      requirements,
    }: {
      projectId: string;
      requirements: TnSRequirementSpecDocInput[];
    }) => CommunicationService.saveTnSSpecDoc(projectId, requirements),
  });
};

export const useUpdatePipelineV3ArchieTnsEvaluationHookConfig = () => {
  return useMutation({
    mutationFn: ({
      projectId,
      attemptSubmissionEnabled,
      reviewSubmissionEnabled,
    }: {
      projectId: string;
      attemptSubmissionEnabled: boolean;
      reviewSubmissionEnabled: boolean;
    }) =>
      CommunicationService.updatePipelineV3ArchieTnsEvaluationHookConfig(
        projectId,
        attemptSubmissionEnabled,
        reviewSubmissionEnabled,
      ),
  });
};

export const useGetArchieResults = (projectId: string, hardRefresh: boolean = false) => {
  return useQuery({
    queryKey: ['archie-results', projectId, hardRefresh],
    queryFn: () => CommunicationService.getArchieResults(projectId, hardRefresh),
    onError: handleGlobalQueryError,
    enabled: !!projectId,
  });
};

export const useBackfillTnsEvaluation = () => {
  return useMutation({
    mutationFn: ({ projectId, attemptIds }: { projectId: string; attemptIds: string[] }) =>
      CommunicationService.backfillTnsEvaluation(projectId, attemptIds),
  });
};

export const useSyncKratosBuckets = () => {
  return useMutation({
    mutationFn: (params: {
      startDate?: string;
      endDate?: string;
      projectIds?: string[];
      taskIds?: string[];
      filePaths?: string[];
      deliveryIds?: string[];
      modifiedStartDate?: string;
      modifiedEndDate?: string;
    }) => CommunicationService.syncKratosBuckets(params),
    onSuccess: () => {
      toast(`Successfully started job to sync Kratos buckets.`, {
        type: 'success',
        autoClose: 10000,
        position: 'top-right',
      });
    },
    onError: () => {
      toast(`Failed to sync Kratos buckets`, {
        type: 'error',
      });
    },
  });
};

export const useCheckExistingDelivery = (
  projectId: string,
  billingDate: string,
  options?: { enabled?: boolean },
) => {
  return useQuery<{ alreadyDelivered: boolean }>({
    queryKey: ['check-existing-delivery', projectId, billingDate],
    queryFn: () => CommunicationService.checkExistingDelivery(projectId, billingDate),
    enabled: options?.enabled ?? true,
  });
};

export const useCopyFilesToStaging = () => {
  return useMutation({
    mutationFn: () => CommunicationService.copyFilesFromCustomerToStaging(),
    onSuccess: (data: { jobId: string }) => {
      toast(`Successfully started job ${data.jobId} to copy files to staging.`, {
        type: 'success',
        autoClose: 10000,
        position: 'top-right',
      });
    },
    onError: () => {
      toast(`Failed to copy files to staging`, {
        type: 'error',
      });
    },
  });
};

export const useS3Browse = (bucket: string, prefix: string = '') => {
  return useQuery({
    queryKey: ['s3Browse', bucket, prefix],
    queryFn: async () => {
      return await CommunicationService.getS3BrowseContents(bucket, prefix);
    },
    enabled: !!bucket,
  });
};

export const useDownloadS3Files = () => {
  return useMutation(
    async (params: { bucket: string; files: Array<{ key: string; name: string }> }) => {
      const blob = await CommunicationService.downloadS3Files(params.bucket, params.files);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Set filename based on whether it's single file or multiple files
      if (params.files.length === 1) {
        link.setAttribute('download', params.files[0].name);
      } else {
        link.setAttribute('download', 'download.zip');
      }

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  );
};

export const useGetCohorts = (
  params: {
    cohortIds?: string[];
    cohortVersionIds?: string[];
    page?: number;
    pageSize?: number;
    search?: string;
    filterToCreatedByUser?: boolean;
    filterToUserFavorites?: boolean;
    workforceProjectId?: string;
    referencedProjectIds?: string[];
    filters?: FilterObject;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    archived?: boolean;
  },
  options?: { enabled?: boolean },
) => {
  return useQuery({
    ...options,
    queryKey: ['cohorts', JSON.stringify(params)],
    queryFn: () =>
      CommunicationService.getCohorts({
        ...params,
        filters: params.filters ? JSON.stringify(params.filters) : undefined,
      }),
    enabled: options?.enabled ?? true,
  });
};

export const useCreateCohort = () => {
  return useMutation({
    mutationFn: (params: {
      name: string;
      filters: FilterGroup;
      description: string;
      ownerIds: string[];
      clonedFromCohort?: string;
      excludeCBsInFraudHolding: boolean;
      cohortMappings?: Record<string, string>;
      workforceProjectId?: string;
      ignoreAutoArchival?: boolean;
    }) => {
      return CommunicationService.createCohort(params);
    },
  });
};

export const useGetCohortCounts = (referencedProjectIds?: string[]) => {
  return useQuery({
    queryKey: ['cohorts-counts', referencedProjectIds],
    queryFn: () => CommunicationService.getCohortCounts(referencedProjectIds),
  });
};

export const useCheckCohortNameAvailability = (name: string) => {
  return useQuery({
    queryKey: ['cohort-name-availability', name],
    queryFn: () => CommunicationService.checkCohortNameAvailability(name),
    enabled: !!name && name.trim().length > 0,
  });
};

export const useUpdateCohort = () => {
  return useMutation({
    mutationFn: (params: {
      cohortId: string;
      currentCohortVersionId: string;
      name: string;
      filters: FilterGroup;
      description: string;
      ownerIds: string[];
      excludeCBsInFraudHolding: boolean;
      cohortMappings?: Record<string, string>;
      ignoreAutoArchival?: boolean;
    }) => {
      return CommunicationService.updateCohort(params);
    },
  });
};

export const useGetCohortDetails = (cohortId: string, hoursOffset: number) => {
  return useQuery({
    queryKey: ['cohorts-details', cohortId, hoursOffset],
    queryFn: () => CommunicationService.getCohortDetails(cohortId, hoursOffset),
  });
};

export const useGetCohortContributorDetails = (cohortId: string) => {
  return useQuery({
    queryKey: ['cohorts-contributor-details', cohortId],
    queryFn: () => CommunicationService.getCohortContributorDetails(cohortId),
    enabled: false,
  });
};

export const useGetContributorDetails = (
  filters: FilterGroup,
  excludeCBsInFraudHolding: boolean,
  cohortId?: string,
  options?: { enabled: boolean },
) => {
  return useQuery({
    ...options,
    queryKey: ['contributor-details', JSON.stringify(filters), excludeCBsInFraudHolding, cohortId],

    queryFn: () =>
      CommunicationService.getContributorDetails(filters, excludeCBsInFraudHolding, cohortId),
    enabled: options?.enabled ?? false,
  });
};

export const useGetCohortVersions = (
  cohortId: string,
  includeArchived: boolean = true,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['cohort-versions', cohortId, includeArchived],
    queryFn: () => CommunicationService.getCohortVersions(cohortId, includeArchived),
    enabled: options?.enabled !== false && !!cohortId,
  });
};

export const useGetMembershipEval = (
  filters: FilterGroup,
  excludeCBsInFraudHolding: boolean,
  cohortId?: string,
  options?: { enabled: boolean; cacheTime?: number },
) => {
  return useQuery({
    ...options,
    queryKey: ['membership-eval', JSON.stringify(filters), excludeCBsInFraudHolding, cohortId],
    queryFn: () =>
      CommunicationService.getMembershipEval(filters, excludeCBsInFraudHolding, cohortId),
    enabled: options?.enabled ?? true,
    cacheTime: options?.cacheTime ?? 10 * 1000, // 10 seconds
  });
};

export const useUpdateCohortFavorite = () => {
  return useMutation({
    mutationFn: (params: { cohortId: string; favorite: boolean }) => {
      return CommunicationService.updateCohortFavorite(params.cohortId, params.favorite);
    },
  });
};

export const useDelayCohortArchival = () => {
  return useMutation({
    mutationFn: (cohortId: string) => {
      return CommunicationService.delayCohortArchival(cohortId);
    },
  });
};

export const useUnarchiveCohort = () => {
  return useMutation({
    mutationFn: (cohortId: string) => {
      return CommunicationService.unarchiveCohort(cohortId);
    },
  });
};

export const useGetCohortMappingsByConsumer = (
  consumerId: string,
  consumerType: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['cohort-mappings', consumerId, consumerType],
    queryFn: () => CommunicationService.getCohortMappingsByConsumer(consumerId, consumerType),
    enabled: options?.enabled ?? true,
  });
};

export const useArchiveCohort = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cohortId: string) => CommunicationService.archiveCohort(cohortId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts'] });
    },
  });
};
